/**
 * Fixture provider — the same `ConsoleApi` interface, served from
 * `src/fixtures/`.
 *
 * This is not a toy: it reproduces the contract's *behaviour*, not only its
 * shapes, so the pages are already correct when the HTTP provider is swapped in.
 *  - cursor paging + filters exactly like `GET /integration/v1/devices`
 *    (`model` filters `product_class`, order is last_inform_at desc / id asc)
 *  - TR-098 path **prefix** filtering, like `?path=` on the parameters endpoint
 *  - writes are fail-closed: an offline device answers 409 `device-offline`
 *    (see `acs_service/routers/actions.py`); `Idempotency-Key` replays return the
 *    stored response, and a reused key with a different body answers 409
 *    `idempotency-conflict`
 *  - ZTP activations run the three adapter steps synchronously and record them
 *    (`authorize` → `assign-profile` → `push-tr069`); an OLT outside the fixture
 *    inventory answers 404 `olt-not-found` like the canned adapter does, and the
 *    read-back derives advancement with the server's rule
 *  - diagnostics gate on `online` (409) and on the `tr143` capability for `speedtest` (422
 *    `action-unsupported`) with the server's own detail texts, and consume a connection-request
 *    token when a budget is configured (429 + `Retry-After`). `POST …/diagnostics` then only
 *    **queues** (§۵.۸ / T5): the answer appears on the later `GET …/diagnostics/{job}`, assembled by
 *    reading the fixture board's DiagnosticObject (`fixtures/diagnostics.ts`) with the server's own
 *    attribution and `DiagnosticsState` rules. Nothing here computes an RTT, a hop list or an
 *    Mbps pair — the numbers a probe publishes are the leaves its board holds
 *  - the alarm feed is DERIVED from `FIXTURE_EVENTS` through the same
 *    `SEVERITY_BY_TYPE` table as `acs_service/routers/alarms.py` (an online
 *    `device.online_status` is not an alarm), an unknown `severity` filter is 422
 *    `reference-invalid`, and an ack writes one entry into a per-(alarm, client)
 *    ledger so a repeat answers `already_acked` — with 404 `alarm-not-found` for
 *    an id that is not in the outbox or not alarm-bearing
 *  - the OLT proxy surface reproduces the adapter's answers: `onu_count` + the
 *    per-row `note` degrade of `GET /ops/v1/olts`, 404 `olt-not-found` for a table
 *    outside the inventory, 502 `adapter-unreachable` for a table the fixture
 *    cannot read, a direct authorize that writes only the four fields the driver
 *    writes and answers `admin_state` through `_admin_state()` of
 *    `routers/olt_ops.py` (so: the row's `auth_state`, i.e. `authorized`), and the
 *    optical read's fail-closed order ending in **409 `stale-snapshot`** when no
 *    OLT association is on record
 *  - the firmware registry and the campaign ladder are DERIVED, not stored:
 *    `fixtures/campaignEngine.ts` is the port of `campaigns.py::plan`/`wave_slice` and
 *    `routers/firmware.py::_campaign_item`/`_campaign_detail`/`_progress`, so
 *    `next_action`, `progress` and the two backlog numbers are computed from the same
 *    record the way the façade computes them — including the campaign that holds
 *    because its maintenance window has not opened
 *  - failures are real `ApiError`s carrying server-shaped problem documents
 */

import {
  assertWriteKey,
  type ConsoleApi,
  type EventStreamHandle,
  type EventStreamOptions,
  type FirmwareImageUpload,
  type LiveEventHandler,
  type ReadOptions,
  type WriteOptions,
} from './consoleApi';
import { ApiError } from './errors';
import type {
  ActionRequest,
  ActionResponse,
  Alarm,
  ActivationAccepted,
  ActivationRequest,
  ActivationStatus,
  AlarmAckRequest,
  AlarmAckResponse,
  AlarmAssignRequest,
  AlarmAssignResponse,
  AlarmClearRequest,
  AlarmClearResponse,
  AlarmEscalateRequest,
  AlarmEscalateResponse,
  AlarmSeverity,
  AlarmState,
  AlarmsResponse,
  AuditListResponse,
  BindingRequest,
  BindingResponse,
  Campaign,
  CampaignAccepted,
  CampaignCreateRequest,
  CampaignHalted,
  CampaignListResponse,
  CampaignRolledBack,
  CampaignRollbackRequest,
  CampaignScheduled,
  DeletedResponse,
  DeviceCapabilities,
  DeviceJobRow,
  DeviceSummary,
  DevicesListResponse,
  DiagnosticsRequest,
  DiagnosticsType,
  EffectivePermissions,
  EventReadResponse,
  FirmwareListResponse,
  FirmwareUploadAccepted,
  Health,
  Job,
  JobListRow,
  JobsListResponse,
  ListJobsQuery,
  ListOperationsQuery,
  OperationsResponse,
  OperationRunAccepted,
  OperationSlotItem,
  OperationRunRequest,
  OperationValueResponse,
  OperationWriteAccepted,
  PresetWriteResponse,
  ReconciliationListResponse,
  TemplateListResponse,
  OperationWriteRequest,
  RawWriteRequest,
  ListAlarmsQuery,
  ListMaintenanceQuery,
  MaintenanceCancelResponse,
  MaintenanceCreateRequest,
  Incident,
  IncidentAttachResponse,
  IncidentCorrelateResponse,
  IncidentAttachRequest,
  IncidentCloseRequest,
  IncidentCreateRequest,
  IncidentListResponse,
  IncidentNoteRequest,
  ListIncidentCorrelateQuery,
  ListIncidentsQuery,
  ListNotificationDeliveriesQuery,
  NotificationChannel,
  NotificationChannelCreateRequest,
  NotificationChannelDeletedResponse,
  NotificationChannelListResponse,
  NotificationChannelUpdateRequest,
  NotificationDelivery,
  NotificationDeliveryListResponse,
  MaintenanceCreateResponse,
  MaintenanceListResponse,
  MaintenanceWindow,
  ListAuditQuery,
  ListCampaignsQuery,
  ListDevicesQuery,
  ListEventsQuery,
  ListFirmwareQuery,
  ListOltOnusQuery,
  ListParamsQuery,
  ListServiceProfilesQuery,
  ListStationsQuery,
  ListSubscribersQuery,
  OnuAuthorizeRequest,
  OnuAuthorizeResponse,
  OnuTableRow,
  OltInventoryItem,
  OltListResponse,
  OnuTableResponse,
  OpticalDegrading,
  OpticalDegradingWindow,
  OpticalHistory,
  OpticalWorst,
  OpticalRead,
  PonCapacityResponse,
  OpticalWindow,
  ParamsResponse,
  Problem,
  ProblemCode,
  ProfileActivationAccepted,
  ProfileDocument,
  ProfileReadResponse,
  ProfileReadinessResponse,
  ProfileValidationReport,
  ProfileVersionListResponse,
  StationsResponse,
  SessionGranted,
  SessionRequest,
  TotpSessionRequest,
  SessionRevoked,
  ServiceProfile,
  ServiceProfileListResponse,
  ServiceProfileWriteRequest,
  SigningKeyListResponse,
  Subscriber,
  SubscriberBindingView,
  SubscriberCreateRequest,
  SubscriberDeviceView,
  SubscriberListResponse,
  SubscriberOverview,
  SubscriberSearchHit,
  SubscriberSearchRequest,
  SubscriberSearchResponse,
  ConsoleUser,
  OpsSettingsRead,
  UserCreateRequest,
  UserListResponse,
  UserMutationAccepted,
  UserPatchRequest,
  AuditEntry,
  MatrixCellChange,
  MatrixMutationAccepted,
  MatrixWriteBody,
  PermissionMatrixRead,
  SystemComponentDetail,
  SystemComponentsResponse,
  SystemQueuesResponse,
  SystemWorkersResponse,
  DeviceStats,
  AlarmStats,
} from './types';
import {
  ALARM_SEVERITIES,
  ALARM_STATES,
  DIAGNOSTICS_TYPES,
  OPTICAL_WINDOWS,
  DEGRADING_WINDOWS,
  RX_BIN_DBM,
  SUBSCRIBER_STATUSES,
  WORST_LIMIT_DEFAULT,
  WORST_LIMIT_MAX,
} from './types';
import {
  SUBSCRIBER_SEARCH_CRITERIA,
  SUBSCRIBER_SEARCH_DEFAULT_LIMIT,
  SUBSCRIBER_SEARCH_MAX_LIMIT,
  SUBSCRIBER_SEARCH_MIN_LIMIT,
} from './types';
import { paginate } from '../fixtures/cursor';
import {
  FIXTURE_COUNTS,
  FIXTURE_JOB_DEPTHS,
  FIXTURE_OUTBOX_DEPTHS,
  FIXTURE_SESSIONS_ACTIVE,
  QUEUES_NOTE,
  WORKERS_NOTE,
  fixtureWorkerBeats,
} from '../fixtures/systemMeasurement';
import {
  FIXTURE_CONSOLE_USERS,
  FIXTURE_RBAC_ROLES,
  FIXTURE_SESSION_EXPIRES_AT,
  fixtureSidFor,
  fixtureTokenFor,
  type FixtureConsoleUser,
} from '../fixtures/session';
import {
  FIXTURE_CONSOLE_USER_ROWS,
  cloneUserRows,
  fixtureSettingsState,
  lastOwnerAllows,
  lastOwnerRefusal,
  roleInvalidDetail,
  isKnownRole,
  isKnownStatus,
  settingsReadFor,
  settingsRejection,
  statusInvalidDetail,
  unknownSettingsDetail,
  userExistsRefusal,
  type FixtureSettingsState,
} from '../fixtures/identity';
import {
  buildFixtureMatrix,
  cloneMatrix,
  FIXTURE_LEVELS,
  MATRIX_AUDIT_KIND,
  matrixAcceptedDetail,
  validateMatrixWrite,
  type MatrixGrid,
} from '../fixtures/matrix';
import {
  ALARM_SCAN_PAGE_SIZE,
  FIXTURE_ACK_CLIENT,
  alarmFromEvent,
  deriveAlarmRows,
  notAnAlarmDetail,
  outboxEventOf,
  unknownAlarmDetail,
  type AlarmLifecycle,
} from '../fixtures/alarms';
import {
  FIXTURE_MAINTENANCE_WINDOWS,
  MAINTENANCE_PAGE_SIZE,
  MAINTENANCE_STATES,
  MAINTENANCE_WINDOW_PLANNER,
  STATE_DETAIL,
  sortWindowRows,
  unknownWindowDetail,
  validateWindowCreate,
} from '../fixtures/maintenance';
import { FIXTURE_TEMPLATES, TemplateStore } from '../fixtures/templates';
import {
  CORRELATE_MIN_DEVICES_FLOOR,
  FIXTURE_INCIDENTS,
  INCIDENT_OPERATOR,
  INCIDENT_STATES,
  correlateFixtureAlarms,
  incidentClosedDetail,
  incidentTimelineEntry,
  sharedOltOf,
  unknownIncidentDetail,
  worstFixtureSeverity,
} from '../fixtures/incidents';
import {
  DELIVERY_FEED_LIMIT,
  FIXTURE_CHANNELS,
  FIXTURE_DELIVERIES,
  FIXTURE_CHANNEL_KINDS,
  FIXTURE_MIN_SEVERITIES,
  MIN_SEVERITY_DETAIL,
  NOTIFY_OPERATOR,
  NOTIFY_URL_INVALID_DETAIL,
  channelKindUnsupportedDetail,
  fixtureNotifyClock,
  fixtureUrlValid,
  unknownChannelDetail,
} from '../fixtures/notifications';
import {
  AUDIT_DEFAULT_LIMIT,
  AUDIT_PAGE_LIMIT,
  FIXTURE_AUDIT_NOTE,
  fixtureAuditRows,
} from '../fixtures/audit';
import { fixtureKeyRows } from '../fixtures/keys';
import {
  ACTIVATION_INTENT_NOTE,
  FIXTURE_PROFILE_STATE,
  MISSING_ACTIVE_ROW_NOTE,
  NOTHING_ACTIVATED_NOTE,
  PROFILE_INTENT_NOTE,
  ROLLBACK_INTENT_NOTE,
  VERSIONS_NOTE,
  cloneProfileState,
  fixtureActivePair,
  fixtureReadiness,
  fixtureValidationReport,
  fixtureVersionRows,
  validateFixtureProfile,
  type FixtureProfileState,
} from '../fixtures/profile';
import {
  FIXTURE_OLT_INVENTORY,
  FIXTURE_OLT_TABLE_NOTES,
  associationOf,
  opticalDegradingOf,
  opticalHistoryOf,
  opticalWorstOf,
  ponCapacityOf,
  buildOltTables,
  inventoryRowOf,
  opticalOf,
} from '../fixtures/olts';
import {
  FIXTURE_ACTIVATIONS,
  FIXTURE_OLT_IDS,
  failedStep,
  raiseToInformed,
  successSteps,
  type FixtureActivation,
} from '../fixtures/activations';
import {
  FIXTURE_DEVICES,
  dataModelRootFor,
  findDevice,
  isoAgo,
  parametersFor,
  toDeviceSummary,
  type FixtureDevice,
} from '../fixtures/devices';
import {
  REFUSED_FAULT_CODE,
  candidatesFor,
  probeObjectFor,
  type ProbeFate,
  type ProbeObject,
} from '../fixtures/diagnostics';
import {
  applyDispatch,
  campaignJobReader,
  campaignPopulation,
  cloneCampaign,
  fixtureFileId,
  previousImageOf,
  snapshotVersion,
  FIXTURE_CAMPAIGNS,
  FIXTURE_FIRMWARE,
  type FixtureCampaign,
} from '../fixtures/firmware';
import {
  campaignDetail,
  campaignItem,
  computeTargets,
  openWaveJobs,
  parseWindow,
  planFor,
  validateWaves,
  waveCounts,
  waveSlice,
  windowState,
  type CampaignDoc,
} from '../fixtures/campaignEngine';
import { FIXTURE_EVENTS, jobsFor, stationsFor } from '../fixtures/records';
import {
  FIXTURE_SERVICE_PROFILES,
  FIXTURE_SUBSCRIBERS,
  maskIdentifier,
  type FixtureSubscriber,
} from '../fixtures/subscribers';
import { fixtureJobsPage, fixtureFleetJobs, jobListItem } from '../fixtures/jobs';
import {
  capabilityUnprovenDetail,
  deviceWords as deviceWordsOf,
  fixtureOperationValue,
  fixtureOperationsResponse,
  fixtureSlotsFor,
  isKnownOperation,
  rawConfirmText,
  runConfirmText,
} from '../fixtures/operations';
import { buildProblem } from '../fixtures/problems';
import {
  DIAGNOSTICS_TARGET_PATTERN,
  SERIAL_PATTERN,
  SUBSCRIBER_REF_PREFIX,
} from '../lib/validators';

export interface FixtureProviderOptions {
  /** Artificial latency so loading states are visible in `npm run dev` (0 in tests). */
  latencyMs?: number;
  /** Force the next write to fail with this contract code (error-path demo). */
  forceProblem?: ProblemCode | null;
  /** `Retry-After` seconds used with the forced `rate-limited` scenario. */
  retryAfterSeconds?: number;
  /**
   * The login budget the session door publishes when it answers 429 — the mirror of
   * `ACS_LOGIN_RATE_PER_MINUTE` (default 10, `acs_service/config.py:182`), because the door's
   * refusal sentence names its own number (`routers/identity.py:111`). Only the wording is
   * ported here; the fixture has no real bucket to drain.
   */
  loginRatePerMinute?: number;
  /**
   * How the fixture board answers once the read-back runs, mirroring the cases of
   * `acs_service/diagnostics.py::settle`: `answered` publishes the leaves of the board's
   * DiagnosticObject, `no-object` and `unattributed` are the two absences the server reports as
   * absences, `faulted` is the task the board refused (§۵.۲'s `cwmp.9001`), and `pending` leaves the
   * job `queued` because the task has not ended. There is no answered-at-POST option any more — T5
   * made the door only queue, so the fate belongs to the read, not to the request.
   */
  diagnosticsFate?: ProbeFate;
  /**
   * Connection-request fan-out budget, as tokens per second
   * (`ACS_CR_RATE_LIMIT_PER_SEC` → `TokenBucket` in `acs_service/ratelimit.py`).
   * Unset means unlimited, which is what the façade does when
   * `app.state.cr_bucket` is absent. Every live probe consumes one token; when
   * the bucket is empty the answer is 429 `rate-limited` + `Retry-After`.
   */
  connectionRequestBudget?: number;
  /** Injectable clock for the rate-limit bucket (tests). */
  now?: () => number;
  /**
   * Injectable clock for the reads that derive a verdict from the time — today that is
   * a campaign's maintenance window (G-FW-05), which `next_action` reads exactly like
   * the campaign worker does. Unset means the platform clock, the façade's
   * `datetime.now(timezone.utc)`.
   */
  clock?: () => Date;
  /**
   * The demo accounts the fixture's session doors answer with (U-08-f).
   *
   * An EMPTY list is not a missing table — it is the deployment the server refuses with
   * 503 `auth-unconfigured` because nobody seeded an owner, and the login screen has to be
   * able to render that sentence in the demo too.
   */
  consoleUsers?: readonly FixtureConsoleUser[];
  /**
   * The stored operator-profile family the profile doors answer from (U-16-e2).
   *
   * Unset means `FIXTURE_PROFILE_STATE` (three activations, the newest active). Passing
   * `FIXTURE_PROFILE_WINDOW_TRIMMED` seeds the restore case the server documents — an
   * `active_version` whose row the bounded `PROFILE_VERSION_HISTORY` window no longer carries — so
   * the «شماره هست، سند نیست» answers come from the same read-back pairing the doors use, not from a
   * stub. `FIXTURE_PROFILE_EMPTY` is the never-activated deployment.
   */
  profileState?: FixtureProfileState;
  /**
   * The console-user table the four `/ops/v1/users` doors read and write (U-16-g1).
   *
   * Unset means `FIXTURE_CONSOLE_USER_ROWS`, which holds TWO active owners on purpose: the last-owner
   * guard (§۳-۲) is only demonstrable in both directions if a test can first disable one owner and then
   * reach the state where exactly one is left. Passing a one-owner table is how a test forces the
   * refusal; passing `[]` is the empty deployment the door answers with an empty list and `count: 0`.
   */
  consoleUserRows?: readonly ConsoleUser[];
  /**
   * The stored `ops_settings` intent the two settings doors merge into (U-16-g1). Unset means nothing
   * written yet, i.e. `GET /ops/v1/settings` answers defaults with `updated_at: null` — the state a
   * fresh deployment is actually in.
   */
  settingsState?: FixtureSettingsState;
  /**
   * The permission-matrix grid the two matrix doors start from (U-16-g2). Unset means the copied §۴
   * table (`fixtures/matrix.ts::buildFixtureMatrix`); passing a grid seeds the demo on an edited matrix
   * the way `consoleUserRows` seeds a one-owner deployment.
   */
  permissionMatrixGrid?: MatrixGrid;
  /**
   * Make the components door's depth read FAIL (O4), the way the server's does when the store
   * cannot answer: the store component's `depths` becomes `null` (not zeros — a depth the server
   * did not send is not a depth of zero) and the component keeps the state the counts probe gave
   * it. This is the case a console must be able to render, and the one a fixture that always
   * answers a number would hide.
   */
  systemDepthsUnavailable?: boolean;
  /**
   * Make the WORKERS door's heartbeat read FAIL (DA-09), the way the server's does when
   * `list_worker_beats()` throws: `status: degraded` WITH an `error` and NO rows. The case a
   * console must be able to render, and the one a fixture that always answers rows would hide —
   * «I could not look» is neither green nor a dead worker, and an empty object with no error
   * would be the same payload as a deployment that legitimately runs no loops.
   */
  systemWorkersUnreadable?: boolean;
  /**
   * The SUBSCRIBER SURFACE's own switch — the mirror of `ACS_SUBSCRIBER_ENABLED`
   * (`routers/subscribers.py::require_surface`).
   *
   * Default ON, because the demo deployment runs the surface. When it is off the
   * two doors of this unit answer `503 subscriber-disabled` — the surface's NAMED
   * refusal — and never a 404 (which would pretend the door was never built) and
   * never an empty list (which would pretend this deployment holds no subscribers:
   * a lie a caller would act on).
   */
  subscriberSurfaceEnabled?: boolean;
  /**
   * The stored `acs_maintenance` rows the three maintenance doors read and write
   * (ALM-05 console remainder). Unset means `FIXTURE_MAINTENANCE_WINDOWS` — one
   * plan still standing and one cancelled with the canceller's name, the two
   * lifecycle stories the panel has to render.
   */
  maintenanceWindows?: readonly MaintenanceWindow[];
}

/**
 * One probe this session queued: what the door was asked for, the task handle the NBI returned, and
 * the job record the read-back pass writes its answer into. `diagnostics.py::poll` needs the same
 * three facts — job, task, object read — to decide anything.
 */
interface ProbeJobEntry {
  deviceId: string;
  device: FixtureDevice;
  job: Job;
  probe: DiagnosticsType;
  target: string;
  nbiTask: { _id: string; name: string };
}

export interface StoredResponse {
  fingerprint: string;
  payload: unknown;
}

/** The actions the v1 contract accepts on `…/actions`. */
const CONTRACT_ACTIONS: readonly ActionRequest['action'][] = [
  'apply_policy',
  'read_snapshot',
  'reboot',
  'factory_reset',
  'download',
  'rotate_credential',
];

/** The three fields the activation model pins to `min_length=1`. */
const REQUIRED_ACTIVATION_FIELDS = [
  'olt_id',
  'pon_port',
  'line_profile',
  'service_profile',
] as const;

/** The gate `routers/diagnostics.py` applies to `speedtest`. */
const TR143 = 'tr143';

/** `FirmwareUploadRequest.checksum_sha256` — the pattern the contract pins. */
const CHECKSUM_PATTERN = /^[0-9a-f]{64}$/;

/** `firmware.py:387` — the statuses with something left to stop. */
const HALTABLE_STATUSES: readonly string[] = ['pending', 'running', 'rolling_back'];

/** `diagnostics.py::ATTRIBUTION_LEAVES` — the leaves that say which probe an object answers. */
const ATTRIBUTION_LEAVES: readonly string[] = ['Host', 'DownloadURL'];

/** What the server publishes when the board answered leaves but no state among them. */
const NO_DIAGNOSTICS_STATE = 'no-diagnostics-state-returned';

/** Session activations are recorded "3 minutes ago" so the step timeline rises. */
const SESSION_ACTIVATION_AGO = 3;

/**
 * Port of `acs_service/ratelimit.py` `TokenBucket` (same refill maths, same
 * `retry_after_seconds` floor/cap) so the fixture can answer the real
 * 429 `rate-limited` + `Retry-After` surface instead of faking it.
 */
export class TokenBucket {
  readonly capacity: number;
  private tokens: number;
  private last: number;

  constructor(
    readonly ratePerSec: number,
    private readonly clock: () => number = () => Date.now() / 1000,
  ) {
    this.ratePerSec = Math.max(0, ratePerSec);
    this.capacity = Math.max(1, Math.ceil(Math.max(1, this.ratePerSec)));
    this.tokens = this.capacity;
    this.last = clock();
  }

  allow(tokens = 1, now = this.clock()): boolean {
    const elapsed = Math.max(0, now - this.last);
    this.last = now;
    this.tokens = Math.min(this.capacity, this.tokens + elapsed * this.ratePerSec);
    if (this.tokens >= tokens) {
      this.tokens -= tokens;
      return true;
    }
    return false;
  }

  /** Seconds until one token is available (min 1, capped at 300) — like the server. */
  retryAfterSeconds(): number {
    if (this.ratePerSec <= 0) return 300;
    const needed = 1 - this.tokens;
    if (needed <= 0) return 1;
    return Math.max(1, Math.min(300, Math.ceil(needed / this.ratePerSec)));
  }
}

export function createFixtureApi(options: FixtureProviderOptions = {}): ConsoleApi {
  const latency = options.latencyMs ?? 120;
  const diagnosticsFate: ProbeFate = options.diagnosticsFate ?? 'answered';
  const depthsUnavailable = options.systemDepthsUnavailable ?? false;
  const workersUnreadable = options.systemWorkersUnreadable ?? false;
  /** The subscriber surface's switch — ON unless a test turns it off (see the option's own note). */
  const subscriberSurfaceEnabled = options.subscriberSurfaceEnabled ?? true;
  const forced = {
    code: options.forceProblem ?? null,
    retryAfter: options.retryAfterSeconds ?? 12,
  };
  const bucket =
    options.connectionRequestBudget === undefined
      ? null
      : new TokenBucket(options.connectionRequestBudget, options.now);
  /**
   * Instance-local state: bindings/jobs created during this session only.
   *
   * The overlay carries the `subscriber_ref` as well as the nas_ref, because the SERVER's
   * binding row carries the whole bind body (`store.put_binding(body.model_dump())` of
   * `routers/devices.py`), and the subscriber door reads that ref to decide whether the
   * device is already somebody else's (`routers/subscribers.py`: the `409 binding-conflict`
   * branch). A fixture that dropped it would let the subscriber door refuse a link the
   * server refuses, and accept one it accepts — the two would disagree on the same device.
   */
  const bindingOverlay = new Map<
    string,
    { nas_ref: string; binding_version: number; subscriber_ref: string | null }
  >();
  const sessionJobs = new Map<string, { deviceId: string; row: DeviceJobRow }>();
  const idempotency = new Map<string, StoredResponse>();
  /** Seeded from `fixtures/activations.ts`, then appended to by this session. */
  const activations = new Map<string, FixtureActivation>();
  const diagnosticJobs = new Map<string, ProbeJobEntry>();
  /**
   * The `alarm.ack` ledger: alarm id → the entry this "client" wrote. The server
   * keys it by (alarm, client), and this fixture has exactly one client
   * (`FIXTURE_ACK_CLIENT`), so a second ack of the same alarm is `already_acked`
   * and writes nothing — the same answer `routers/alarms.py` gives.
   */
  const ackLedger = new Map<string, { note: string | null; acked_at: string }>();
  /**
   * The fixture's `acs_alarms` rows (N1/N2): alarm id → the lifecycle the
   * doors moved. The server keeps this on the ROW, so the fixture keeps it on
   * a row too — a fixture that answered «cleared» from a separate flag would
   * tell the operator a state the server never sent.
   */
  const lifecycleLedger = new Map<string, AlarmLifecycle>();
  /**
   * The lifecycle the feed answers with: the stored rows first, and the ack
   * ledger for the rows nobody moved through a lifecycle door (the ack moves
   * the row to `acked`, exactly as the server's ack door does).
   */
  const lifecyclesOf = (): Map<string, AlarmLifecycle> => {
    const lifecycles = new Map<string, AlarmLifecycle>();
    for (const id of ackLedger.keys()) {
      lifecycles.set(id, {
        state: 'acked',
        acknowledged: true,
        escalated: false,
        assignee: null,
        cleared_by: null,
      });
    }
    for (const [id, record] of lifecycleLedger) lifecycles.set(id, record);
    return lifecycles;
  };
  /** olt_id → ONU table; a manual authorize mutates this session copy only. */
  const onuTables = buildOltTables();
  /**
   * The operator-profile family this session's profile doors read and write (U-16-e2). The clone is
   * what keeps the module fixtures shared-safe: an activation in one test must not move another
   * test's `active_version`, exactly like the campaign records above.
   */
  const profileState = cloneProfileState(options.profileState ?? FIXTURE_PROFILE_STATE);
  /**
   * Session copies of the firmware registry and the campaign ladder, plus anything this
   * session writes. A `:resume` moves a record and the fixture modules are shared by every
   * test in the file, so the provider works on clones (`cloneCampaign`) — the same reason
   * the activation and job maps above are session-local.
   */
  const firmwareRows = FIXTURE_FIRMWARE.map((row) => ({ ...row }));
  // ALM-05: the window plans this session writes land on a per-provider copy,
  // like every other mutable collection here — a test's create must not leak
  // into the next provider instance.
  const maintenanceRows: MaintenanceWindow[] = (
    options.maintenanceWindows ?? FIXTURE_MAINTENANCE_WINDOWS
  ).map((row) => ({ ...row }));
  let maintenanceSeq = 0;
  const templateStore = new TemplateStore(FIXTURE_TEMPLATES);
  // N5: the incident rows this session writes land on a per-provider clone,
  // like every other mutable collection — a test's create must not leak into
  // the next provider instance. The correlate PROPOSALS are computed live
  // from this session's alarm lifecycles, never stored.
  const incidentRows: Incident[] = FIXTURE_INCIDENTS.map((row) => ({
    ...row,
    alarm_ids: [...row.alarm_ids],
    timeline: row.timeline.map((entry) => ({ ...entry })),
  }));
  let incidentSeq = 0;
  // N6: the channel registry and the delivery feed this session writes land
  // on per-provider clones, like every other mutable collection — a test's
  // create must not leak into the next provider instance. The fixture has no
  // notify worker: an intent minted by `:test` stays `pending` (D4).
  const channelRows: NotificationChannel[] = FIXTURE_CHANNELS.map((row) => ({
    ...row,
  }));
  const deliveryRows: NotificationDelivery[] = FIXTURE_DELIVERIES.map((row) => ({
    ...row,
  }));
  let channelSeq = 0;
  let deliverySeq = 0;
  const campaignRecords = new Map<string, FixtureCampaign>(
    FIXTURE_CAMPAIGNS.map((entry) => [entry.doc.campaign_id, cloneCampaign(entry)]),
  );
  /**
   * The fixture's one open session — the demo path's stand-in for the store's session table.
   * `null` while nobody has signed in, which is what makes `closeSession` and
   * `effectivePermissions` answer 401 the way the server does rather than inventing an identity.
   */
  let fixtureSession: { sid: string; username: string; role: string } | null = null;
  /** The demo accounts behind the session doors; `[]` models a deployment with no owner seeded. */
  const consoleUsers = options.consoleUsers ?? FIXTURE_CONSOLE_USERS;
  /**
   * The console-user table this session's identity doors write (U-16-g1). A session clone for the same
   * reason the campaign records are: `PATCH` moves a row, and the module fixture is shared by every
   * test in the file.
   */
  const userRows = cloneUserRows(options.consoleUserRows ?? FIXTURE_CONSOLE_USER_ROWS);
  /** The `ops_settings` meta document this session's settings doors merge into (U-16-g1). */
  const settingsState = fixtureSettingsState(options.settingsState);
  /**
   * The permission-matrix grid this session's two `/ops/v1/permissions` doors read and write (U-16-g2).
   * A session clone built from the copied §۴ table (`fixtures/matrix.ts`), for the same reason the
   * settings document above is one: a PUT in one test must not move another test's cells. The optional
   * `permissionMatrixGrid` seeds a non-default deployment the way `consoleUserRows` seeds a one-owner table.
   */
  const matrixGrid = cloneMatrix(options.permissionMatrixGrid ?? buildFixtureMatrix());
  /**
   * The `permission.change` rows this session wrote, newest first. `routers/identity.py` appends one per
   * ACCEPTED write with `**principal.actor()` beside the cells (U-16-b); `listAudit` shows them ahead of
   * the seeded ledger exactly as the store would show a fresh row ahead of old ones. A refused write
   * reaches none of this — the door's judgment happens before the store sees a row, and the fixture keeps
   * that order.
   */
  const matrixAuditRows: AuditEntry[] = [];
  /**
   * The `subscriber.search` rows this session wrote (S2, G-SUB-08).
   *
   * **The row carries the SHAPE of the search and the COUNT — never the value that
   * was searched for.** `by` names the CRITERIA the body carried (`mobile` ·
   * `national_id` · …) and `count` is the number of rows that matched; a phone
   * number in the audit trail is personal data in a place nobody was looking for
   * it, and an audit is about «who did this», not «who had which number».
   *
   * The rows are session-local, exactly like the matrix rows above: a search in one
   * test must not appear in another test's ledger.
   */
  const subscriberSearchAuditRows: AuditEntry[] = [];
  /**
   * The `subscriber.suspend` / `subscriber.resume` rows this session wrote (S4, G-SUB-05).
   *
   * **THE SERVER WRITES ONE ROW FOR AN APPLIED TRANSITION, AND THE FIXTURE MUST NOT
   * DIVERGE FROM IT.** `_transition_apply` of `routers/subscribers.py` appends one
   * ledger row per applied transition — the row's OWN minted name, the device link,
   * the state the row was ACTUALLY in, the state it is in now and the actor.
   *
   * **NO IDENTIFYING FIELD OF THE SUBSCRIBER.** A phone number in the audit trail is
   * personal data in a place nobody was looking for it: the ledger records «who did
   * this, to which row, and from what state to what», and nothing else.
   *
   * The rows are session-local, exactly like the search rows above: a suspension in
   * one test must not appear in another test's ledger.
   */
  const subscriberLifecycleAuditRows: AuditEntry[] = [];
  /** The ledger's own row id — the counter behind the fixture's `fx-audit-lifecycle-*`. */
  let lifecycleAuditCounter = 0;
  /**
   * The subscriber table and the service-profile table, as this SESSION's copies (S1,
   * G-SUB-01/03). The clones are the reason the module fixtures stay shared-safe: a
   * subscriber created in one test must not appear in another test's listing, exactly
   * like the campaign records and the operator-profile family above.
   *
   * The identifying fields are stored WHOLE here, because that is where the server keeps
   * them (`subscriber_view` masks on the way OUT — a masked value cannot be matched
   * against anything).
   */
  const subscriberRows = new Map<string, FixtureSubscriber>(
    FIXTURE_SUBSCRIBERS.map((row) => [row.subscriber_id, { ...row }]),
  );
  const profileRows = new Map<string, ServiceProfile>(
    FIXTURE_SERVICE_PROFILES.map((row) => [row.profile_id, { ...row, dba: { ...row.dba } }]),
  );
  let campaignCounter = 0;
  let jobCounter = 0;
  let activationCounter = 0;
  let diagnosticCounter = 0;

  for (const entry of FIXTURE_ACTIVATIONS) {
    activations.set(entry.activation_id, {
      ...entry,
      steps: entry.steps.map((step) => ({ ...step })),
    });
  }

  function wait<T>(value: T): Promise<T> {
    if (latency <= 0) return Promise.resolve(value);
    return new Promise((resolve) => {
      setTimeout(() => resolve(value), latency);
    });
  }

  function problemError(problem: Problem, retryAfter?: number): ApiError {
    return new ApiError('problem', `problem+json ${problem.status}`, {
      problem,
      status: problem.status,
      ...(retryAfter === undefined ? {} : { retryAfterSeconds: retryAfter }),
    });
  }

  /** The shared validation of create and attach, ported from
   *  `routers/incidents.py::_vet_attach_candidates`: every id exists, no row
   *  is `cleared`, and no id already lives in ANOTHER open incident. */
  function vetIncidentAlarmIds(
    alarmIds: readonly string[],
    incidentId: string,
    instance: string,
  ): Alarm[] {
    if (new Set(alarmIds).size !== alarmIds.length) {
      throw problemError(
        buildProblem('reference-invalid', {
          detail: 'alarm_ids carries the same id twice',
          instance,
        }),
      );
    }
    const all = deriveAlarmRows(lifecyclesOf());
    const found = all.filter((row) => alarmIds.includes(row.alarm_id));
    const missing = alarmIds.filter((id) => !found.some((row) => row.alarm_id === id));
    if (missing.length > 0) {
      throw problemError(
        buildProblem('reference-invalid', {
          detail: `unknown alarm id(s): ${missing.join(', ')}`,
          instance,
        }),
      );
    }
    const cleared = found.filter((row) => row.state === 'cleared').map((row) => row.alarm_id);
    if (cleared.length > 0) {
      throw problemError(
        buildProblem('reference-invalid', {
          detail:
            `cleared alarm(s) cannot join an incident: ${cleared.sort().join(', ')} — ` +
            'a settled cycle is history, not a live cause',
          instance,
        }),
      );
    }
    const occupied = alarmIds.filter((id) =>
      incidentRows.some(
        (row) =>
          row.state === 'open' && row.incident_id !== incidentId && row.alarm_ids.includes(id),
      ),
    );
    if (occupied.length > 0) {
      throw problemError(
        buildProblem('alarm-already-in-incident', {
          detail:
            `${occupied.sort().join(', ')} already live(s) in an open incident — ` +
            'the remedy is that incident, not a second one',
          instance,
        }),
      );
    }
    return found;
  }

  /** The 409 word of a closed incident, spelled once (the server's sentence). */
  function closedIncident(incidentId: string, instance: string): ApiError {
    return problemError(
      buildProblem('incident-already-closed', {
        detail: incidentClosedDetail(incidentId),
        instance,
      }),
    );
  }

  /** The device's OLT per the fixture's own association rule (`associationOf`). */
  function oltIdOfDevice(deviceId: string): string | null {
    const index = FIXTURE_DEVICES.findIndex((device) => device.acs_device_id === deviceId);
    if (index < 0) return null;
    const link = associationOf(FIXTURE_DEVICES[index]!, index);
    return link ? link.olt_id : null;
  }

  /** 422 `reference-invalid` — the code the façade gives a malformed or unusable request. */
  function referenceInvalid(detail: string, instance?: string): ApiError {
    return problemError(
      buildProblem('reference-invalid', { detail, ...(instance ? { instance } : {}) }),
    );
  }

  /**
   * The caller the door would name in its `detail` (`Principal.subject`).
   *
   * Fixture mode has exactly one session, so the subject is that username; with nobody signed in the demo
   * names itself `fixture-anonymous` rather than inventing a person — a sentence about who made a change
   * must not fabricate one (§۶).
   */
  function identitySubject(): string {
    return fixtureSession?.username ?? 'fixture-anonymous';
  }

  /**
   * `_revoke_sessions` of `routers/identity.py`, as far as one fixture session reaches.
   *
   * A JWT cannot be taken back before it expires, which is why the store has a session table at all (§۵);
   * the demo's table is `fixtureSession`, so ending the account of the signed-in username ends that session
   * and returns the count the door's sentence carries.
   */
  function revokeSessionsOf(username: string): number {
    if (fixtureSession === null || fixtureSession.username !== username) return 0;
    fixtureSession = null;
    return 1;
  }

  /** 422 `action-unsupported` — the record is fine; this door cannot do that to it. */
  function actionUnsupported(detail: string, instance?: string): ApiError {
    return problemError(
      buildProblem('action-unsupported', { detail, ...(instance ? { instance } : {}) }),
    );
  }

  /* ---- operations doors' refusals, in the gate's own words (U-16-f) ---- */

  /** 409 `device-offline` — the one refusal a retry can clear, so it is always last (§۴.۳). */
  function deviceOffline(detail: string, instance?: string): ApiError {
    return problemError(
      buildProblem('device-offline', { detail, ...(instance ? { instance } : {}) }),
    );
  }

  /** 409 `capability-unproven` — the §۴.۴ gate: a reason, and never a dead end (VM-11). */
  function capabilityUnproven(
    op: string,
    slot: OperationSlotItem,
    device: FixtureDevice,
    instance: string,
  ): ApiError {
    return problemError(
      buildProblem('capability-unproven', {
        detail: capabilityUnprovenDetail(op, slot, device),
        instance,
      }),
    );
  }

  /** §۶.۲'s per-path denial for the raw door: an unbound path is outside the witnessed tree. */
  function capabilityUnprovenPath(path: string, device: FixtureDevice, instance: string): ApiError {
    return problemError(
      buildProblem('capability-unproven', {
        detail:
          `not-witnessed: ${JSON.stringify(path)} is outside the witnessed tree of ` +
          `${deviceWordsOf(device)} (rule M4-2). ` +
          'Nothing on §۶.۲ list blocked it: the door denies by default, so the ' +
          'path needs a witness of §۷.۲ :discover before it can be written',
        instance,
      }),
    );
  }

  /** 409 `confirmation-required`, carrying the exact issued text (G-2: friction, not deadlock). */
  function confirmationRequired(detail: string, instance: string): ApiError {
    return problemError(buildProblem('confirmation-required', { detail, instance }));
  }

  /** 404 `operation-not-found` — a row the operator cannot address at all (§۲.۳). */
  function operationNotFound(detail: string, instance: string): ApiError {
    return problemError(buildProblem('operation-not-found', { detail, instance }));
  }

  /** 422 `not-an-action` — the slot exists; it simply has no method to run (§۴.۱۰). */
  function notAnAction(detail: string, instance: string): ApiError {
    return problemError(buildProblem('not-an-action', { detail, instance }));
  }

  /** The slot row for a door that addresses it by name, or the door's own 404. */
  function operationSlotOrThrow(
    device: FixtureDevice,
    op: string,
    acsDeviceId: string,
    method: 'PUT' | 'POST',
  ): OperationSlotItem {
    if (!isKnownOperation(op)) {
      throw operationNotFound(
        `${JSON.stringify(op)} is not an operator operation of this catalog — bootstrap and ` +
          'policy.* rows are not operations on a device (§۲.۳)',
        `${method} /ops/v1/devices/${acsDeviceId}/operations/${op}`,
      );
    }
    return fixtureSlotsFor(device).find((item) => item.op === op) as OperationSlotItem;
  }

  /**
   * `provisioning/catalog.py::validate_value`, as far as this fixture carries it: the row's declared
   * type is the contract, so a boolean slot given a string is 422 `reference-invalid` naming the
   * declared type. An unknown pairing is not rejected here — the door would be inventing a rule.
   */
  function catalogRejection(slot: OperationSlotItem, value: unknown): string | null {
    if (value === undefined || value === null) return `${slot.value_type} requires a value`;
    switch (slot.value_type) {
      case 'boolean':
        return typeof value === 'boolean' ? null : `${JSON.stringify(value)} is not a boolean`;
      case 'int':
      case 'unsignedInt':
      case 'float':
        return typeof value === 'number' && Number.isFinite(value)
          ? null
          : `${JSON.stringify(value)} is not a ${slot.value_type}`;
      case 'string':
        return typeof value === 'string' ? null : `${JSON.stringify(value)} is not a string`;
      default:
        return null;
    }
  }

  /**
   * A job this session minted, in the door's row shape.
   *
   * It is created `queued` and moves to `running` on the NEXT read of the jobs door — the mirror of
   * `job_machine`'s lifecycle, and deliberately no further: only a witnessed read-back closes a job
   * (Derived-3), so the fixture that manufactured a `completed` would be teaching the panel the exact
   * lie D6 forbids.
   */
  const mintedJobs: JobListRow[] = [];
  const movedJobs = new Set<string>();

  function mintOperationJob(
    device: FixtureDevice,
    action: string,
    op: string,
    summary: string,
  ): string {
    jobCounter += 1;
    const jobId = `job:fx${String(jobCounter).padStart(4, '0')}${device.serial.slice(-4)}`;
    mintedJobs.unshift({
      job_id: jobId,
      device_id: device.acs_device_id,
      action: op ? `${action}:${op}` : action,
      status: 'queued',
      requested_at: new Date().toISOString(),
      started_at: null,
      completed_at: null,
      result: summary,
      error: null,
    });
    return jobId;
  }

  function mintAccepted(
    device: FixtureDevice,
    action: string,
    summary: string,
  ): OperationWriteAccepted {
    return { job_id: mintOperationJob(device, action, '', summary), status: 'queued' };
  }

  /** Every row the jobs door can answer with: this session's writes, then the seeded fleet history. */
  function fleetJobRows(): JobListRow[] {
    for (const row of mintedJobs) {
      if (row.status === 'queued' && !movedJobs.has(row.job_id)) {
        movedJobs.add(row.job_id);
        row.status = 'running';
        row.started_at = row.requested_at;
      }
    }
    const sessionRows: JobListRow[] = [];
    for (const entry of sessionJobs.values()) {
      sessionRows.push(jobListItem(entry.deviceId, entry.row));
    }
    for (const entry of diagnosticJobs.values()) {
      sessionRows.push(jobListItem(entry.deviceId, rowFromJob(entry.job)));
    }
    return [...sessionRows, ...fixtureFleetJobs(mintedJobs)];
  }

  /** `campaign-not-found` — the campaign writes and reads share one answer for a missed id. */
  function campaignOrThrow(campaignId: string): FixtureCampaign {
    const entry = campaignRecords.get(campaignId);
    if (!entry) {
      throw problemError(
        buildProblem('campaign-not-found', {
          detail: `no campaign ${campaignId}`,
          instance: `/ops/v1/campaigns/${campaignId}`,
        }),
      );
    }
    return entry;
  }

  function deviceOrThrow(acsDeviceId: string): { device: FixtureDevice; index: number } {
    const index = FIXTURE_DEVICES.findIndex((d) => d.acs_device_id === acsDeviceId);
    const device = index >= 0 ? FIXTURE_DEVICES[index] : findDevice(acsDeviceId);
    if (index < 0 || !device) {
      throw problemError(
        buildProblem('device-not-found', {
          detail: `unknown device ${acsDeviceId}`,
          instance: `GET /integration/v1/devices/${acsDeviceId}`,
        }),
      );
    }
    return { device, index };
  }

  /** Contract summary with any session-local binding applied. */
  function summaryOf(device: FixtureDevice): DeviceSummary {
    const overlay = bindingOverlay.get(device.acs_device_id);
    const base = toDeviceSummary(device);
    if (!overlay) return base;
    return { ...base, nas_ref: overlay.nas_ref, binding_version: overlay.binding_version };
  }

  function requireRef(value: string | null | undefined, prefix: string, field: string): void {
    if (value === null || value === undefined || value.length === 0) return;
    if (!value.startsWith(prefix)) {
      throw problemError(
        buildProblem('reference-invalid', {
          detail: `"${value}" is not a valid ${field} (must start with "${prefix}")`,
        }),
      );
    }
  }

  /**
   * A replayed key returns the stored response before any gate runs — the
   * server checks idempotency first (`auth.check_idempotency`), so a replay of a
   * once-successful write still succeeds even if the device is offline now.
   */
  function replayOrThrow<T>(key: string, sketch: unknown): T | undefined {
    return lookupIdempotency<T>(idempotency, key, sketch);
  }

  /** Negative answers are never recorded, so a key stays reusable after a rejection. */
  function record(key: string, sketch: unknown, payload: unknown): void {
    storeIdempotency(idempotency, key, sketch, payload);
  }

  function replayOrRecord<T>(key: string, body: unknown, produce: () => T): T {
    return applyIdempotency(idempotency, key, body, produce);
  }

  /* ---------------- the subscriber surface (S1, G-SUB-01/03) ---------------- */

  /**
   * The caller's LEVEL on one capability key — the same question the door asks.
   *
   * The answer comes from the signed-in demo account's own capability map, which is the
   * same source `effectivePermissions` reads — so the console's mirror of the server's
   * answer and the mask the fixture applies cannot disagree about what the caller holds.
   * Nobody signed in is `none` on every key: a signed-out caller is not an editor, and
   * the server would not have let it past the gate at all.
   */
  function callerLevel(capability: string): string {
    const open = fixtureSession;
    if (open === null) return 'none';
    const user = consoleUsers.find((entry) => entry.username === open.username);
    return user?.capabilities[capability] ?? 'none';
  }

  /**
   * The audit kind of the search (S2) — `subscriber.search`.
   *
   * One name, because the question an audit reader asks is «who searched, and for
   * what KIND of thing» — the shape of the search, never the value that was searched
   * for.
   */
  const SUBSCRIBER_SEARCH_AUDIT_KIND = 'subscriber.search';

  /**
   * The two BOUNDED pages the 360-degree view reads (`models.py::OVERVIEW_ALARM_PAGE`
   * and `OVERVIEW_JOB_PAGE`, both `20`).
   *
   * Both lists grow with TIME, so an unbounded read of either would cost more every
   * day and answer no more: the page is the bound.
   */
  const OVERVIEW_ALARM_PAGE = 20;
  const OVERVIEW_JOB_PAGE = 20;

  /**
   * One subscriber row the search matched, as THIS caller may see it — the port of
   * `_search_hit` of `routers/subscriber_search.py`.
   *
   * The mask is the DOOR's decision and the level comes from the search door's own
   * key (`fleet.devices.read`): `edit` sees the identifying fields whole, every other
   * level sees them masked. `bss_subscriber_ref` is NOT masked — it is the BSS's own
   * IDENTIFIER, not personal data.
   */
  function subscriberSearchHit(row: FixtureSubscriber, level: string): SubscriberSearchHit {
    const whole = level === 'edit';
    return {
      subscriber_id: row.subscriber_id,
      mobile: whole ? row.mobile : maskIdentifier(row.mobile),
      national_id: whole ? row.national_id : maskIdentifier(row.national_id),
      contract_number: whole ? row.contract_number : maskIdentifier(row.contract_number),
      status: row.status,
      acs_device_id: row.acs_device_id,
      service_profile_id: row.service_profile_id,
      bss_subscriber_ref: row.bss_subscriber_ref,
      created_at: row.created_at,
      updated_at: row.updated_at,
    };
  }

  /**
   * Does this row satisfy EVERY criterion the caller gave? — the port of the store's
   * `search_subscribers` (`acs_service/store.py`).
   *
   * The criteria are a CONJUNCTION: a row must satisfy every one of them. The three
   * identifying fields are fields of the SUBSCRIBER row and each is an EQUALITY.
   *
   * **`subscriber_ref` matches EITHER identity.** The row has two names — the one
   * this service minted (`subscriber_id`) and the BSS's own (`bss_subscriber_ref`) —
   * and they share ONE namespace (S1's rule), so a search by either names the same
   * holder. A search that resolved only one of them would answer «there is no such
   * subscriber» about a subscriber the caller had just been given.
   *
   * **`serial` is a field of the DEVICE row**, and the answer is the subscriber who
   * HOLDS that device: a device no subscriber holds matches nothing.
   */
  function subscriberMatchesCriteria(
    row: FixtureSubscriber,
    criteria: readonly { name: string; value: string }[],
  ): boolean {
    for (const criterion of criteria) {
      const value = criterion.value;
      if (criterion.name === 'mobile') {
        if (row.mobile !== value) return false;
      } else if (criterion.name === 'national_id') {
        if (row.national_id !== value) return false;
      } else if (criterion.name === 'contract_number') {
        if (row.contract_number !== value) return false;
      } else if (criterion.name === 'subscriber_ref') {
        if (row.subscriber_id !== value && row.bss_subscriber_ref !== value) return false;
      } else if (criterion.name === 'serial') {
        const device = FIXTURE_DEVICES.find((entry) => entry.serial === value);
        if (!device || row.acs_device_id !== device.acs_device_id) return false;
      }
    }
    return true;
  }

  /**
   * One subscriber row, as THIS caller may see it — the port of `subscriber_view` of
   * `routers/subscribers.py`.
   *
   * The level decides the identifying fields and nothing else does: `edit` sees them
   * whole, every other level sees them masked (`mask_identifier`, byte for byte). The
   * mask is applied on the way OUT, exactly like the server's — the store keeps the real
   * value, because a masked value cannot be matched against anything.
   */
  function subscriberView(row: FixtureSubscriber, level: string): Subscriber {
    const whole = level === 'edit';
    return {
      subscriber_id: row.subscriber_id,
      mobile: whole ? row.mobile : maskIdentifier(row.mobile),
      national_id: whole ? row.national_id : maskIdentifier(row.national_id),
      contract_number: whole ? row.contract_number : maskIdentifier(row.contract_number),
      status: row.status,
      acs_device_id: row.acs_device_id,
      service_profile_id: row.service_profile_id,
      // The BSS's OWN reference is an IDENTIFIER, not personal data: it is not masked on
      // any level, because the party that minted it is the party that must read it.
      bss_subscriber_ref: row.bss_subscriber_ref,
      created_at: row.created_at,
      updated_at: row.updated_at,
    };
  }

  /**
   * One service-profile row. There is no mask here and no level: a profile is a
   * commercial shape (a speed, a VLAN, a DBA), not a person, so every reader of
   * `service.profile` sees the same fields.
   */
  function profileView(row: ServiceProfile): ServiceProfile {
    return { ...row, dba: { ...row.dba } };
  }

  /** The profile row under this name, or `undefined` — the store's own lookup. */
  function findProfileByName(name: string): ServiceProfile | undefined {
    for (const row of profileRows.values()) {
      if (row.name === name) return row;
    }
    return undefined;
  }

  /** The subscriber row that holds this device's link, or `undefined`. */
  function findSubscriberByDevice(acsDeviceId: string): FixtureSubscriber | undefined {
    for (const row of subscriberRows.values()) {
      if (row.acs_device_id === acsDeviceId) return row;
    }
    return undefined;
  }

  /**
   * The subscriber row that carries the BSS's OWN reference, or `undefined`.
   *
   * The reference is a SECOND identity (the row's own key is the subscriber id), and it
   * is unique where it is present: a reference that names two rows names nothing.
   */
  function findSubscriberByBssRef(bssRef: string): FixtureSubscriber | undefined {
    if (!bssRef) return undefined;
    for (const row of subscriberRows.values()) {
      if (row.bss_subscriber_ref === bssRef) return row;
    }
    return undefined;
  }

  /**
   * ONE guarded step of the subscriber's lifecycle (S4, G-SUB-05) — the port of the
   * door's `_transition_status` of `routers/subscribers.py`.
   *
   * The two steps are (the state the row must be in → the state it moves to):
   * `active` → `suspended` and `suspended` → `active`. The vocabulary is the EXISTING
   * two words — a third word would be a second vocabulary.
   *
   * **THE ORDER is the door's**: the switch answers first, then the row is read, then
   * the key is reserved, and only then is the row judged. A refusal records nothing,
   * so the key stays free for a corrected request.
   */
  async function subscriberLifecycleStep(
    step: 'suspend' | 'resume',
    subscriberId: string,
    opts: WriteOptions,
  ): Promise<Subscriber> {
    const key = assertWriteKey(opts.idempotencyKey);
    const instance = `POST /ops/v1/subscribers/${subscriberId}:${step}`;
    const expect = step === 'suspend' ? 'active' : 'suspended';
    const status = step === 'suspend' ? 'suspended' : 'active';

    // The switch answers first — a well-formed request from a caller who may make it
    // gets the honest 503, never a 404 and never an empty answer.
    if (!subscriberSurfaceEnabled) {
      throw problemError(buildProblem('subscriber-disabled', { instance }));
    }

    // The row, read through the TWO identities it carries: the minted one and the
    // BSS's own. Either of them names it.
    const row = subscriberRows.get(subscriberId) ?? findSubscriberByBssRef(subscriberId);

    // The sketch names the ROW (its own minted identity), never the name the caller
    // typed: one key must not be able to answer one subscriber with another
    // subscriber's stored answer.
    const sketch = {
      endpoint: `subscriber.${step}`,
      subscriber_id: row?.subscriber_id ?? subscriberId,
      body: {},
    };

    // The key is reserved BEFORE the row is judged — replay-equivalence: a replay of a
    // suspension that SUCCEEDED must answer the stored answer, not meet the row it has
    // just suspended and answer its own conflict.
    const replayed = replayOrThrow<Subscriber>(key, sketch);
    if (replayed !== undefined) return wait(replayed);

    if (!row) {
      throw problemError(buildProblem('subscriber-not-found', { instance }));
    }
    if (row.status !== expect) {
      throw problemError(
        buildProblem('subscriber-state-conflict', {
          detail: `this subscriber is '${row.status}' and this step requires '${expect}'`,
          instance,
        }),
      );
    }

    // The write: ONE field and the store's own stamp. No second row, and no device
    // row, no binding and no profile — the resume undoes exactly what the suspension
    // did, because the suspension did nothing else either.
    const now = (options.clock?.() ?? new Date()).toISOString();
    const moved: FixtureSubscriber = { ...row, status, updated_at: now };
    subscriberRows.set(moved.subscriber_id, moved);

    // THE AUDIT ROW — one per APPLIED transition, the way the server writes it
    // (`_transition_apply`): the row's OWN minted name (never the name the caller
    // typed), the device link, the state the row was ACTUALLY in, the state it is in
    // now, and the actor. A REFUSAL writes nothing — the guard above returns before
    // this point, so the ledger records what HAPPENED and nothing happened.
    //
    // No identifying field of the subscriber: a phone number in the audit trail is
    // personal data in a place nobody was looking for it.
    const actorRow = userRows.find((candidate) => candidate.username === fixtureSession?.username);
    subscriberLifecycleAuditRows.unshift({
      id: `fx-audit-lifecycle-${++lifecycleAuditCounter}`,
      kind: `subscriber.${step}`,
      subscriber_id: moved.subscriber_id,
      acs_device_id: moved.acs_device_id ?? null,
      from_status: row.status,
      to_status: moved.status,
      actor_kind: 'human',
      actor: identitySubject(),
      user_id: actorRow?.user_id ?? null,
      client_id: null,
      created_at: now,
    });

    // The answer carries the fields the CALLER may see — the level on the door's own
    // key (`subscriber.manage`), as on the server.
    const payload = subscriberView(moved, callerLevel('subscriber.manage'));
    record(key, sketch, payload);
    return wait(payload);
  }

  /** The list doors' own page bound — `Query(200, ge=1, le=200)` on the server. */
  const SUBSCRIBER_PAGE_LIMIT = 200;

  /**
   * `SubscriberCreateRequest`'s field rules, verbatim from `acs_service/models.py`.
   *
   * The server answers these with FastAPI's own 422 body, which carries no problem code;
   * the fixture keeps the console's `reference-invalid` convention so the same panel
   * renders, and the pages validate client-side first (`lib/validators.ts`).
   */
  function subscriberBodyProblems(body: SubscriberCreateRequest): string[] {
    const bad: string[] = [];
    if (!/^09[0-9]{9}$/.test(String(body.mobile ?? ''))) bad.push('mobile');
    const nationalId = body.national_id ?? null;
    if (nationalId !== null && !/^[0-9]{10}$/.test(nationalId)) bad.push('national_id');
    const contract = body.contract_number ?? null;
    if (contract !== null && (contract.length < 1 || contract.length > 64)) {
      bad.push('contract_number');
    }
    if (body.acs_device_id && !body.acs_device_id.startsWith('acs-device:')) {
      bad.push('acs_device_id');
    }
    if (body.service_profile_id && !/^sprof:[0-9a-f]{8}$/.test(body.service_profile_id)) {
      bad.push('service_profile_id');
    }
    // The BSS's own reference has the shape the frozen v1 `subscriber_ref` has — the
    // same `^sub:` string the BSS mints itself (`BSS_SUBSCRIBER_REF_PATTERN` of
    // `acs_service/models.py`).
    const bssRef = body.bss_subscriber_ref ?? null;
    if (bssRef !== null && !/^sub:/.test(bssRef)) bad.push('bss_subscriber_ref');
    return bad;
  }

  /**
   * `SubscriberCreateRequest.validate_body` — the SECOND gate over the body, keyed by the
   * field that is wrong: the status vocabulary, and the rule that a subscriber with
   * neither a national id nor a contract number is a row nobody can be identified by.
   */
  function subscriberSecondGate(body: SubscriberCreateRequest): string[] {
    const bad: string[] = [];
    const status = body.status ?? 'active';
    if (!(SUBSCRIBER_STATUSES as readonly string[]).includes(status)) bad.push('status');
    if (!body.national_id && !body.contract_number) bad.push('national_id|contract_number');
    return bad;
  }

  /**
   * `ServiceProfileWriteRequest`'s field rules, verbatim from `acs_service/models.py`.
   * Same convention as the subscriber body: the server's FastAPI 422 carries no problem
   * code, the console renders `reference-invalid`.
   */
  function profileBodyProblems(body: ServiceProfileWriteRequest): string[] {
    const bad: string[] = [];
    const name = String(body?.name ?? '');
    if (name.length < 1 || name.length > 64) bad.push('name');
    if (!Number.isInteger(body?.down_mbps) || body.down_mbps < 1 || body.down_mbps > 100000) {
      bad.push('down_mbps');
    }
    if (!Number.isInteger(body?.up_mbps) || body.up_mbps < 1 || body.up_mbps > 100000) {
      bad.push('up_mbps');
    }
    if (!Number.isInteger(body?.vlan_id) || body.vlan_id < 1 || body.vlan_id > 4094) {
      bad.push('vlan_id');
    }
    const dba = body?.dba;
    if (!dba || typeof dba.tcont !== 'string' || dba.tcont.length < 1 || dba.tcont.length > 64) {
      bad.push('dba.tcont');
    }
    for (const ceiling of ['assured_mbps', 'maximum_mbps'] as const) {
      const value = dba?.[ceiling];
      if (value === undefined || value === null) continue;
      if (!Number.isInteger(value) || value < 0 || value > 100000) bad.push(`dba.${ceiling}`);
    }
    return bad;
  }

  /** `n` random bytes as lowercase hex — the alphabet `secrets.token_hex` produces. */
  function hexToken(bytes: number): string {
    const buffer = new Uint8Array(bytes);
    globalThis.crypto.getRandomValues(buffer);
    return Array.from(buffer, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  /** `sub:` + twelve hex characters — the door mints the identity, server-side. */
  function mintSubscriberId(): string {
    return `sub:${hexToken(6)}`;
  }

  /** `sprof:` + eight hex characters — the service profile's own key. */
  function mintProfileId(): string {
    return `sprof:${hexToken(4)}`;
  }

  /* ---------------- operator profile (U-16-e2) ---------------- */

  /**
   * `_judge_candidate` of `routers/profile.py`, ported: validation runs BEFORE the idempotency
   * check, so a rejected body records nothing and the same key stays reusable for the next attempt
   * (H8-b's «rejections are not recorded»). Both refusals carry the whole report in `problems` —
   * the same shape `:validate` publishes, so one defect has one spelling in the two doors.
   */
  function judgeCandidate(document: unknown): ProfileDocument {
    const [blessed, found] = validateFixtureProfile(document);
    if (blessed !== null) return blessed;
    const blocked = found.filter((p) => p.rule.startsWith('release-'));
    if (blocked.length > 0 && blocked.length === found.length) {
      const err = problemError(
        buildProblem('profile-release-blocked', {
          detail:
            `this candidate's only complaints are the ${found.length} release rule(s) this ` +
            'deployment enforces, so it may not be activated here; nothing was stored',
          instance: 'PUT /ops/v1/operator-profile',
        }),
      );
      (err.problem as { problems?: unknown }).problems = found;
      throw err;
    }
    const err = problemError(
      buildProblem('profile-invalid', {
        detail: `the candidate document does not pass validation (${found.length} complaint(s)); nothing was stored`,
        instance: 'PUT /ops/v1/operator-profile',
      }),
    );
    (err.problem as { problems?: unknown }).problems = found;
    throw err;
  }

  /**
   * `Store.activate_operator_profile` on the memory backend is a compare-and-set inside a critical
   * section; this fixture holds the same single-threaded property, and the losing race is answered
   * the same way: 409 `profile-stale-version` with the store's own read-back as `current_version`,
   * and nothing stored. Rows already written are byte-identical afterwards — versions are
   * immutable, an activation APPENDS, and a rollback mints a forward version with `rollback_of`.
   */
  function activateProfile(
    document: ProfileDocument,
    expectedVersion: number,
    rollbackOf: number | null,
  ): ProfileActivationAccepted {
    if (
      expectedVersion !== (profileState.active_version ?? 0) &&
      !(profileState.active_version === null && expectedVersion === 0)
    ) {
      const err = problemError(
        buildProblem('profile-stale-version', {
          detail:
            `the profile is no longer at version ${expectedVersion}: this deployment's store is ` +
            `at version ${profileState.active_version}. Re-read it and diff before activating again; nothing was stored`,
          instance:
            rollbackOf === null
              ? 'PUT /ops/v1/operator-profile'
              : 'POST /ops/v1/operator-profile:rollback',
        }),
      );
      (err.problem as { current_version?: unknown }).current_version = profileState.active_version;
      throw err;
    }
    const highest = profileState.versions.reduce((max, row) => Math.max(max, row.version), 0);
    const next = Math.max(profileState.active_version ?? 0, highest) + 1;
    profileState.versions.push({
      version: next,
      activated_at: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
      actor: { kind: 'human', subject: fixtureSession?.username ?? 'owner-demo' },
      document: { ...document },
    });
    // The store trims to `PROFILE_VERSION_HISTORY` (mirrored as 100 in `fixtures/profile.ts`).
    if (profileState.versions.length > 100) {
      profileState.versions.splice(0, profileState.versions.length - 100);
    }
    profileState.active_version = next;
    return rollbackOf === null
      ? {
          active_version: next,
          previous_version: expectedVersion === 0 ? null : expectedVersion,
          rollback_of: null,
          note: ACTIVATION_INTENT_NOTE,
        }
      : {
          active_version: next,
          previous_version: expectedVersion === 0 ? null : expectedVersion,
          rollback_of: rollbackOf,
          note: ROLLBACK_INTENT_NOTE,
        };
  }

  /* ---------------- activation / diagnostics helpers ---------------- */

  /**
   * `ActivationRequest` field rules, verbatim from `acs_service/models.py`.
   * The server answers these with FastAPI's default 422 body (no problem code is
   * invented for them); the fixture keeps the console's `reference-invalid`
   * convention so the same panel renders, and the pages validate client-side
   * first (see `lib/validators.ts`).
   */
  function validateActivationRequest(body: ActivationRequest): void {
    if (!SERIAL_PATTERN.test(body.serial)) {
      throw problemError(
        buildProblem('reference-invalid', {
          detail: `"${body.serial}" is not a valid serial (must match ^[A-Za-z0-9._-]{6,32}$)`,
          instance: 'POST /ops/v1/activations',
        }),
      );
    }
    for (const field of REQUIRED_ACTIVATION_FIELDS) {
      const value = body[field];
      if (typeof value !== 'string' || value.trim().length === 0) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `${field} must not be empty`,
            instance: 'POST /ops/v1/activations',
          }),
        );
      }
    }
    requireRef(body.subscriber_ref, SUBSCRIBER_REF_PREFIX, 'subscriber_ref');
  }

  /** `DiagnosticsRequest` rules, with the server's own wording for the option seam. */
  function validateDiagnosticsRequest(body: DiagnosticsRequest): void {
    if (!(DIAGNOSTICS_TYPES as readonly string[]).includes(body.type)) {
      throw problemError(
        buildProblem('reference-invalid', {
          detail: `type must be one of ${DIAGNOSTICS_TYPES.join('|')}`,
          instance: 'POST /integration/v1/devices/{id}/diagnostics',
        }),
      );
    }
    if (body.target.length === 0) {
      throw problemError(
        buildProblem('reference-invalid', {
          detail: 'target must not be empty',
          instance: 'POST /integration/v1/devices/{id}/diagnostics',
        }),
      );
    }
    if (body.target.startsWith('-')) {
      throw problemError(
        buildProblem('reference-invalid', {
          detail:
            "target must not start with '-': it is forwarded to the CPE diagnostics " +
            'object and would be read as an option',
          instance: 'POST /integration/v1/devices/{id}/diagnostics',
        }),
      );
    }
    if (!DIAGNOSTICS_TARGET_PATTERN.test(body.target)) {
      throw problemError(
        buildProblem('reference-invalid', {
          detail: `"${body.target}" is not a valid target (must match ^[A-Za-z0-9._:-]+$)`,
          instance: 'POST /integration/v1/devices/{id}/diagnostics',
        }),
      );
    }
  }

  /** `_has_capability` from `routers/diagnostics.py` (case-insensitive). */
  function hasCapability(device: FixtureDevice, capability: string): boolean {
    return device.capabilities.some((entry) => String(entry).trim().toLowerCase() === capability);
  }

  /**
   * The leaf name of a parameter path (`a.b.C` → `C`), as `diagnostics.py` reads it with
   * `path.rpartition('.')[2]`.
   */
  function leafName(path: string): string {
    return path.slice(path.lastIndexOf('.') + 1);
  }

  /**
   * `acs_service/diagnostics.py::attribute`, ported: the object that names **this probe's host**
   * wins, and only the leaves under that object's own prefix are its answer. A board that holds an
   * object from somebody else's probe is not an answer, and publishing it is the exact failure the
   * server refuses with `unattributed`.
   */
  function attributedLeaves(read: ProbeObject, target: string): Record<string, unknown> | null {
    for (const [path, value] of Object.entries(read.leaves)) {
      if (!ATTRIBUTION_LEAVES.includes(leafName(path))) continue;
      if (String(value ?? '') !== target) continue;
      const out: Record<string, unknown> = {};
      for (const [other, otherValue] of Object.entries(read.leaves)) {
        if (other.startsWith(read.prefix)) out[other] = otherValue;
      }
      return out;
    }
    return null;
  }

  /** `diagnostics.py::state_of` — the board's own `DiagnosticsState` word, if it reported one. */
  function stateWord(leaves: Record<string, unknown>): string | null {
    for (const [path, value] of Object.entries(leaves)) {
      if (leafName(path) === 'DiagnosticsState') return String(value ?? '');
    }
    return null;
  }

  /** The hosts the objects on this board do name, published with `unattributed`. */
  function hostsSeen(read: ProbeObject): string[] {
    return [
      ...new Set(
        Object.entries(read.leaves)
          .filter(([path]) => ATTRIBUTION_LEAVES.includes(leafName(path)))
          .map(([, value]) => String(value ?? '')),
      ),
    ].sort();
  }

  /**
   * The read-back pass, in fixture form: `diagnostics.py::settle`'s cases over the board object from
   * `fixtures/diagnostics.ts`. `pending` is the task that has not ended — nothing is written and the
   * job stays `queued`, which is the honest answer for a probe nobody has answered. Every other fate
   * closes the job with what the board said, **including** the two that say it said nothing:
   * `no-diagnostics-object` and `unattributed` are reported as absences, never as a number.
   */
  function settleProbe(entry: ProbeJobEntry): Job {
    const job = entry.job;
    if (job.status !== 'queued') return job;
    if (diagnosticsFate === 'pending') return job;

    const base: Record<string, unknown> = {
      task: 'diagnostics',
      probe: entry.probe,
      target: entry.target,
      nbi_task: entry.nbiTask,
    };
    const stamp = isoAgo(0);

    if (diagnosticsFate === 'faulted') {
      return {
        ...job,
        status: 'failed',
        completed_at: stamp,
        result: { ...base, outcome: `cwmp.${REFUSED_FAULT_CODE}` },
      };
    }

    const read = probeObjectFor(entry.probe, entry.target, {
      tr181: dataModelRootFor(entry.device) === 'tr181',
      fate: diagnosticsFate,
    });
    if (read === null) {
      return {
        ...job,
        status: 'failed',
        completed_at: stamp,
        result: {
          ...base,
          outcome: 'no-diagnostics-object',
          read_prefixes: candidatesFor(entry.probe),
        },
      };
    }

    const observed = attributedLeaves(read, entry.target);
    if (observed === null) {
      return {
        ...job,
        status: 'failed',
        completed_at: stamp,
        result: {
          ...base,
          read_from: read.readPrefix,
          outcome: 'unattributed',
          hosts_seen: hostsSeen(read),
        },
      };
    }

    return {
      ...job,
      status: 'completed',
      completed_at: stamp,
      result: {
        ...base,
        read_from: read.prefix,
        observed,
        outcome: stateWord(observed) ?? NO_DIAGNOSTICS_STATE,
        read_at: stamp,
      },
    };
  }

  /**
   * `GET /ops/v1/activations/{id}` read-back: `failed` is returned as recorded,
   * anything else is advanced by the server's derivation rule (a discovered
   * `acs-device:<serial>` document ⇒ at least `informed`).
   */
  function readActivation(activationId: string): ActivationStatus {
    const entry = activations.get(activationId);
    if (!entry) {
      throw problemError(
        buildProblem('job-not-found', {
          detail: `no activation ${activationId}`,
          instance: `GET /ops/v1/activations/${activationId}`,
        }),
      );
    }
    const discovered = findDevice(`acs-device:${entry.serial}`) !== undefined;
    const status = raiseToInformed(entry.status, discovered);
    const read: ActivationStatus = {
      activation_id: entry.activation_id,
      status,
      steps: entry.steps.map((step) => ({ ...step })),
    };
    if (status === 'failed') {
      read.error = entry.error ?? { step: '', code: '', detail: '' };
      return read;
    }
    const deviceId = entry.acs_device_id ?? (discovered ? `acs-device:${entry.serial}` : undefined);
    if (deviceId !== undefined) read.acs_device_id = deviceId;
    return read;
  }

  /**
   * `OnuAuthorizeRequest` field rules, verbatim from `acs_service/models.py`:
   * `sn` carries `^[A-Za-z0-9._-]{6,32}$` (same pattern as an activation serial)
   * and `pon_port` `min_length=1`. Same convention as `validateActivationRequest`:
   * the console renders these as `reference-invalid` and the pages validate
   * client-side first, because FastAPI's own body-validation 422 carries no
   * problem code.
   */
  function validateOnuAuthorize(body: OnuAuthorizeRequest, instance: string): void {
    if (!SERIAL_PATTERN.test(body.sn)) {
      throw problemError(
        buildProblem('reference-invalid', {
          detail: `"${body.sn}" is not a valid serial (must match ^[A-Za-z0-9._-]{6,32}$)`,
          instance,
        }),
      );
    }
    if (typeof body.pon_port !== 'string' || body.pon_port.trim().length === 0) {
      throw problemError(
        buildProblem('reference-invalid', {
          detail: 'pon_port must not be empty',
          instance,
        }),
      );
    }
  }

  /**
   * The ONU table of one OLT, with the two failures the proxy can answer:
   * an OLT outside the adapter inventory (`olt-not-found`) and an adapter that
   * could not read this table (`adapter-unreachable` — the same condition the
   * inventory row reports through its `note`).
   */
  function oltTableOrThrow(oltId: string, instance: string): OnuTableRow[] {
    const table = onuTables.get(oltId);
    if (!table) {
      throw problemError(
        buildProblem('olt-not-found', { detail: `unknown olt ${oltId}`, instance }),
      );
    }
    const note = FIXTURE_OLT_TABLE_NOTES[oltId];
    if (note) {
      throw problemError(buildProblem('adapter-unreachable', { detail: note, instance }));
    }
    return table;
  }

  function consumeConnectionRequest(instance: string): void {
    if (!bucket || bucket.allow()) return;
    throw problemError(
      buildProblem('rate-limited', {
        detail: 'connection-request budget exhausted; retry later',
        instance,
      }),
      bucket.retryAfterSeconds(),
    );
  }

  function forceScenario(instance: string): void {
    if (!forced.code) return;
    const code = forced.code;
    forced.code = null;
    throw problemError(
      buildProblem(code, {
        detail: `forced fixture scenario: ${code}`,
        instance,
      }),
      code === 'rate-limited' ? forced.retryAfter : undefined,
    );
  }

  return {
    dataSource: 'fixture',

    health: () => wait<Health>(buildFixtureHealth()),

    listDevices: (query: ListDevicesQuery): Promise<DevicesListResponse> => {
      let rows = [...FIXTURE_DEVICES];
      if (typeof query.online === 'boolean') {
        rows = rows.filter((d) => d.online === query.online);
      }
      if (query.manufacturer) {
        rows = rows.filter((d) => d.manufacturer === query.manufacturer);
      }
      // The server maps `model` onto `product_class` (routers/devices.py).
      if (query.model) {
        rows = rows.filter((d) => d.product_class === query.model);
      }
      const page = paginate(rows, query.cursor, query.limit ?? 20);
      return wait<DevicesListResponse>({
        items: page.items.map((d) => summaryOf(d)),
        next_cursor: page.next_cursor,
      });
    },

    getDevice: async (acsDeviceId: string): Promise<DeviceSummary> => {
      const { device } = deviceOrThrow(acsDeviceId);
      return wait(summaryOf(device));
    },

    /* ---- the dashboard's aggregate counts (V4 / G-TLM-05) ----
       The doors count over the WHOLE store, so the fixture reduces its whole set — never a page.
       A fixture that reduced `listDevices`' first page would reproduce, one layer down, exactly
       the sample bug these doors were built to remove (`offline = total − online` and
       `unbound = total − bound` are the server's own arithmetic, not a client convenience). */

    getDeviceStats: async (): Promise<DeviceStats> => {
      forceScenario('/ops/v1/stats/devices');
      const rows = FIXTURE_DEVICES.map(summaryOf);
      const total = rows.length;
      const online = rows.filter((row) => row.online).length;
      return wait<DeviceStats>({
        total,
        online,
        offline: total - online,
        unbound: total - rows.filter((row) => row.nas_ref !== null).length,
        counted_at: new Date().toISOString(),
      });
    },

    getAlarmStats: async (): Promise<AlarmStats> => {
      forceScenario('/ops/v1/stats/alarms');
      const rows = deriveAlarmRows(lifecyclesOf());
      const byState = Object.fromEntries(ALARM_STATES.map((state) => [state, 0])) as Record<
        AlarmState,
        number
      >;
      const bySeverity = Object.fromEntries(
        ALARM_SEVERITIES.map((severity) => [severity, 0]),
      ) as Record<AlarmSeverity, number>;
      for (const row of rows) {
        byState[row.state] += 1;
        // `cleared` is the end of the lifecycle, not open work — the server's own rule.
        if (row.state === 'open' || row.state === 'acked') bySeverity[row.severity] += 1;
      }
      return wait<AlarmStats>({
        total: rows.length,
        active: byState.open + byState.acked,
        by_state: byState,
        active_by_severity: bySeverity,
        counted_at: new Date().toISOString(),
      });
    },

    getParameters: async (acsDeviceId: string, query: ListParamsQuery): Promise<ParamsResponse> => {
      const { device } = deviceOrThrow(acsDeviceId);
      const all = parametersFor(device);
      // `?path=` is a TR-098 path *prefix* filter on the server, not full text.
      const prefix = query.path?.toLowerCase();
      const filtered = prefix ? all.filter((p) => p.path.toLowerCase().startsWith(prefix)) : all;
      const page = paginate(filtered, query.cursor, query.limit ?? 20);
      return wait<ParamsResponse>({
        items: page.items,
        next_cursor: page.next_cursor,
        source_version: 'genieacs-v1.2',
        stale: !device.online,
      });
    },

    getStations: async (
      acsDeviceId: string,
      query: ListStationsQuery,
    ): Promise<StationsResponse> => {
      const { device } = deviceOrThrow(acsDeviceId);
      const page = paginate(stationsFor(device), query.cursor, query.limit ?? 20);
      return wait<StationsResponse>({
        items: page.items,
        next_cursor: page.next_cursor,
        stale: !device.online,
      });
    },

    getJob: async (jobId: string): Promise<Job> => {
      const session = sessionJobs.get(jobId);
      if (session) return wait(jobFromRow(session.row));
      const diagnostic = diagnosticJobs.get(jobId);
      if (diagnostic) return wait<Job>({ ...diagnostic.job });
      for (let index = 0; index < FIXTURE_DEVICES.length; index += 1) {
        const device = FIXTURE_DEVICES[index];
        if (!device) continue;
        const match = jobsFor(device, index).find((row) => row.job_id === jobId);
        if (match) return wait(jobFromRow(match));
      }
      throw problemError(
        buildProblem('job-not-found', {
          detail: `no job with id ${jobId} is known to the ACS`,
          instance: `GET /integration/v1/jobs/${jobId}`,
        }),
      );
    },

    pollEvents: (query: ListEventsQuery): Promise<EventReadResponse> => {
      const page = paginate(FIXTURE_EVENTS, query.cursor, query.limit ?? 20);
      return wait<EventReadResponse>({
        items: page.items,
        next_cursor: page.next_cursor,
      });
    },

    /* ---- the live-event channel (N9 slice 2, plan D3) ----
       A fixture has no server to hold a connection open, and inventing a live one
       would be the oldest lie this repo forbids. What the fixture CAN mirror
       honestly is the door's own shape: history first — exactly the outbox the
       poll door serves — then quiet, forever. The burst is one-shot and never
       grows: a fixture that kept emitting later would be fabricating a fleet. */
    streamEvents: (handler: LiveEventHandler, options: EventStreamOptions): EventStreamHandle => {
      let cancelled = false;
      void (async () => {
        if (options.signal.aborted) return;
        handler.onStatus('connecting');
        await wait(0);
        if (cancelled || options.signal.aborted) return;
        handler.onStatus('live');
        for (const event of FIXTURE_EVENTS) {
          if (cancelled || options.signal.aborted) return;
          handler.onEvent({ cursorId: event.id, event });
        }
      })();
      return {
        close: () => {
          cancelled = true;
        },
      };
    },

    runAction: async (
      acsDeviceId: string,
      body: ActionRequest,
      opts: WriteOptions,
    ): Promise<ActionResponse> => {
      const { device } = deviceOrThrow(acsDeviceId);
      assertWriteKey(opts.idempotencyKey);

      if (!CONTRACT_ACTIONS.includes(body.action)) {
        throw problemError(
          buildProblem('action-unsupported', {
            detail: `action "${body.action}" is not part of the v1 contract`,
            instance: `POST /integration/v1/devices/${acsDeviceId}/actions`,
          }),
        );
      }

      // Fail-closed commands — exactly the live behaviour for an offline ONU.
      if (!device.online) {
        throw problemError(
          buildProblem('device-offline', {
            detail: `${acsDeviceId} is offline; command rejected (fail-closed, queue policy in phase 3)`,
            instance: `POST /integration/v1/devices/${acsDeviceId}/actions`,
          }),
        );
      }

      if (body.action === 'download' && !body.desired_state?.file) {
        throw problemError(
          buildProblem('action-unsupported', {
            detail: 'download requires desired_state.file (firmware file UUID)',
            instance: `POST /integration/v1/devices/${acsDeviceId}/actions`,
          }),
        );
      }

      if (body.action === 'apply_policy') {
        requireRef(body.policy_ref ?? null, 'policy:', 'policy_ref');
      }

      consumeConnectionRequest(`POST /integration/v1/devices/${acsDeviceId}/actions`);
      forceScenario(`POST /integration/v1/devices/${acsDeviceId}/actions`);

      return wait(
        replayOrRecord(opts.idempotencyKey, body, () => {
          jobCounter += 1;
          const jobId = `job:fx${String(jobCounter).padStart(4, '0')}${device.serial.slice(-4)}`;
          const row: DeviceJobRow = {
            job_id: jobId,
            action: body.action,
            status: 'queued',
            requested_at: new Date().toISOString(),
            started_at: null,
            completed_at: null,
            result_summary: null,
          };
          sessionJobs.set(jobId, { deviceId: acsDeviceId, row });
          return { job_id: jobId, status: 'queued' } satisfies ActionResponse;
        }),
      );
    },

    bindDevice: async (body: BindingRequest, opts: WriteOptions): Promise<BindingResponse> => {
      const { device } = deviceOrThrow(body.acs_device_id);
      assertWriteKey(opts.idempotencyKey);
      requireRef(body.acs_device_id, 'acs-device:', 'acs_device_id');
      requireRef(body.nas_ref, 'nas:', 'nas_ref');
      requireRef(body.subscriber_ref, 'sub:', 'subscriber_ref');
      requireRef(body.org_ref, 'org:', 'org_ref');
      requireRef(body.asset_ref, 'asset:', 'asset_ref');

      const current = bindingOverlay.get(device.acs_device_id) ?? {
        nas_ref: device.nas_ref ?? '',
        binding_version: device.binding_version,
      };
      if (current.nas_ref && current.nas_ref !== body.nas_ref) {
        throw problemError(
          buildProblem('binding-conflict', {
            detail: `device already bound to ${current.nas_ref}`,
            instance: 'POST /integration/v1/devices:bind',
          }),
        );
      }

      return wait(
        replayOrRecord(opts.idempotencyKey, body, () => {
          const version = current.binding_version + 1;
          bindingOverlay.set(device.acs_device_id, {
            nas_ref: body.nas_ref,
            binding_version: version,
            subscriber_ref: body.subscriber_ref ?? null,
          });
          return { bound: true, binding_version: version } satisfies BindingResponse;
        }),
      );
    },

    /* ---- fleet jobs — `GET /ops/v1/jobs` (`routers/system.py:177`), U-16-f ----
       The store's three filters and its cursor paging, mirrored exactly: `device` and `status` are
       exact matches, `type` is an ACTION PREFIX (`diagnostics:` → the probe family), and `total`
       counts the rows matching THIS filter over the whole set rather than the page. An unknown
       device is a query, so it is an empty page — not a 404, and not a fabricated row. */

    listJobs: async (query: ListJobsQuery): Promise<JobsListResponse> =>
      wait(fixtureJobsPage(fleetJobRows(), query)),

    listDeviceJobs: async (
      acsDeviceId: string,
      query: Omit<ListJobsQuery, 'device'> = {},
    ): Promise<JobsListResponse> =>
      wait(fixtureJobsPage(fleetJobRows(), { ...query, device: acsDeviceId })),

    /* ---- per-device operations / bulk / workflow — `routers/operations.py`, U-16-f ----
       Same rule set as the doors: locked slots stay listed (B-6), the gate owns both `available` and
       the write, a refusal under the gate queues and audits NOTHING ("a 409 that also wrote a job
       would be a lie twice"), and an accepted write answers 202 `queued` with a `job_id` and no claim
       about the device (invariant ۳ / D6). */

    listDeviceOperations: async (
      acsDeviceId: string,
      query: ListOperationsQuery = {},
    ): Promise<OperationsResponse> => {
      const { device } = deviceOrThrow(acsDeviceId);
      if (query.root !== undefined && query.root !== 'tr098' && query.root !== 'tr181') {
        throw referenceInvalid(
          `root=${JSON.stringify(query.root)} is not one of tr098, tr181 — an unknown filter must ` +
            "not read as 'this device has no operations'",
          `GET /ops/v1/devices/${acsDeviceId}/operations`,
        );
      }
      return wait(fixtureOperationsResponse(device, query));
    },

    getOperationValue: async (acsDeviceId: string, op: string): Promise<OperationValueResponse> => {
      const { device } = deviceOrThrow(acsDeviceId);
      if (!isKnownOperation(op)) {
        throw operationNotFound(
          `${JSON.stringify(op)} is not an operator operation of this catalog — bootstrap and ` +
            'policy.* rows are not operations on a device (§۲.۳)',
          `GET /ops/v1/devices/${acsDeviceId}/operations/${op}/value`,
        );
      }
      return wait(fixtureOperationValue(device, op));
    },

    setOperationValue: async (
      acsDeviceId: string,
      op: string,
      body: OperationWriteRequest,
      opts: WriteOptions,
    ): Promise<OperationWriteAccepted> => {
      assertWriteKey(opts.idempotencyKey);
      const { device } = deviceOrThrow(acsDeviceId);
      const slot = operationSlotOrThrow(device, op, acsDeviceId, 'PUT');
      const instance = `PUT /ops/v1/devices/${acsDeviceId}/operations/${op}`;
      // The door's own refusal order: body, gate, offline. Nothing under the gate is queued.
      const rejected = catalogRejection(slot, body.value);
      if (rejected !== null) {
        throw referenceInvalid(`${JSON.stringify(op)} rejects this body: ${rejected}`, instance);
      }
      if (!slot.available) throw capabilityUnproven(op, slot, device, instance);
      if (!device.online) {
        throw deviceOffline(
          `${acsDeviceId} is offline; a write needs a session to carry the SetParameterValues ` +
            'and there is none (fail-closed)',
          instance,
        );
      }
      return wait(
        replayOrRecord(opts.idempotencyKey, body, () => {
          const summary =
            body.readback === false
              ? 'read-back not asked; nothing on this record can become a verdict'
              : "read-back asked; the verdict is the read-back's, not this door's";
          return {
            job_id: mintOperationJob(device, 'operation.set', op, summary),
            status: 'queued',
          } satisfies OperationWriteAccepted;
        }),
      );
    },

    runOperation: async (
      acsDeviceId: string,
      op: string,
      body: OperationRunRequest,
      opts: WriteOptions,
    ): Promise<OperationRunAccepted> => {
      assertWriteKey(opts.idempotencyKey);
      const { device } = deviceOrThrow(acsDeviceId);
      const slot = operationSlotOrThrow(device, op, acsDeviceId, 'POST');
      const instance = `POST /ops/v1/devices/${acsDeviceId}/operations/${op}:run`;
      if (slot.kind !== 'action') {
        throw notAnAction(
          `${JSON.stringify(op)} is a parameter slot of this catalog, so there is no method to run — ` +
            `it is written with PUT /ops/v1/devices/${acsDeviceId}/operations/${op} (§۴.۳), which is ` +
            'where its value, type and read-back are judged',
          instance,
        );
      }
      const expected = runConfirmText(slot.rpc ?? '', device.model);
      if (body.confirm !== expected) {
        throw confirmationRequired(
          `${deviceWordsOf(device)}: running an RPC must be confirmed by typing RUN <method> <model>; ` +
            `for this device that is ${JSON.stringify(expected)}, and it carried ` +
            (body.confirm === undefined || body.confirm === null
              ? 'nothing'
              : JSON.stringify(body.confirm)),
          instance,
        );
      }
      if (!slot.available) throw capabilityUnproven(op, slot, device, instance);
      if (!device.online) {
        throw deviceOffline(
          `${acsDeviceId} is offline; the method needs a session to carry it and there is none`,
          instance,
        );
      }
      return wait(
        replayOrRecord(opts.idempotencyKey, body, () => {
          const jobId = mintOperationJob(
            device,
            'operation.run',
            op,
            "the method was handed over; the verdict is the post-dispatch reading's (§۵.۳ step ۶ does not apply to an RPC)",
          );
          return {
            job_id: jobId,
            status: 'queued',
            rpc: slot.rpc ?? '',
            op,
          } satisfies OperationRunAccepted;
        }),
      );
    },

    listTemplates: async (_opts?: ReadOptions): Promise<TemplateListResponse> =>
      wait<TemplateListResponse>(templateStore.list()),

    /* ---- reconciliation (read door): the fixture has NO C6 worker, so it carries ZERO runs
       and the door's own note — inventing runs would be the D4 lie with a date on it. */
    listReconciliation: async (_opts?: ReadOptions): Promise<ReconciliationListResponse> =>
      wait<ReconciliationListResponse>({
        runs: [],
        total: 0,
        next_cursor: null,
        note: 'worker reconciliation هنوز سیم‌کشی نشده (C6): فهرست خالی یعنی گزارشی ثبت نشده، نه اینکه ناوگان سازگار است',
      }),

    upsertTemplate: async (
      name: string,
      document: Record<string, unknown>,
      _opts: WriteOptions,
    ): Promise<PresetWriteResponse> =>
      wait<PresetWriteResponse>(templateStore.upsert(name, document)),

    writeRawParameters: async (
      acsDeviceId: string,
      body: RawWriteRequest,
      opts: WriteOptions,
    ): Promise<OperationWriteAccepted> => {
      assertWriteKey(opts.idempotencyKey);
      const { device } = deviceOrThrow(acsDeviceId);
      const instance = `PUT /ops/v1/devices/${acsDeviceId}/parameters/raw`;
      if (Object.keys(body.paths).length === 0) {
        throw referenceInvalid('paths must carry at least one entry (min_length=1)', instance);
      }
      // §۶.۱'s layer ۲ is graded BEFORE the gate, exactly like the door: a body whose operator did
      // not look at the model is not yet a request worth examining.
      const expected = rawConfirmText(device.model, Object.keys(body.paths).length);
      if (body.confirm !== expected) {
        throw confirmationRequired(
          `${deviceWordsOf(device)}: a raw write must be confirmed by typing WRITE-RAW <model> <count>; ` +
            `for this device and this body that is ${JSON.stringify(expected)}, and it carried ` +
            (body.confirm === undefined || body.confirm === null
              ? 'nothing'
              : JSON.stringify(body.confirm)),
          instance,
        );
      }
      const table = new Map(parametersFor(device).map((item) => [item.path, item]));
      for (const path of Object.keys(body.paths)) {
        if (!table.has(path)) {
          throw capabilityUnprovenPath(path, device, instance);
        }
      }
      if (!device.online) {
        throw deviceOffline(
          `${acsDeviceId} is offline; a raw write needs a session to carry the SetParameterValues`,
          instance,
        );
      }
      return wait(
        replayOrRecord(opts.idempotencyKey, body, () => {
          const count = Object.keys(body.paths).length;
          return mintAccepted(
            device,
            'parameters.raw',
            `${count} path(s) named by the caller, outside the per-operation vocabulary (§۴.۵)`,
          );
        }),
      );
    },

    /* ---- ZTP activations: POST /ops/v1/activations ---- */

    createActivation: async (
      body: ActivationRequest,
      opts: WriteOptions,
    ): Promise<ActivationAccepted> => {
      assertWriteKey(opts.idempotencyKey);
      // Body rules run first, exactly like FastAPI request validation.
      validateActivationRequest(body);
      const instance = 'POST /ops/v1/activations';
      const sketch = { endpoint: 'activations:create', body };

      const replayed = replayOrThrow<ActivationAccepted>(opts.idempotencyKey, sketch);
      if (replayed !== undefined) return wait(replayed);

      // No device-existence or online gate: the CPE has not informed yet (P1).
      forceScenario(instance);

      activationCounter += 1;
      const activationId = `job:fxact${String(activationCounter).padStart(4, '0')}${body.serial.slice(-4)}`;
      const vlan = body.vlan ?? null;

      if (!FIXTURE_OLT_IDS.includes(body.olt_id)) {
        // The canned adapter raises `olt-not-found` from its first call; the
        // façade records the failed step and the job, THEN answers the problem
        // (`_run_activation` in routers/ops.py), so the id stays readable back.
        const step = failedStep(
          'authorize',
          SESSION_ACTIVATION_AGO,
          'olt-not-found',
          `unknown olt ${body.olt_id}`,
        );
        activations.set(activationId, {
          activation_id: activationId,
          status: 'failed',
          serial: body.serial,
          olt_id: body.olt_id,
          pon_port: body.pon_port,
          requested_at: isoAgo(SESSION_ACTIVATION_AGO),
          steps: [step],
          error: { step: 'authorize', code: 'olt-not-found', detail: `unknown olt ${body.olt_id}` },
        });
        throw problemError(
          buildProblem('olt-not-found', {
            detail: `unknown olt ${body.olt_id}`,
            instance,
          }),
        );
      }

      const steps = successSteps(
        body.olt_id,
        body.serial,
        body.pon_port,
        body.line_profile,
        body.service_profile,
        vlan,
        SESSION_ACTIVATION_AGO,
      );
      activations.set(activationId, {
        activation_id: activationId,
        status: 'await_inform',
        serial: body.serial,
        olt_id: body.olt_id,
        pon_port: body.pon_port,
        requested_at: isoAgo(SESSION_ACTIVATION_AGO),
        steps,
      });
      const payload: ActivationAccepted = { activation_id: activationId, status: 'await_inform' };
      record(opts.idempotencyKey, sketch, payload);
      return wait(payload);
    },

    getActivation: async (activationId: string): Promise<ActivationStatus> =>
      wait(readActivation(activationId)),

    /* ---- device diagnostics: POST …/diagnostics + read-back ---- */

    runDiagnostics: async (
      acsDeviceId: string,
      body: DiagnosticsRequest,
      opts: WriteOptions,
    ): Promise<ActionResponse> => {
      assertWriteKey(opts.idempotencyKey);
      validateDiagnosticsRequest(body);
      const { device } = deviceOrThrow(acsDeviceId);
      const action = `diagnostics:${body.type}`;
      const instance = `POST /integration/v1/devices/${acsDeviceId}/diagnostics`;
      const sketch = { endpoint: action, acs_device_id: acsDeviceId, body };

      // Replays return the stored answer without re-running the probe on the CPE.
      const replayed = replayOrThrow<ActionResponse>(opts.idempotencyKey, sketch);
      if (replayed !== undefined) return wait(replayed);

      if (!device.online) {
        throw problemError(
          buildProblem('device-offline', {
            detail:
              `${acsDeviceId} is offline; diagnostics rejected (fail-closed, ` +
              'queue policy in phase 3)',
            instance,
          }),
        );
      }

      // Fail-closed capability gate — the same answer the API gives, which is why
      // the page disables the option but must never be the only line of defence.
      if (body.type === 'speedtest' && !hasCapability(device, TR143)) {
        throw problemError(
          buildProblem('action-unsupported', {
            detail:
              `${acsDeviceId} lacks the '${TR143}' capability ` +
              '(TR-143 throughput test) required for speedtest',
            instance,
          }),
        );
      }

      forceScenario(instance);
      consumeConnectionRequest(instance);

      diagnosticCounter += 1;
      const jobId = `job:fxdiag${String(diagnosticCounter).padStart(4, '0')}${device.serial.slice(-4)}`;
      const requestedAt = isoAgo(1);
      // §۵.۸: the door queues a CWMP task and nothing else. `result` is null here on purpose — the
      // only way this console shows a number is a read of the board's object, done by `getDiagnostics`.
      const job: Job = {
        job_id: jobId,
        action,
        status: 'queued',
        result: null,
        started_at: requestedAt,
        completed_at: null,
        requested_at: requestedAt,
      };
      diagnosticJobs.set(jobId, {
        deviceId: acsDeviceId,
        device,
        job,
        probe: body.type,
        target: body.target,
        nbiTask: {
          _id: `task:fxdiag${String(diagnosticCounter).padStart(4, '0')}`,
          name: body.type,
        },
      });
      const payload: ActionResponse = { job_id: jobId, status: job.status };
      record(opts.idempotencyKey, sketch, payload);
      return wait(payload);
    },

    getDiagnostics: async (acsDeviceId: string, jobId: string): Promise<Job> => {
      const entry = diagnosticJobs.get(jobId);
      // Scoped to the device in the path and to diagnostics jobs, so this endpoint
      // cannot be used to enumerate another tenant's job ids (routers/diagnostics.py).
      if (
        !entry ||
        entry.deviceId !== acsDeviceId ||
        !entry.job.action.startsWith('diagnostics:')
      ) {
        throw problemError(
          buildProblem('job-not-found', {
            detail: `no diagnostics job ${jobId} for device ${acsDeviceId}`,
            instance: `GET /integration/v1/devices/${acsDeviceId}/diagnostics/${jobId}`,
          }),
        );
      }
      // The read-back: the first GET after the task ended is this fixture's stand-in for the
      // verification pass, and it writes the answer through so a second GET re-reads the same fact
      // instead of asking the board again — the same rule `diagnostics.py::poll` follows by refusing
      // to touch a job that is already terminal.
      entry.job = settleProbe(entry);
      return wait<Job>(entry.job);
    },

    /* ---- alarms feed: GET /ops/v1/alarms + POST …/alarms/{id}:ack ---- */

    /**
     * Stored, not derived — exactly like the server: the rows come from
     * `FIXTURE_EVENTS` through the `SEVERITY_BY_TYPE` port and the lifecycle
     * from the fixture's own `acs_alarms` rows, then filter → sort(newest
     * first) → cursor page.
     */
    listAlarms: async (query: ListAlarmsQuery): Promise<AlarmsResponse> => {
      const instance = 'GET /ops/v1/alarms';
      const severity = query.severity ?? '';
      if (severity && !(ALARM_SEVERITIES as readonly string[]).includes(severity)) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `severity must be one of ${ALARM_SEVERITIES.join(', ')}`,
            instance,
          }),
        );
      }
      // The console asks for a page size; the server's own default is 200.
      const limit = query.limit ?? 20;
      if (!Number.isInteger(limit) || limit < 1 || limit > ALARM_SCAN_PAGE_SIZE) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `limit must be between 1 and ${ALARM_SCAN_PAGE_SIZE}`,
            instance,
          }),
        );
      }
      let rows = deriveAlarmRows(lifecyclesOf());
      if (severity) rows = rows.filter((row) => row.severity === severity);
      // `device` is an EXACT acs_device_id match on the server, not a substring.
      if (query.device) rows = rows.filter((row) => row.acs_device_id === query.device);
      if (typeof query.acked === 'boolean') {
        rows = rows.filter((row) => row.acked === query.acked);
      }
      const page = paginate(rows, query.cursor, limit);
      return wait<AlarmsResponse>({ items: page.items, next_cursor: page.next_cursor });
    },

    /**
     * The ack writes one ledger entry and answers `already_acked` on a repeat.
     * `Idempotency-Key` is sent by the caller (console policy) but deliberately
     * NOT replayed here: the contract's dedupe for this POST is the ledger, so
     * replaying a stored 200 would hide the `already_acked` signal the operator
     * needs to see.
     */
    ackAlarm: async (
      eventId: string,
      body: AlarmAckRequest,
      opts: WriteOptions,
    ): Promise<AlarmAckResponse> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = `POST /ops/v1/alarms/${eventId}:ack`;
      forceScenario(instance);
      const event = outboxEventOf(eventId);
      if (!event) {
        throw problemError(
          buildProblem('alarm-not-found', {
            detail: unknownAlarmDetail(eventId),
            instance,
          }),
        );
      }
      if (alarmFromEvent(event) === null) {
        throw problemError(
          buildProblem('alarm-not-found', {
            detail: notAnAlarmDetail(eventId, event.type),
            instance,
          }),
        );
      }
      const note = body.note ?? null;
      if (note !== null && note.length > 500) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: 'note must be at most 500 characters',
            instance,
          }),
        );
      }
      const already = ackLedger.get(eventId);
      if (!already) ackLedger.set(eventId, { note, acked_at: new Date().toISOString() });
      // The ack moves the ROW to `acked` — the server's ack door does, and a
      // fixture whose row stayed `open` after an ack would show the operator a
      // state the server never answers.
      const current = lifecycleLedger.get(eventId);
      lifecycleLedger.set(eventId, {
        state: 'acked',
        acknowledged: true,
        escalated: current?.escalated ?? false,
        // The escalation and the hand-over are the row's OWN facts: an
        // acknowledgement does not undo them, so the answer keeps them.
        escalated_by: current?.escalated_by ?? null,
        assignee: current?.assignee ?? null,
        assigned_by: current?.assigned_by ?? null,
        cleared_by: current?.cleared_by ?? null,
      });
      return wait<AlarmAckResponse>({
        alarm_id: eventId,
        acked: true,
        already_acked: already !== undefined,
        note,
        acked_by: FIXTURE_ACK_CLIENT,
      });
    },

    /* ---- the lifecycle doors (N2): `:clear`, `:escalate`, `:assign` ----
       The fixture answers with the server's SHAPES and the server's rules:
       a closed record moves no more (each door with its own code), a replay
       writes nothing and says so, and the actor is named. The actor here is
       the fixture's console session — the real façade names the human who
       signed in (and refuses a machine assertion outright). */

    clearAlarm: async (
      eventId: string,
      body: AlarmClearRequest,
      opts: WriteOptions,
    ): Promise<AlarmClearResponse> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = `POST /ops/v1/alarms/${eventId}:clear`;
      forceScenario(instance);
      const event = outboxEventOf(eventId);
      if (!event) {
        throw problemError(
          buildProblem('alarm-not-found', {
            detail: unknownAlarmDetail(eventId),
            instance,
          }),
        );
      }
      if (alarmFromEvent(event) === null) {
        throw problemError(
          buildProblem('alarm-not-found', {
            detail: notAnAlarmDetail(eventId, event.type),
            instance,
          }),
        );
      }
      const note = body.note ?? null;
      if (note !== null && note.length > 500) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: 'note must be at most 500 characters',
            instance,
          }),
        );
      }
      const current = lifecycleLedger.get(eventId);
      const state = current?.state ?? (ackLedger.has(eventId) ? 'acked' : 'open');
      if (state === 'cleared') {
        // The same person's own closure is a replay (nothing is written and
        // the answer says so); somebody else's closure is a refusal — the
        // two are different answers, exactly as on the server.
        if (current?.cleared_by === FIXTURE_ACK_CLIENT) {
          return wait<AlarmClearResponse>({
            alarm_id: eventId,
            state: 'cleared',
            cleared_by: FIXTURE_ACK_CLIENT,
            already_cleared: true,
            note,
          });
        }
        throw problemError(
          buildProblem('alarm-already-cleared', {
            detail: `alarm ${eventId} was already closed; its closure stands and is not repeated`,
            instance,
          }),
        );
      }
      lifecycleLedger.set(eventId, {
        state: 'cleared',
        acknowledged: current?.acknowledged ?? false,
        escalated: current?.escalated ?? false,
        assignee: current?.assignee ?? null,
        cleared_by: FIXTURE_ACK_CLIENT,
      });
      return wait<AlarmClearResponse>({
        alarm_id: eventId,
        state: 'cleared',
        cleared_by: FIXTURE_ACK_CLIENT,
        already_cleared: false,
        note,
      });
    },

    escalateAlarm: async (
      eventId: string,
      body: AlarmEscalateRequest,
      opts: WriteOptions,
    ): Promise<AlarmEscalateResponse> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = `POST /ops/v1/alarms/${eventId}:escalate`;
      forceScenario(instance);
      const event = outboxEventOf(eventId);
      if (!event) {
        throw problemError(
          buildProblem('alarm-not-found', {
            detail: unknownAlarmDetail(eventId),
            instance,
          }),
        );
      }
      if (alarmFromEvent(event) === null) {
        throw problemError(
          buildProblem('alarm-not-found', {
            detail: notAnAlarmDetail(eventId, event.type),
            instance,
          }),
        );
      }
      const note = body.note ?? null;
      if (note !== null && note.length > 500) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: 'note must be at most 500 characters',
            instance,
          }),
        );
      }
      const current = lifecycleLedger.get(eventId);
      const state = current?.state ?? (ackLedger.has(eventId) ? 'acked' : 'open');
      if (state === 'cleared') {
        throw problemError(
          buildProblem('alarm-not-escalatable', {
            detail: `alarm ${eventId} is closed; a closed record cannot be escalated`,
            instance,
          }),
        );
      }
      const already = current?.escalated ?? false;
      // The row's state does NOT move (escalation is a flag) and the state
      // stays whatever the row already was — never a guessed 'acked'.
      lifecycleLedger.set(eventId, {
        state: current?.state ?? 'open',
        acknowledged: current?.acknowledged ?? false,
        escalated: true,
        escalated_by: FIXTURE_ACK_CLIENT,
        assignee: current?.assignee ?? null,
        cleared_by: current?.cleared_by ?? null,
      });
      return wait<AlarmEscalateResponse>({
        alarm_id: eventId,
        escalated: true,
        already_escalated: already,
        // A replay names the person who escalated it FIRST, not the caller:
        // the ledger recorded that one, and the answer must not claim an
        // escalation the ledger does not carry.
        escalated_by: already ? (current?.escalated_by ?? FIXTURE_ACK_CLIENT) : FIXTURE_ACK_CLIENT,
        note,
      });
    },

    assignAlarm: async (
      eventId: string,
      body: AlarmAssignRequest,
      opts: WriteOptions,
    ): Promise<AlarmAssignResponse> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = `POST /ops/v1/alarms/${eventId}:assign`;
      forceScenario(instance);
      const event = outboxEventOf(eventId);
      if (!event) {
        throw problemError(
          buildProblem('alarm-not-found', {
            detail: unknownAlarmDetail(eventId),
            instance,
          }),
        );
      }
      if (alarmFromEvent(event) === null) {
        throw problemError(
          buildProblem('alarm-not-found', {
            detail: notAnAlarmDetail(eventId, event.type),
            instance,
          }),
        );
      }
      const note = body.note ?? null;
      if (note !== null && note.length > 500) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: 'note must be at most 500 characters',
            instance,
          }),
        );
      }
      const assignee = body.assignee.trim();
      if (assignee.length === 0 || assignee.length > 120) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: 'assignee must be between 1 and 120 characters',
            instance,
          }),
        );
      }
      const current = lifecycleLedger.get(eventId);
      const state = current?.state ?? (ackLedger.has(eventId) ? 'acked' : 'open');
      if (state === 'cleared') {
        throw problemError(
          buildProblem('alarm-not-assignable', {
            detail: `alarm ${eventId} is closed; a closed record cannot be assigned`,
            instance,
          }),
        );
      }
      const previous = current?.assignee ?? null;
      const already = previous === assignee;
      lifecycleLedger.set(eventId, {
        state: current?.state ?? 'open',
        acknowledged: current?.acknowledged ?? false,
        escalated: current?.escalated ?? false,
        assignee,
        assigned_by: already ? (current?.assigned_by ?? FIXTURE_ACK_CLIENT) : FIXTURE_ACK_CLIENT,
        cleared_by: current?.cleared_by ?? null,
      });
      return wait<AlarmAssignResponse>({
        alarm_id: eventId,
        assignee,
        previous_assignee: previous,
        // A replay names the hand-over the ledger already recorded, not the
        // caller — the answer must not claim an action nothing wrote.
        assigned_by: already ? (current?.assigned_by ?? FIXTURE_ACK_CLIENT) : FIXTURE_ACK_CLIENT,
        note,
      });
    },

    /* ---- maintenance windows (ALM-05) — the doors, not the raise ----
       The fixture mirrors the list/create/cancel rules so the dev console
       and the real façade tell one story about planning a quiet night. The
       alarm feed here does NOT consult these rows: suppression lives at the
       raise on the server, and this fixture has no live raise (plan D4). */

    listMaintenance: async (query: ListMaintenanceQuery): Promise<MaintenanceListResponse> => {
      const instance = 'GET /ops/v1/maintenance';
      const state = query.state ?? '';
      if (state && !(MAINTENANCE_STATES as readonly string[]).includes(state)) {
        throw problemError(buildProblem('reference-invalid', { detail: STATE_DETAIL, instance }));
      }
      const offset = query.offset ?? 0;
      const limit = query.limit ?? 50;
      if (!Number.isInteger(limit) || limit < 1 || limit > MAINTENANCE_PAGE_SIZE || offset < 0) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `limit must be between 1 and ${MAINTENANCE_PAGE_SIZE}`,
            instance,
          }),
        );
      }
      // The door is OFFSET-paged (not a cursor): a bounded slice and the
      // total over the filter, newest first.
      let rows = sortWindowRows(maintenanceRows);
      if (state) rows = rows.filter((row) => row.state === state);
      return wait<MaintenanceListResponse>({
        items: rows.slice(offset, offset + limit),
        total: rows.length,
      });
    },

    createMaintenance: async (
      body: MaintenanceCreateRequest,
      opts: WriteOptions,
    ): Promise<MaintenanceCreateResponse> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = 'POST /ops/v1/maintenance';
      forceScenario(instance);
      const verdict = validateWindowCreate(body, (target) => findDevice(target) !== undefined);
      if ('why' in verdict) {
        throw problemError(
          buildProblem(verdict.code, {
            detail: verdict.why,
            instance,
          }),
        );
      }
      maintenanceSeq += 1;
      const row: MaintenanceWindow = {
        window_id: `mwin:fixture-${maintenanceSeq}-${Math.random().toString(36).slice(2, 8)}`,
        scope: body.scope,
        target: body.target,
        starts_at: verdict.startsAt,
        ends_at: verdict.endsAt,
        note: body.note ?? null,
        state: 'active',
        created_at: new Date().toISOString(),
        created_by: MAINTENANCE_WINDOW_PLANNER,
        cancelled_at: null,
        cancelled_by: null,
        suppressed: 0,
      };
      maintenanceRows.push(row);
      return wait<MaintenanceCreateResponse>({ item: row });
    },

    cancelMaintenance: async (
      windowId: string,
      opts: WriteOptions,
    ): Promise<MaintenanceCancelResponse> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = `POST /ops/v1/maintenance/${windowId}:cancel`;
      forceScenario(instance);
      const row = maintenanceRows.find((entry) => entry.window_id === windowId);
      if (!row) {
        throw problemError(
          buildProblem('maintenance-not-found', {
            detail: unknownWindowDetail(windowId),
            instance,
          }),
        );
      }
      const already = row.state === 'cancelled';
      if (!already) {
        // CAS, as the server keeps it: the FIRST canceller's name is the
        // row's fact and a replay never overwrites it.
        row.state = 'cancelled';
        row.cancelled_at = new Date().toISOString();
        row.cancelled_by = MAINTENANCE_WINDOW_PLANNER;
      }
      return wait<MaintenanceCancelResponse>({
        window_id: row.window_id,
        state: row.state,
        already_cancelled: already,
        cancelled_by: row.cancelled_by,
        cancelled_at: row.cancelled_at,
      });
    },

    /* ---- incidents (N5) — `/ops/v1/incidents` ----
       The correlate door is a LIVE reduction of this session's alarm feed
       (one source of truth), the incident rows are the session clone above,
       and the refusal sentences are `fixtures/incidents.ts`'s ports of the
       server's. Create and note assert a key (their replay would be a second
       row / a second entry); attach and close accept one like every write. */

    correlateIncidents: async (
      query: ListIncidentCorrelateQuery & { offset?: number },
    ): Promise<IncidentCorrelateResponse> => {
      const instance = 'GET /ops/v1/incidents:correlate';
      const minDevices = query.min_devices;
      if (
        minDevices !== undefined &&
        (!Number.isInteger(minDevices) ||
          minDevices < CORRELATE_MIN_DEVICES_FLOOR ||
          minDevices > 50)
      ) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `min_devices must be between ${CORRELATE_MIN_DEVICES_FLOOR} and 50`,
            instance,
          }),
        );
      }
      const oltOf = new Map<string, string | null>();
      FIXTURE_DEVICES.forEach((device, index) => {
        const link = associationOf(device, index);
        oltOf.set(device.acs_device_id, link ? link.olt_id : null);
      });
      const answer = correlateFixtureAlarms({
        rows: deriveAlarmRows(lifecyclesOf()),
        oltOf,
        minDevices,
        asOf: new Date().toISOString(),
      });
      return wait<IncidentCorrelateResponse>(answer);
    },

    listIncidents: async (query: ListIncidentsQuery): Promise<IncidentListResponse> => {
      const instance = 'GET /ops/v1/incidents';
      const state = query.state ?? '';
      if (state && !(INCIDENT_STATES as readonly string[]).includes(state)) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `state must be one of ${INCIDENT_STATES.join(', ')}`,
            instance,
          }),
        );
      }
      const offset = query.offset ?? 0;
      const limit = query.limit ?? 50;
      if (!Number.isInteger(limit) || limit < 1 || limit > 200 || offset < 0) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: 'limit must be between 1 and 200',
            instance,
          }),
        );
      }
      let rows = [...incidentRows].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      if (state) rows = rows.filter((row) => row.state === state);
      return wait<IncidentListResponse>({
        items: rows.slice(offset, offset + limit),
        total: rows.length,
      });
    },

    getIncident: async (incidentId: string): Promise<Incident> => {
      const instance = `GET /ops/v1/incidents/${incidentId}`;
      forceScenario(instance);
      const row = incidentRows.find((entry) => entry.incident_id === incidentId);
      if (!row) {
        throw problemError(
          buildProblem('incident-not-found', {
            detail: unknownIncidentDetail(incidentId),
            instance,
          }),
        );
      }
      return wait<Incident>(row);
    },

    createIncident: async (body: IncidentCreateRequest, opts: WriteOptions): Promise<Incident> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = 'POST /ops/v1/incidents';
      forceScenario(instance);
      const title = body.title.trim();
      if (!title || title.length > 200) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: 'title must be 1..200 characters',
            instance,
          }),
        );
      }
      const alarmIds = body.alarm_ids ?? [];
      const vetted = vetIncidentAlarmIds(alarmIds, '', instance);
      incidentSeq += 1;
      const now = new Date().toISOString();
      const row: Incident = {
        incident_id: `inc:fixture-${incidentSeq}-${Math.random().toString(36).slice(2, 8)}`,
        title,
        severity: worstFixtureSeverity(vetted.map((entry) => entry.severity as AlarmSeverity)),
        state: 'open',
        olt_id: sharedOltOf(vetted.map((entry) => oltIdOfDevice(entry.acs_device_id))),
        created_at: now,
        created_by: INCIDENT_OPERATOR,
        closed_at: null,
        closed_by: null,
        alarm_ids: vetted.map((entry) => entry.alarm_id).sort(),
        timeline: [incidentTimelineEntry('created', body.note ?? null, now)],
      };
      incidentRows.push(row);
      return wait<Incident>(row);
    },

    noteIncident: async (
      incidentId: string,
      body: IncidentNoteRequest,
      opts: WriteOptions,
    ): Promise<Incident> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = `POST /ops/v1/incidents/${incidentId}:note`;
      forceScenario(instance);
      const row = incidentRows.find((entry) => entry.incident_id === incidentId);
      if (!row) {
        throw problemError(
          buildProblem('incident-not-found', {
            detail: unknownIncidentDetail(incidentId),
            instance,
          }),
        );
      }
      if (row.state === 'closed') {
        throw closedIncident(incidentId, instance);
      }
      const text = body.text.trim();
      if (!text || text.length > 1000) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: 'text must be 1..1000 characters',
            instance,
          }),
        );
      }
      row.timeline.push(incidentTimelineEntry('note', text, new Date().toISOString()));
      return wait<Incident>(row);
    },

    attachIncident: async (
      incidentId: string,
      body: IncidentAttachRequest,
      opts: WriteOptions,
    ): Promise<IncidentAttachResponse> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = `POST /ops/v1/incidents/${incidentId}:attach`;
      forceScenario(instance);
      const row = incidentRows.find((entry) => entry.incident_id === incidentId);
      if (!row) {
        throw problemError(
          buildProblem('incident-not-found', {
            detail: unknownIncidentDetail(incidentId),
            instance,
          }),
        );
      }
      if (row.state === 'closed') {
        throw closedIncident(incidentId, instance);
      }
      const vetted = vetIncidentAlarmIds(body.alarm_ids, incidentId, instance);
      const attached = vetted
        .map((entry) => entry.alarm_id)
        .filter((id) => !row.alarm_ids.includes(id));
      row.alarm_ids = [...row.alarm_ids, ...attached].sort();
      if (attached.length > 0) {
        row.timeline.push(
          incidentTimelineEntry('attach', attached.join(', '), new Date().toISOString()),
        );
      }
      return wait<IncidentAttachResponse>({
        incident: row,
        attached,
        already_attached: body.alarm_ids.filter((id) => !attached.includes(id)).sort(),
      });
    },

    closeIncident: async (
      incidentId: string,
      body: IncidentCloseRequest,
      opts: WriteOptions,
    ): Promise<Incident> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = `POST /ops/v1/incidents/${incidentId}:close`;
      forceScenario(instance);
      const row = incidentRows.find((entry) => entry.incident_id === incidentId);
      if (!row) {
        throw problemError(
          buildProblem('incident-not-found', {
            detail: unknownIncidentDetail(incidentId),
            instance,
          }),
        );
      }
      if (row.state === 'closed') {
        throw closedIncident(incidentId, instance);
      }
      const now = new Date().toISOString();
      row.state = 'closed';
      row.closed_at = now;
      row.closed_by = INCIDENT_OPERATOR;
      row.timeline.push(incidentTimelineEntry('close', body.note ?? null, now));
      return wait<Incident>(row);
    },

    /* ---- notification channels (N6) — `/ops/v1/notification-channels` ----
       The port of the registry doors: kind and URL refused BY NAME, the
       secret write-only (the row answers `has_secret`), every write keyed,
       and `:test` a REAL pending intent — the fixture has no notify worker,
       so the intent stays pending and the feed says so (D4). */

    listNotificationChannels: async (): Promise<NotificationChannelListResponse> => {
      // The store's order: created_at ASC with a channel_id tie-break.
      const items = [...channelRows].sort(
        (a, b) =>
          (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0) ||
          (a.channel_id < b.channel_id ? -1 : 1),
      );
      return wait<NotificationChannelListResponse>({ items, total: items.length });
    },

    createNotificationChannel: async (
      body: NotificationChannelCreateRequest,
      opts: WriteOptions,
    ): Promise<NotificationChannel> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = 'POST /ops/v1/notification-channels';
      forceScenario(instance);
      const kind = body.kind ?? 'webhook';
      if (!(FIXTURE_CHANNEL_KINDS as readonly string[]).includes(kind)) {
        throw problemError(
          buildProblem('channel-kind-unsupported', {
            detail: channelKindUnsupportedDetail(kind),
            instance,
          }),
        );
      }
      if (!fixtureUrlValid(body.url)) {
        throw problemError(
          buildProblem('notification-url-invalid', {
            detail: NOTIFY_URL_INVALID_DETAIL,
            instance,
          }),
        );
      }
      const minSeverity = body.min_severity ?? 'info';
      if (!(FIXTURE_MIN_SEVERITIES as readonly string[]).includes(minSeverity)) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: MIN_SEVERITY_DETAIL,
            instance,
          }),
        );
      }
      const name = body.name.trim();
      if (!name || name.length > 200 || body.url.length > 2000) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: 'name must be 1..200 characters and url 1..2000',
            instance,
          }),
        );
      }
      channelSeq += 1;
      const now = fixtureNotifyClock();
      const row: NotificationChannel = {
        channel_id: `ch:fixture-${channelSeq}-${Math.random().toString(36).slice(2, 8)}`,
        kind: 'webhook',
        name,
        url: body.url,
        min_severity: minSeverity,
        enabled: body.enabled ?? true,
        // The secret never leaves the store — `has_secret` says it exists.
        has_secret: Boolean(body.secret),
        created_by: NOTIFY_OPERATOR,
        created_at: now,
        updated_at: now,
      };
      channelRows.push(row);
      return wait<NotificationChannel>(row);
    },

    updateNotificationChannel: async (
      channelId: string,
      body: NotificationChannelUpdateRequest,
      opts: WriteOptions,
    ): Promise<NotificationChannel> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = `PUT /ops/v1/notification-channels/${channelId}`;
      forceScenario(instance);
      const row = channelRows.find((entry) => entry.channel_id === channelId);
      if (!row) {
        throw problemError(
          buildProblem('notification-channel-not-found', {
            detail: unknownChannelDetail(channelId),
            instance,
          }),
        );
      }
      // The door filters the None keys first, then re-vets the url/min_severity
      // PAIRS with the stored row (`routers/notifications.py::update_channel`).
      const changes = Object.fromEntries(
        Object.entries(body).filter(([, value]) => value !== undefined && value !== null),
      ) as Partial<NotificationChannelUpdateRequest>;
      if ('url' in changes || 'min_severity' in changes) {
        if (!fixtureUrlValid(String(changes.url ?? row.url))) {
          throw problemError(
            buildProblem('notification-url-invalid', {
              detail: NOTIFY_URL_INVALID_DETAIL,
              instance,
            }),
          );
        }
        const severity = String(changes.min_severity ?? row.min_severity);
        if (!(FIXTURE_MIN_SEVERITIES as readonly string[]).includes(severity)) {
          throw problemError(
            buildProblem('reference-invalid', {
              detail: MIN_SEVERITY_DETAIL,
              instance,
            }),
          );
        }
      }
      if ('name' in changes) {
        const name = String(changes.name).trim();
        if (!name || name.length > 200) {
          throw problemError(
            buildProblem('reference-invalid', {
              detail: 'name must be 1..200 characters',
              instance,
            }),
          );
        }
        row.name = name;
      }
      if ('url' in changes) row.url = String(changes.url);
      if ('min_severity' in changes) {
        row.min_severity = changes.min_severity as NotificationChannel['min_severity'];
      }
      if ('enabled' in changes) row.enabled = Boolean(changes.enabled);
      if ('secret' in changes) row.has_secret = Boolean(changes.secret);
      row.updated_at = fixtureNotifyClock();
      return wait<NotificationChannel>(row);
    },

    deleteNotificationChannel: async (
      channelId: string,
      opts: WriteOptions,
    ): Promise<NotificationChannelDeletedResponse> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = `DELETE /ops/v1/notification-channels/${channelId}`;
      forceScenario(instance);
      const index = channelRows.findIndex((entry) => entry.channel_id === channelId);
      if (index === -1) {
        throw problemError(
          buildProblem('notification-channel-not-found', {
            detail: unknownChannelDetail(channelId),
            instance,
          }),
        );
      }
      channelRows.splice(index, 1);
      return wait<NotificationChannelDeletedResponse>({
        deleted: true,
        channel_id: channelId,
      });
    },

    testNotificationChannel: async (
      channelId: string,
      opts: WriteOptions,
    ): Promise<NotificationDelivery> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = `POST /ops/v1/notification-channels/${channelId}:test`;
      forceScenario(instance);
      const row = channelRows.find((entry) => entry.channel_id === channelId);
      if (!row) {
        throw problemError(
          buildProblem('notification-channel-not-found', {
            detail: unknownChannelDetail(channelId),
            instance,
          }),
        );
      }
      const now = fixtureNotifyClock();
      const intent: NotificationDelivery = {
        delivery_id: `ntf:fixture-${(deliverySeq += 1)}-${Math.random().toString(36).slice(2, 8)}`,
        channel_id: channelId,
        // The channel_name is snapshotted AT BIRTH — the worker keeps
        // delivering against the id even after a rename.
        channel_name: row.name,
        kind: 'test',
        alarm_id: null,
        alarm_type: '',
        severity: '',
        acs_device_id: '',
        occurred_at: now,
        status: 'pending',
        attempts: 0,
        last_error: null,
        next_attempt_at: now,
        created_at: now,
        finished_at: null,
      };
      deliveryRows.push(intent);
      return wait<NotificationDelivery>(intent);
    },

    listNotificationDeliveries: async (
      query: ListNotificationDeliveriesQuery,
    ): Promise<NotificationDeliveryListResponse> => {
      const instance = 'GET /ops/v1/notification-deliveries';
      const limit = query.limit ?? 100;
      if (!Number.isInteger(limit) || limit < 1 || limit > DELIVERY_FEED_LIMIT) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `limit must be between 1 and ${DELIVERY_FEED_LIMIT}`,
            instance,
          }),
        );
      }
      // The store's order: created_at DESC with a delivery_id DESC tie-break.
      let rows = [...deliveryRows].sort(
        (a, b) =>
          (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0) ||
          (a.delivery_id < b.delivery_id ? 1 : -1),
      );
      if (query.channel_id) {
        rows = rows.filter((row) => row.channel_id === query.channel_id);
      }
      return wait<NotificationDeliveryListResponse>({
        items: rows.slice(0, limit),
        total: rows.length,
      });
    },

    /* ---- OLT views: GET /ops/v1/olts + …/onus + …/onus:authorize ---- */

    /* ---- audit ledger: GET /ops/v1/audit (R7, U-16-d) ---- */

    /**
     * The fixture's copy of the ledger, with the door's own behaviour: the two filters are EXACT
     * matches on the stored top-level keys and compose by AND, an out-of-range `limit` is
     * `reference-invalid`, a filter that matches nothing is an empty page with the note (never a 404),
     * and `total` counts the filter over the whole ledger rather than the page.
     *
     * The rows are `fixtures/audit.ts`, whose key sets are checked against a measurement of the real
     * door by `test/auditShapes.test.ts` — so this provider answers with the shapes the server
     * produces, including the historical (ب) rows and the actor-less (د) session rows.
     */
    listAudit: async (query: ListAuditQuery): Promise<AuditListResponse> => {
      const instance = 'GET /ops/v1/audit';
      const limit = query.limit ?? AUDIT_DEFAULT_LIMIT;
      if (!Number.isInteger(limit) || limit < 1 || limit > AUDIT_PAGE_LIMIT) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `limit must be between 1 and ${AUDIT_PAGE_LIMIT}`,
            instance,
          }),
        );
      }
      // This session's own rows first (the matrix writes, the subscriber searches and
      // the subscriber's lifecycle writes — all of them the newest entries), then the
      // seeded ledger.
      let rows = subscriberSearchAuditRows.concat(
        subscriberLifecycleAuditRows,
        matrixAuditRows,
        fixtureAuditRows(),
      );
      if (query.kind) rows = rows.filter((row) => row.kind === query.kind);
      if (query.device_id) rows = rows.filter((row) => row.device_id === query.device_id);
      const total = rows.length;
      const page = paginate(rows, query.cursor, limit);
      return wait<AuditListResponse>({
        entries: page.items,
        total,
        next_cursor: page.next_cursor,
        note: FIXTURE_AUDIT_NOTE,
      });
    },

    /* ---- signing keys: GET /ops/v1/keys (door U-08-b, page U-16-e1) ---- */

    /**
     * The fixture's copy of the published key set.
     *
     * No filters and no pagination are honoured here because the door declares neither: it answers the
     * whole published JWKS every time, and `keys.ts` holds the rows whose key sets and null-branches
     * `test/keysShapes.test.ts` compares against a measurement of the real door. The capability check
     * is NOT here on purpose — `AccessGate` mirrors `permissions/effective` for that, and a fixture that
     * answered 403 itself would make the denied-state tests prove nothing about the mirror.
     */
    listKeys: async (): Promise<SigningKeyListResponse> =>
      wait<SigningKeyListResponse>({ keys: fixtureKeyRows() }),

    /* ---- operator profile: the six doors of `routers/profile.py` (U-06, page U-16-e2) ---- */

    /**
     * The export read, through the ported `_active` pairing: a null version is
     * `NOTHING_ACTIVATED_NOTE`, a version with no row behind it is the number itself under
     * `MISSING_ACTIVE_ROW_NOTE` — never a substitute document, never an empty profile.
     */
    getOperatorProfile: async (): Promise<ProfileReadResponse> => {
      const [version, document] = fixtureActivePair(profileState);
      if (version === null) {
        return wait<ProfileReadResponse>({
          active_version: null,
          document: null,
          note: NOTHING_ACTIVATED_NOTE,
        });
      }
      return wait<ProfileReadResponse>({
        active_version: version,
        document: document === null ? null : { ...document },
        note: document === null ? MISSING_ACTIVE_ROW_NOTE : PROFILE_INTENT_NOTE,
      });
    },

    /**
     * The judge door: 200 with the WHOLE report, never a 422 — §۲ step ۲ asks for every complaint
     * fixable in one pass, and no `Idempotency-Key` is taken here (the door has no effect, and a
     * stored replay would glue the first verdict to a document the operator edited in between).
     */
    validateOperatorProfile: async (document: ProfileDocument): Promise<ProfileValidationReport> =>
      wait(fixtureValidationReport(profileState, document)),

    /**
     * The activation door: judge → replay check → compare-and-swap → one appended version. The page
     * passes the key through `assertWriteKey`; a reused key with a different request answers 409
     * `idempotency-conflict` the same way every other fixture write does.
     */
    activateOperatorProfile: async (body, opts) => {
      const key = assertWriteKey(opts.idempotencyKey);
      if (
        typeof body?.expected_version !== 'number' ||
        !Number.isInteger(body.expected_version) ||
        body.expected_version < 0
      ) {
        throw referenceInvalid(
          'expected_version is required and must be an integer ≥ 0 — it is never read as 0 when absent',
        );
      }
      const document = judgeCandidate(body.document);
      const sketch = {
        endpoint: 'operator-profile:activate',
        expected_version: body.expected_version,
        body: document,
      };
      const replayed = replayOrThrow<ProfileActivationAccepted>(key, sketch);
      if (replayed !== undefined) return wait(replayed);
      const payload = activateProfile(document, body.expected_version, null);
      record(key, sketch, payload);
      return wait(payload);
    },

    /**
     * The bounded window: newest first, `VERSION_PAGE` rows per answer, `has_more` says older rows
     * exist, no row carries a document, and `VERSIONS_NOTE` carries the caveats (an empty
     * `changed_keys` on a trimmed history means «not computable»).
     */
    listOperatorProfileVersions: async (): Promise<ProfileVersionListResponse> => {
      const { rows, hasMore } = fixtureVersionRows(profileState);
      return wait<ProfileVersionListResponse>({
        count: rows.length,
        has_more: hasMore,
        versions: rows,
        note: VERSIONS_NOTE,
      });
    },

    /**
     * Forward rollback, with the door's own refusal order: a version the window no longer carries is
     * 404 `profile-version-not-found` («never minted» and «trimmed» are one answer from this door),
     * a stored row is re-judged by today's rules and refused with 409 `profile-release-blocked` (409
     * even when the complaint is shape — the bytes are the store's own and the caller cannot edit
     * them), and a landed row mints a NEW version with `rollback_of`. `confirm` must be literally
     * `true`: absent or false is a 422, not a default.
     */
    rollbackOperatorProfile: async (body, opts) => {
      const key = assertWriteKey(opts.idempotencyKey);
      if (body?.confirm !== true) {
        throw referenceInvalid(
          'a profile rollback requires {"confirm": true}; absent and false are refusals, not defaults',
        );
      }
      if (typeof body?.to_version !== 'number' || body.to_version < 1) {
        throw referenceInvalid(
          'to_version must be an integer ≥ 1 — there is no version 0 to roll back to',
        );
      }
      if (
        typeof body?.expected_version !== 'number' ||
        !Number.isInteger(body.expected_version) ||
        body.expected_version < 0
      ) {
        throw referenceInvalid('expected_version is required and must be an integer ≥ 0');
      }
      const target = profileState.versions.find((row) => row.version === body.to_version);
      if (target === undefined) {
        throw problemError(
          buildProblem('profile-version-not-found', {
            detail:
              `version ${body.to_version} is not a row this store's history carries, so it cannot ` +
              `be rolled forward; the window keeps the newest ${profileState.versions.length} ` +
              'activation(s) of a bounded history and nothing was stored',
            instance: 'POST /ops/v1/operator-profile:rollback',
          }),
        );
      }
      const document = (() => {
        const [blessed, found] = validateFixtureProfile(target.document);
        if (blessed !== null) return blessed;
        const err = problemError(
          buildProblem('profile-release-blocked', {
            detail:
              `version ${target.version} no longer passes this build's profile validation ` +
              `(${found.length} complaint(s)), so it may not be rolled forward; re-declare the ` +
              'profile through `PUT /ops/v1/operator-profile` instead',
            instance: 'POST /ops/v1/operator-profile:rollback',
          }),
        );
        (err.problem as { problems?: unknown }).problems = found;
        throw err;
      })();
      const sketch = {
        endpoint: 'operator-profile:rollback',
        to_version: body.to_version,
        expected_version: body.expected_version,
        body: document,
      };
      const replayed = replayOrThrow<ProfileActivationAccepted>(key, sketch);
      if (replayed !== undefined) return wait(replayed);
      const payload = activateProfile(document, body.expected_version, body.to_version);
      record(key, sketch, payload);
      return wait(payload);
    },

    /**
     * The readiness report — it reports and decides nothing. The fixture derives it from the SAME
     * stored family the read doors use (judged, never asserted), every fleet row keeps
     * `write_authorized: false` by design, and the two document-less states say which one this is.
     * The console must treat this answer as a report: the page never reads a permission out of it.
     */
    getOperatorProfileReadiness: async (): Promise<ProfileReadinessResponse> => {
      const [version, document] = fixtureActivePair(profileState);
      return wait(fixtureReadiness(document, version));
    },

    listOlts: async (): Promise<OltListResponse> => {
      const items: OltInventoryItem[] = FIXTURE_OLT_INVENTORY.map((spec) =>
        inventoryRowOf(spec, onuTables.get(spec.olt_id)),
      );
      return wait<OltListResponse>({ items, total: items.length });
    },

    listOltOnus: async (oltId: string, query: ListOltOnusQuery): Promise<OnuTableResponse> => {
      const rows = oltTableOrThrow(oltId, `GET /ops/v1/olts/${oltId}/onus`).filter((row) =>
        query.status ? row.status === query.status : true,
      );
      return wait<OnuTableResponse>({
        olt_id: oltId,
        items: rows.map((row) => ({ ...row })),
        total: rows.length,
      });
    },

    /**
     * V7 (G-PRC-08) — `GET /ops/v1/olts/{olt_id}/pon-capacity`. The mirror reduces the
     * fixture's OWN ONU tables against the DECLARED capacity from the settings mirror
     * (`ponCapacityOf`), so a changed declaration changes the measurement here exactly
     * as it does server-side. An unknown OLT is 404 like every OLT door, and the OLT
     * whose table the fixture cannot read follows `listOltOnus`'s own convention
     * (`adapter-unreachable`) — the fixture has no «nobody read» state to answer
     * `ports: null` FROM, so the null discipline stays server-side tested.
     */
    getOltPonCapacity: async (oltId: string): Promise<PonCapacityResponse> => {
      const instance = `GET /ops/v1/olts/${oltId}/pon-capacity`;
      const table = onuTables.get(oltId);
      if (!table) {
        throw problemError(
          buildProblem('olt-not-found', { detail: `unknown olt ${oltId}`, instance }),
        );
      }
      const note = FIXTURE_OLT_TABLE_NOTES[oltId];
      if (note) {
        throw problemError(buildProblem('adapter-unreachable', { detail: note, instance }));
      }
      return wait(ponCapacityOf(oltId, settingsState.stored));
    },

    /**
     * DIRECT manual authorize — the repair/retry path, no activation job behind
     * it. It reuses the server's idempotency rule (replay returns the stored
     * body, the same key with a different body is a 409) and converges the ONU
     * row the same way the adapter does: re-applying changes nothing visible.
     */
    authorizeOnu: async (
      oltId: string,
      body: OnuAuthorizeRequest,
      opts: WriteOptions,
    ): Promise<OnuAuthorizeResponse> => {
      assertWriteKey(opts.idempotencyKey);
      const instance = `POST /ops/v1/olts/${oltId}/onus:authorize`;
      validateOnuAuthorize(body, instance);
      const sketch = { endpoint: 'olts:authorize', olt_id: oltId, body };

      const replayed = replayOrThrow<OnuAuthorizeResponse>(opts.idempotencyKey, sketch);
      if (replayed !== undefined) return wait(replayed);

      const table = oltTableOrThrow(oltId, instance);
      forceScenario(instance);

      const ponPort = body.pon_port.trim();
      const existing = table.find((row) => row.sn === body.sn);
      let row = existing;
      if (existing) {
        // EXACTLY the four fields the adapter updates (`adapter.py:296-297`,
        // `drivers/base.py:181-183`): the real op passes only
        // `(olt_id, sn, pon_port, body.profile)` to it (`olt_ops.py:233-234`), so
        // `line_profile`/`service_profile` are never written to the row here —
        // they exist on the request solely so the emitted `onu.authorized` event
        // can carry them (`olt_ops.py:244-245`). Writing them would show the dev
        // UI profiles as applied that the OLT never received.
        Object.assign(existing, {
          pon_port: ponPort,
          profile: body.profile ?? null,
          status: 'registered',
          auth_state: 'authorized',
        });
      } else {
        // A brand-new row is the driver's own fresh-row shape (`adapter.py:289-293`,
        // `drivers/base.py:172-177`): `line_profile`/`service_profile` are null and
        // there is no tr069 push, whatever the request asked for.
        row = {
          sn: body.sn,
          olt_id: oltId,
          pon_port: ponPort,
          status: 'registered',
          auth_state: 'authorized',
          profile: body.profile ?? null,
          line_profile: null,
          service_profile: null,
          vlan: null,
          tr069: null,
        };
        table.push(row);
        table.sort((a, b) => a.sn.localeCompare(b.sn));
      }

      const payload: OnuAuthorizeResponse = {
        olt_id: oltId,
        sn: body.sn,
        pon_port: ponPort,
        // `_admin_state()` of routers/olt_ops.py:142-155 applied to the row the
        // adapter returned: the canned row spells the field `auth_state`
        // (`drivers/base.py:175,183`), so the real 200 body is `authorized`.
        admin_state: adminStateOfResult(row),
        authorized: true,
      };
      record(opts.idempotencyKey, sketch, payload);
      return wait(payload);
    },

    /* ---- optical: GET /ops/v1/devices/{id}/optical ---- */

    /**
     * The fail-closed order of `read_optical`: device exists → association is on
     * record → the ONU row is the existence gate → one sample is proxied. A
     * missing association is **409 `stale-snapshot`** ("no OLT association
     * known"), which is a state of the system the page must show, not an error
     * it must hide.
     */
    getDeviceOptical: async (
      acsDeviceId: string,
      query: { window?: OpticalWindow },
    ): Promise<OpticalRead> => {
      const instance = `GET /ops/v1/devices/${acsDeviceId}/optical`;
      const window = query.window;
      if (window && !(OPTICAL_WINDOWS as readonly string[]).includes(window)) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `window must be one of ${OPTICAL_WINDOWS.join(', ')}`,
            instance,
          }),
        );
      }
      const { device, index } = deviceOrThrow(acsDeviceId);
      const link = associationOf(device, index);
      if (!link) {
        throw problemError(
          buildProblem('stale-snapshot', {
            detail: `no OLT association known for ${acsDeviceId}`,
            instance,
          }),
        );
      }
      const table = oltTableOrThrow(link.olt_id, instance);
      const row = table.find((entry) => entry.sn === device.serial);
      if (!row) {
        // The adapter's `onu-not-found` is mapped to `device-not-found` by
        // `_ADAPTER_PROBLEM_MAP` — an SN the OLT never saw is not a sample.
        throw problemError(
          buildProblem('device-not-found', {
            detail: `no ONU ${device.serial} on olt ${link.olt_id}`,
            instance,
          }),
        );
      }
      return wait(opticalOf(device, link, index));
    },

    /**
     * `GET …/optical/history` (V3, G-TLM-02). The same answer the façade gives from stored
     * readings, one per bucket of the window. TWO deliberate differences from the live read
     * above, and both are the server's rules: there is no ONU-row existence gate and **no 409**
     * for a missing association — a reading already taken is not a question about the present,
     * and refusing the past because the plant moved on would be the panel lying about a gap.
     * An unknown device is still 404, and a device with no reading is an EMPTY series, not a
     * line at zero.
     */
    getDeviceOpticalHistory: async (
      acsDeviceId: string,
      query: { window?: OpticalWindow },
    ): Promise<OpticalHistory> => {
      const instance = `GET /ops/v1/devices/${acsDeviceId}/optical/history`;
      const window = (query.window ?? '24h') as OpticalWindow;
      if (!(OPTICAL_WINDOWS as readonly string[]).includes(window)) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `window must be one of ${OPTICAL_WINDOWS.join(', ')}`,
            instance,
          }),
        );
      }
      const { device, index } = deviceOrThrow(acsDeviceId);
      return wait(opticalHistoryOf(device, associationOf(device, index), window));
    },

    getOpticalWorst: async (query: {
      window?: OpticalWindow;
      limit?: number;
    }): Promise<OpticalWorst> => {
      const instance = 'GET /ops/v1/optical/worst';
      const window = (query.window ?? '24h') as OpticalWindow;
      if (!(OPTICAL_WINDOWS as readonly string[]).includes(window)) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `window must be one of ${OPTICAL_WINDOWS.join(', ')}`,
            instance,
          }),
        );
      }
      const limit = query.limit ?? WORST_LIMIT_DEFAULT;
      if (!Number.isInteger(limit) || limit < 1 || limit > WORST_LIMIT_MAX) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `limit must be an integer between 1 and ${WORST_LIMIT_MAX}`,
            instance,
          }),
        );
      }
      // The same mirror the server holds: `bin_dbm` is the published width, `devices` the fleet the
      // roll-up counted — an unmeasured fleet is an empty answer, never a 409 and never a zero bar.
      return wait<OpticalWorst>({
        ...opticalWorstOf(window, limit),
        bin_dbm: RX_BIN_DBM,
      });
    },

    getOpticalDegrading: async (query: {
      window?: OpticalDegradingWindow;
      limit?: number;
    }): Promise<OpticalDegrading> => {
      const instance = 'GET /ops/v1/optical/degrading';
      // The narrower enum is the door's rule, not a convenience: `1h`/`24h` are refused here and the
      // fixture has to refuse them the same way, or the console would demo an answer the server 422s.
      const window = (query.window ?? DEGRADING_WINDOWS[0]) as OpticalDegradingWindow;
      if (!(DEGRADING_WINDOWS as readonly string[]).includes(window)) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `window must be one of ${DEGRADING_WINDOWS.join(', ')}`,
            instance,
          }),
        );
      }
      const limit = query.limit ?? WORST_LIMIT_DEFAULT;
      if (!Number.isInteger(limit) || limit < 1 || limit > WORST_LIMIT_MAX) {
        throw problemError(
          buildProblem('reference-invalid', {
            detail: `limit must be an integer between 1 and ${WORST_LIMIT_MAX}`,
            instance,
          }),
        );
      }
      // The same stored settings document the settings page reads, so a countdown measured here aims
      // at the line the operator actually set. The list is empty for this fleet by construction —
      // the fixture's series is flat — and that is the answer, not a gap in the mirror (D7).
      return wait<OpticalDegrading>(
        opticalDegradingOf(window, limit, Date.now(), settingsState.stored),
      );
    },

    getDeviceCapabilities: async (acsDeviceId: string): Promise<DeviceCapabilities> => {
      const { device } = deviceOrThrow(acsDeviceId);
      return wait<DeviceCapabilities>({
        acs_device_id: device.acs_device_id,
        capabilities: [...device.capabilities],
        tr143: hasCapability(device, TR143),
      });
    },

    /* ---- firmware registry + campaigns — derived, never hand-written ----
       The views come from `fixtures/campaignEngine.ts`, the port of `campaigns.py::plan`,
       `wave_slice` and the router's `_campaign_item`/`_campaign_detail`/`_progress`, so a
       campaign row on this page carries the same numbers the façade would derive from the
       same record. An unknown `status` filter is not an error: `store.list_campaigns`
       treats it as a match clause and answers nothing (firmware.py:747-757). */

    listFirmware: async (query: ListFirmwareQuery): Promise<FirmwareListResponse> => {
      const rows = firmwareRows.filter(
        (row) =>
          (!query.brand || row.brand === query.brand) &&
          (!query.model || row.model === query.model),
      );
      const page = paginate(rows, query.cursor, query.limit ?? 20);
      return wait<FirmwareListResponse>({ items: [...page.items], next_cursor: page.next_cursor });
    },

    listCampaigns: async (query: ListCampaignsQuery): Promise<CampaignListResponse> => {
      const rows = campaignRowsNewestFirst([...campaignRecords.values()])
        .filter((entry) => !query.status || entry.doc.status === query.status)
        .map((entry) => campaignItem(campaignJobReader(entry), entry.doc));
      const page = paginate(rows, query.cursor, query.limit ?? 20);
      return wait<CampaignListResponse>({ items: page.items, next_cursor: page.next_cursor });
    },

    getCampaign: async (campaignId: string): Promise<Campaign> => {
      const entry = campaignOrThrow(campaignId);
      return wait<Campaign>(
        campaignDetail(campaignJobReader(entry), entry.doc, options.clock?.() ?? new Date()),
      );
    },

    /* ---- the write doors ----
       `Idempotency-Key` semantics are the server's: a replayed key with the same request
       returns the stored answer and does the work once, the same key with a different
       request is 409 `idempotency-conflict`, and a refusal records nothing so the key stays
       reusable for the retry the operator was told to make. */

    uploadFirmwareImage: async (
      image: FirmwareImageUpload,
      opts: WriteOptions,
    ): Promise<FirmwareUploadAccepted> => {
      const key = assertWriteKey(opts.idempotencyKey);
      const declaration = {
        endpoint: 'firmware:upload:raw',
        brand: image.brand,
        model: image.model,
        version: image.version,
        checksum_sha256: image.checksum_sha256,
        size_bytes: image.size_bytes ?? null,
      };
      const replayed = lookupIdempotency<FirmwareUploadAccepted>(idempotency, key, declaration);
      if (replayed !== undefined) return wait(replayed);

      for (const [name, value] of [
        ['brand', image.brand],
        ['model', image.model],
        ['version', image.version],
      ] as const) {
        if (!String(value ?? '').trim()) {
          throw referenceInvalid(
            `image metadata is not a valid declaration: ${name} must not be blank`,
          );
        }
      }
      if (!CHECKSUM_PATTERN.test(image.checksum_sha256)) {
        throw referenceInvalid(
          'image metadata is not a valid declaration: checksum_sha256 must be 64 lowercase hex characters',
        );
      }
      const arrived = image.body instanceof Blob ? image.body.size : image.body.byteLength;
      if (arrived === 0) throw referenceInvalid('no image bytes arrived');
      if (image.size_bytes !== undefined && image.size_bytes !== arrived) {
        throw referenceInvalid(
          `declared size_bytes=${image.size_bytes} but ${arrived} bytes arrived`,
        );
      }

      // KNOWN FIXTURE LIMIT, stated instead of hidden: the façade MEASURES the digest of the
      // bytes that arrived (E2 / G-FW-02) and answers 422 `checksum-mismatch` naming both
      // digests when the file is not the one the claim describes. A browser fixture has no
      // hash it may present as an authority, and a digest computed over the very bytes being
      // sent could only ever agree with them — which is not a check. So this door stores the
      // declared digest and does not claim to have verified it. `checksum-mismatch` is a
      // live-mode answer; the server suite and `client.test.ts` are where the refusal is proven.
      const fileId = fixtureFileId(image.brand, image.model, image.version);
      const existing = firmwareRows.find((row) => row.file_id === fileId);
      const row = existing ?? {
        file_id: fileId,
        brand: image.brand,
        model: image.model,
        version: image.version,
        checksum_sha256: image.checksum_sha256,
        size_bytes: arrived,
        status: 'registered',
        genieacs_file: `firmware/${fileId.slice(3)}.bin`,
        created_at: (options.clock?.() ?? new Date()).toISOString(),
      };
      if (!existing) firmwareRows.push(row);

      const answer: FirmwareUploadAccepted = {
        file_id: row.file_id,
        brand: row.brand,
        model: row.model,
        version: row.version,
        status: row.status,
      };
      storeIdempotency(idempotency, key, declaration, answer);
      return wait(answer);
    },

    deleteFirmware: async (fileId: string, opts: WriteOptions): Promise<DeletedResponse> => {
      const key = assertWriteKey(opts.idempotencyKey);
      const sketch = { endpoint: 'firmware:delete', file_id: fileId };
      const replayed = lookupIdempotency<DeletedResponse>(idempotency, key, sketch);
      if (replayed !== undefined) return wait(replayed);
      // The guard above runs BEFORE the existence check and records nothing on the way out:
      // a 404 must leave the key reusable, or the operator's retry answers with the stored 404.
      const index = firmwareRows.findIndex((row) => row.file_id === fileId);
      if (index < 0) {
        throw problemError(
          buildProblem('firmware-not-found', {
            detail: `unknown firmware image ${fileId}`,
            instance: `DELETE /ops/v1/firmware/${fileId}`,
          }),
        );
      }
      firmwareRows.splice(index, 1);
      const answer: DeletedResponse = { deleted: true };
      storeIdempotency(idempotency, key, sketch, answer);
      return wait(answer);
    },

    createCampaign: async (
      body: CampaignCreateRequest,
      opts: WriteOptions,
    ): Promise<CampaignScheduled> => {
      const key = assertWriteKey(opts.idempotencyKey);
      const sketch = { endpoint: 'campaigns:create', body };
      const replayed = lookupIdempotency<CampaignScheduled>(idempotency, key, sketch);
      if (replayed !== undefined) return wait(replayed);

      const image = firmwareRows.find((row) => row.file_id === body.file_id);
      if (!image) {
        throw problemError(
          buildProblem('firmware-not-found', {
            detail: `unknown firmware image ${body.file_id}`,
            instance: 'POST /ops/v1/campaigns',
          }),
        );
      }
      let pcts: number[];
      try {
        pcts = validateWaves(body.waves.map((wave) => wave.pct));
      } catch (exc) {
        throw referenceInvalid(String((exc as Error).message));
      }
      try {
        parseWindow(body.maintenance_window ?? null);
      } catch (exc) {
        // E3 (G-FW-05): a window nobody can parse is not metadata to keep — it is a campaign
        // with no safe time to dispatch, so the create refuses it here.
        throw referenceInvalid(
          `maintenance_window is not a window this service can schedule: ${(exc as Error).message}`,
        );
      }

      const now = options.clock?.() ?? new Date();
      const targets = computeTargets(
        campaignPopulation(),
        { version: image.version },
        body.filter ?? {},
        snapshotVersion,
      );
      const record: CampaignDoc = {
        campaign_id: `camp:s${++campaignCounter}`,
        // pending until the first wave dispatches cleanly; an empty population is complete
        // the moment it is created, and saying so costs no device call
        status: targets.length === 0 ? 'completed' : 'pending',
        file_id: image.file_id,
        brand: image.brand,
        model: image.model,
        version: image.version,
        filter: body.filter ?? {},
        waves: body.waves.map((wave) => ({ pct: wave.pct })),
        per_wave: waveCounts(targets.length, pcts),
        current_wave: 0,
        targets,
        queued: [],
        jobs: [],
        halt_on_fail_pct: body.halt_on_fail_pct ?? 10,
        maintenance_window: body.maintenance_window ?? null,
        dispatch_kind: 'rollout',
        dispatch_file_id: image.file_id,
        started_at: now.toISOString(),
        created_at: now.toISOString(),
        updated_at: null,
      };
      // E4 (G-FW-06): wave 0 becomes QUEUED work, and the slice comes from the engine the
      // dispatch uses — so the queue and the dispatch cannot disagree about what wave 0 means.
      record.queued = waveSlice(record, 0);
      const entry: FixtureCampaign = { doc: record, jobs: {} };
      campaignRecords.set(record.campaign_id, entry);

      const reader = campaignJobReader(entry);
      const answer: CampaignScheduled = {
        campaign_id: record.campaign_id,
        status: record.status as CampaignScheduled['status'],
        wave: { index: 0, devices: 0 },
        queued_devices: record.queued.length,
        next_action: planFor(reader, record, now),
      };
      storeIdempotency(idempotency, key, sketch, answer);
      return wait(answer);
    },

    haltCampaign: async (campaignId: string, opts: WriteOptions): Promise<CampaignHalted> => {
      const key = assertWriteKey(opts.idempotencyKey);
      const sketch = { endpoint: 'campaigns:halt', campaign_id: campaignId };
      const replayed = lookupIdempotency<CampaignHalted>(idempotency, key, sketch);
      if (replayed !== undefined) return wait(replayed);
      const entry = campaignOrThrow(campaignId);
      const status = entry.doc.status;
      if (status === 'halted') {
        // already stopped: the same answer, and no second halt event
        const answer: CampaignHalted = { campaign_id: campaignId, status: 'halted' };
        storeIdempotency(idempotency, key, sketch, answer);
        return wait(answer);
      }
      if (!HALTABLE_STATUSES.includes(status)) {
        throw actionUnsupported(`campaign ${campaignId} is ${status}; nothing to halt`);
      }
      entry.doc.status = 'halted';
      entry.doc.updated_at = (options.clock?.() ?? new Date()).toISOString();
      const answer: CampaignHalted = { campaign_id: campaignId, status: 'halted' };
      storeIdempotency(idempotency, key, sketch, answer);
      return wait(answer);
    },

    resumeCampaign: async (campaignId: string, opts: WriteOptions): Promise<CampaignAccepted> => {
      const key = assertWriteKey(opts.idempotencyKey);
      const sketch = { endpoint: 'campaigns:resume', campaign_id: campaignId };
      const replayed = lookupIdempotency<CampaignAccepted>(idempotency, key, sketch);
      if (replayed !== undefined) return wait(replayed);
      const now = options.clock?.() ?? new Date();
      const entry = campaignOrThrow(campaignId);
      const doc = entry.doc;
      const kind = doc.dispatch_kind || 'rollout';
      const wave = doc.current_wave ?? 0;
      const queued = [...doc.queued];
      const file_id = doc.dispatch_file_id || doc.file_id;

      let targetWave = wave;
      let devices: string[];
      if (queued.length > 0) {
        // continue the interrupted pass of the CURRENT wave
        devices = queued;
      } else {
        if (doc.status === 'completed') {
          throw actionUnsupported(`campaign ${campaignId} already completed; nothing to resume`);
        }
        if (kind === 'rollback') {
          throw actionUnsupported(
            `campaign ${campaignId} was rolled back to ${file_id}; open a new campaign to advance the ladder`,
          );
        }
        const open = openWaveJobs(campaignJobReader(entry), doc, wave, kind);
        if (open > 0) {
          throw actionUnsupported(
            `wave ${wave} of campaign ${campaignId} still has ${open} job(s) in flight; ` +
              'wait for it to finish before advancing',
          );
        }
        if (wave + 1 >= doc.waves.length) {
          throw actionUnsupported(
            `campaign ${campaignId} has no wave after ${wave}; it is already at 100%`,
          );
        }
        targetWave = wave + 1;
        devices = waveSlice(doc, targetWave);
      }

      const result = applyDispatch(entry, { wave: targetWave, devices, kind, now });
      // E3's ruling, recorded where it can be read afterwards: the window binds the AUTOMATIC
      // path. An operator standing at this door is making the decision the window gates — but
      // an override that left no trace is the one thing a window cannot survive.
      const window = windowState(doc.maintenance_window, now);
      const overrode = window !== null && !window.open;
      const answer: CampaignAccepted = {
        campaign_id: campaignId,
        status: result.status as CampaignAccepted['status'],
        wave: { index: targetWave, devices: result.dispatched },
        ...(overrode ? { dispatched_outside_window: true, window } : {}),
      };
      storeIdempotency(idempotency, key, sketch, answer);
      return wait(answer);
    },

    rollbackCampaign: async (
      campaignId: string,
      body: CampaignRollbackRequest,
      opts: WriteOptions,
    ): Promise<CampaignRolledBack> => {
      const key = assertWriteKey(opts.idempotencyKey);
      const sketch = { endpoint: 'campaigns:rollback', campaign_id: campaignId, body };
      const replayed = lookupIdempotency<CampaignRolledBack>(idempotency, key, sketch);
      if (replayed !== undefined) return wait(replayed);
      if (body?.confirm !== true) {
        // the contract's own validator refuses anything but literal `true`
        throw referenceInvalid('rollback requires {"confirm": true}');
      }
      const entry = campaignOrThrow(campaignId);
      const doc = entry.doc;
      const previous = previousImageOf(
        campaignRowsNewestFirst([...campaignRecords.values()]),
        firmwareRows,
        doc,
      );
      if (!previous) {
        throw problemError(
          buildProblem('policy-conflict', {
            detail: `no previous image on record for ${doc.brand} ${doc.model}`,
            instance: `POST /ops/v1/campaigns/${campaignId}:rollback`,
          }),
        );
      }
      const now = options.clock?.() ?? new Date();
      const result = applyDispatch(entry, {
        wave: doc.current_wave ?? 0,
        devices: [...doc.targets],
        kind: 'rollback',
        now,
      });
      doc.dispatch_kind = 'rollback';
      doc.dispatch_file_id = previous.file_id;
      doc.status = 'rolling_back';
      doc.updated_at = now.toISOString();
      const answer: CampaignRolledBack = {
        campaign_id: campaignId,
        status: 'rolling_back',
        rollback_file_id: previous.file_id,
        wave: { index: doc.current_wave ?? 0, devices: result.dispatched },
      };
      storeIdempotency(idempotency, key, sketch, answer);
      return wait(answer);
    },

    /* ---- human identity — the R1 session doors, mirrored (§۶) ----
       Three answers the demo path needs and no fourth: login, logout, and the mirror read. The
       failure modes are the server's own — one sentence for an unknown user and a wrong password,
       503 when the deployment has no account to authenticate against, 401 when a door is asked
       about a session that is not open — so the login screen can render each of them in fixture
       mode, which is where the 370+ vitest tests and the demo both live. */

    openSession: async (body: SessionRequest): Promise<SessionGranted> => {
      // A spent login budget answers with the door's OWN sentence, not the generic forced-scenario
      // string: the wire 429 from `POST /ops/v1/sessions` names the budget and carries the
      // Retry-After (`routers/identity.py:108-112`), and the login screen renders what the server
      // said — a demo string here would let a UI pass on wording the real façade never sends.
      if (forced.code === 'rate-limited') {
        forced.code = null;
        throw problemError(
          buildProblem('rate-limited', {
            detail: `login attempts for this username from this address are budgeted at ${
              options.loginRatePerMinute ?? 10
            } per minute`,
            instance: '/ops/v1/sessions',
          }),
          forced.retryAfter,
        );
      }
      forceScenario('/ops/v1/sessions');
      // An empty user table is the state the façade calls `auth-unconfigured`: nobody seeded an
      // owner, so there is no credential to be wrong about. 503, not 401, and for the server's
      // reason — telling an operator "wrong password" here sends them hunting for a secret that
      // was never created.
      if (consoleUsers.length === 0) {
        throw problemError(
          buildProblem('auth-unconfigured', {
            detail: 'this deployment has no active owner, so no session can be opened',
            instance: '/ops/v1/sessions',
          }),
        );
      }
      if (body.username.trim().length === 0 || body.password.length === 0) {
        // Same code the contract's 422 uses for a malformed body: both fields are required.
        throw referenceInvalid('login needs a non-empty username and a non-empty password');
      }
      const user = consoleUsers.find((entry) => entry.username === body.username);
      if (!user || user.password !== body.password) {
        throw problemError(
          buildProblem('signature-invalid', {
            // The server's sentence, verbatim, for BOTH failures — §۶ refuses a wording that
            // would let an attacker tell an existing username from a missing one.
            detail: 'the username or password is not valid on this deployment',
            instance: '/ops/v1/sessions',
          }),
        );
      }
      fixtureSession = {
        sid: fixtureSidFor(user.username),
        username: user.username,
        role: user.role,
      };
      return wait<SessionGranted>({
        token: fixtureTokenFor(user.username),
        role: user.role,
        expires_at: FIXTURE_SESSION_EXPIRES_AT,
        sid: fixtureSession.sid,
        capabilities: { ...user.capabilities },
      });
    },

    /* R8's second door. No fixture account is enrolled in a second factor — the demo users in
       `fixtures/session.ts` are a username and a password with nothing beside them — so this door
       has no code it could honestly accept. It answers the server's own 401 for an unknown, spent
       or expired challenge (`routers/identity.py`). A test that wants the second step to succeed
       overrides `openSession` AND this method, the same seam `apiWith()` uses; inventing a fixture
       enrolment here would demo a state no deployment has. */
    submitTotp: async (_body: TotpSessionRequest): Promise<SessionGranted> => {
      forceScenario('/ops/v1/sessions/totp');
      throw problemError(
        buildProblem('signature-invalid', {
          detail: 'the second-factor challenge is unknown, expired, or already used',
          instance: '/ops/v1/sessions/totp',
        }),
      );
    },

    closeSession: async (opts: WriteOptions): Promise<SessionRevoked> => {
      forceScenario('/ops/v1/sessions/current');
      const key = assertWriteKey(opts.idempotencyKey);
      const open = fixtureSession;
      if (open === null) {
        // A sequential retry after a revocation is a 401 and cannot be anything else: the door
        // would have to authenticate a session that is no longer in the table to replay for it.
        throw problemError(
          buildProblem('signature-invalid', {
            detail: "session is not in this deployment's session table",
            instance: '/ops/v1/sessions/current',
          }),
        );
      }
      const sketch = { endpoint: 'sessions.current:delete', sid: open.sid };
      const answer = applyIdempotency<SessionRevoked>(idempotency, key, sketch, () => {
        fixtureSession = null;
        return {
          sid: open.sid,
          revoked_at: (options.clock?.() ?? new Date()).toISOString(),
          detail: `session ${open.sid} revoked; the token stops working now, not when it expires`,
        } satisfies SessionRevoked;
      });
      return wait(answer);
    },

    effectivePermissions: async (): Promise<EffectivePermissions> => {
      forceScenario('/ops/v1/permissions/effective');
      const open = fixtureSession;
      const user =
        open === null ? undefined : consoleUsers.find((e) => e.username === open.username);
      if (open === null || user === undefined) {
        throw problemError(
          buildProblem('signature-invalid', {
            detail: 'no bearer token arrived, so there is no caller to answer for',
            instance: '/ops/v1/permissions/effective',
          }),
        );
      }
      return wait<EffectivePermissions>({
        kind: 'human',
        subject: user.username,
        role: user.role,
        // Only the capabilities the role holds: absence IS `none`, which is what lets a page
        // render a `DeniedState` out of this document alone (§۹).
        capabilities: { ...user.capabilities },
        roles: [...FIXTURE_RBAC_ROLES],
      });
    },

    /* ---- console users + ops settings — the identity doors mirrored (§۶, U-16-g1) ----
       The four `/ops/v1/users` doors and the two `/ops/v1/settings` doors answer here with the SERVER's
       refusals, not paraphrases: `409 last-owner` carries `routers/identity.py:457-459`'s sentence and
       leaves the row untouched, because the §۳-۲ guard lives inside the write and the UI's job is to show
       the refusal, never to prevent the attempt. Every write takes an `Idempotency-Key` (the doors declare
       the header as required, so a missing key is a 422 on the wire) and replays the stored answer. */

    listUsers: async (): Promise<UserListResponse> => {
      forceScenario('/ops/v1/users');
      return wait<UserListResponse>({
        count: userRows.length,
        users: userRows.map((row) => ({ ...row })),
      });
    },

    createUser: async (
      body: UserCreateRequest,
      opts: WriteOptions,
    ): Promise<UserMutationAccepted> => {
      const instance = 'POST /ops/v1/users';
      forceScenario(instance);
      const key = assertWriteKey(opts.idempotencyKey);
      // The door validates the role BEFORE the replay check is reached in the response, and the username
      // uniqueness is checked after the replay: a replayed key must return the stored answer even if the
      // name is now taken, because the stored answer IS the creation. This order mirrors `identity.py`.
      const role = body.role ?? 'user';
      if (!isKnownRole(role)) throw referenceInvalid(roleInvalidDetail(role), instance);
      if (body.username.trim().length === 0 || body.password.length === 0) {
        throw referenceInvalid('username and password are both required (min_length=1)', instance);
      }
      const sketch = { endpoint: 'users:create', username: body.username, role };
      const answer = applyIdempotency<UserMutationAccepted>(idempotency, key, sketch, () => {
        if (userRows.some((row) => row.username === body.username)) {
          throw problemError(
            buildProblem('user-exists', { detail: userExistsRefusal(body.username), instance }),
          );
        }
        const now = (options.clock?.() ?? new Date()).toISOString();
        const row: ConsoleUser = {
          user_id: `user-${body.username.replace(/[^A-Za-z0-9._-]/g, '_')}`,
          username: body.username,
          role,
          status: 'active',
          created_at: now,
          updated_at: now,
        };
        userRows.push(row);
        return {
          user: { ...row },
          detail: `user '${row.username}' created with role ${role} by ${identitySubject()}`,
        } satisfies UserMutationAccepted;
      });
      return wait(answer);
    },

    patchUser: async (
      userId: string,
      body: UserPatchRequest,
      opts: WriteOptions,
    ): Promise<UserMutationAccepted> => {
      const instance = `PATCH /ops/v1/users/${userId}`;
      forceScenario(instance);
      const key = assertWriteKey(opts.idempotencyKey);
      const user = userRows.find((row) => row.user_id === userId);
      if (user === undefined) {
        throw problemError(buildProblem('user-not-found', { instance }));
      }
      if (body.role !== undefined && !isKnownRole(body.role)) {
        throw referenceInvalid(roleInvalidDetail(body.role), instance);
      }
      if (body.status !== undefined && !isKnownStatus(body.status)) {
        throw referenceInvalid(statusInvalidDetail(body.status), instance);
      }
      const sketch = {
        endpoint: 'users:patch',
        user_id: userId,
        role: body.role,
        status: body.status,
        password: Boolean(body.password),
      };
      const answer = applyIdempotency<UserMutationAccepted>(idempotency, key, sketch, () => {
        // The sketch carries the PASSWORD FLAG and never the password: `models.py` hashes it before the
        // store, and a stored idempotency record holding a credential would be the leak §۶ names.
        const resulting: ConsoleUser = {
          ...user,
          role: body.role ?? user.role,
          status: body.status ?? user.status,
        };
        // §۳-۲ evaluated against the live table, as `store.put_user_guarded` does — a refusal writes
        // nothing, so the row below is untouched and the next deliberate attempt is a new key.
        if (!lastOwnerAllows(userRows, resulting)) {
          throw problemError(
            buildProblem('last-owner', { detail: lastOwnerRefusal(user.username), instance }),
          );
        }
        const before = { role: user.role, status: user.status };
        user.role = resulting.role;
        user.status = resulting.status;
        const passwordChanged = Boolean(body.password);
        if (passwordChanged) {
          if (body.password === undefined || body.password.length === 0) {
            throw referenceInvalid('a password change needs a non-empty password', instance);
          }
        }
        user.updated_at = (options.clock?.() ?? new Date()).toISOString();
        // U-02/AUD-02's session policy: a disable and a rotated credential take the live sessions with
        // them; a role change takes none («enforced on the next request» is a live role resolution).
        const revoke = user.status !== 'active' || passwordChanged;
        const revoked = revoke ? revokeSessionsOf(user.username) : 0;
        const changed: string[] = (['role', 'status'] as const).filter(
          (name) => before[name] !== (name === 'role' ? user.role : user.status),
        );
        if (passwordChanged) changed.push('password');
        return {
          user: { ...user },
          detail:
            `user '${user.username}' updated by ${identitySubject()}: ` +
            (changed.length > 0 ? changed.join(', ') : 'nothing changed') +
            (revoked > 0 ? `; ${revoked} session(s) revoked` : ''),
        } satisfies UserMutationAccepted;
      });
      return wait(answer);
    },

    deleteUser: async (userId: string, opts: WriteOptions): Promise<UserMutationAccepted> => {
      const instance = `DELETE /ops/v1/users/${userId}`;
      forceScenario(instance);
      const key = assertWriteKey(opts.idempotencyKey);
      const index = userRows.findIndex((row) => row.user_id === userId);
      if (index < 0) throw problemError(buildProblem('user-not-found', { instance }));
      const user = userRows[index] as ConsoleUser;
      const sketch = { endpoint: 'users:delete', user_id: userId };
      const answer = applyIdempotency<UserMutationAccepted>(idempotency, key, sketch, () => {
        // Deleting is the SAME authorization question as demoting (§۳-۲), so it runs the same guard with a
        // row that carries only its id — exactly what `delete_user_guarded` hands the guard.
        if (!lastOwnerAllows(userRows, { user_id: userId })) {
          throw problemError(
            buildProblem('last-owner', { detail: lastOwnerRefusal(user.username), instance }),
          );
        }
        const revoked = revokeSessionsOf(user.username);
        userRows.splice(index, 1);
        return {
          user: { ...user },
          detail:
            `user '${user.username}' deleted by ${identitySubject()}; ` +
            `${revoked} session(s) revoked with it`,
        } satisfies UserMutationAccepted;
      });
      return wait(answer);
    },

    /* ---- system measurement — `GET /ops/v1/system/components` and `GET /ops/v1/system/queues` (O4) ----
       Both doors are READS on `health.read`, and the fixture answers them the way the façade does:
       the same four components with the same evidence, and the closed depth vocabularies WHOLE — a
       word nobody holds is a MEASURED zero, because the store counted it and found none.

       The MISSING-DEPTH case is representable and is never flattened: `systemDepthsUnavailable` makes
       the store component's `depths` `null` (the server's «the depth read failed» answer, which keeps
       the component's state where the counts probe put it), so a page can tell «zero rows» from
       «the server could not measure». */

    getSystemComponents: async (): Promise<SystemComponentsResponse> => {
      forceScenario('/ops/v1/system/components');
      return wait<SystemComponentsResponse>(buildFixtureSystemComponents(depthsUnavailable));
    },

    getSystemQueues: async (): Promise<SystemQueuesResponse> => {
      forceScenario('/ops/v1/system/queues');
      return wait<SystemQueuesResponse>(buildFixtureSystemQueues());
    },

    /* The third system door (DA-09). Like its two sisters it answers from memory, so it takes no
       `ReadOptions` — there is no in-flight request to cancel, and the narrower signature is
       assignable to the interface's. The case the fixture exists to make reachable is the ABSENCE:
       two of the six loops have no row, and a fixture that answered a row for every name would
       teach the console to invent a status for a worker the façade never heard from. */

    getSystemWorkers: async (): Promise<SystemWorkersResponse> => {
      forceScenario('/ops/v1/system/workers');
      return wait<SystemWorkersResponse>(buildFixtureSystemWorkers(workersUnreadable));
    },

    /* ---- ops settings — the whitelist's two doors (§۵) ----
       The page must not learn a rule here: `PUT` answers with the SAME payload the read does, and that
       payload carries `SETTINGS_INTENT_NOTE`. A fixture that dropped the note would let the page render
       «applied» — the C5 worker that applies these values is not wired, and the door says so out loud. */

    getOpsSettings: async (): Promise<OpsSettingsRead> => {
      forceScenario('/ops/v1/settings');
      return wait<OpsSettingsRead>(settingsReadFor(settingsState) as OpsSettingsRead);
    },

    putOpsSettings: async (
      values: Record<string, number>,
      opts: WriteOptions,
    ): Promise<OpsSettingsRead> => {
      const instance = 'PUT /ops/v1/settings';
      forceScenario(instance);
      const key = assertWriteKey(opts.idempotencyKey);
      // Validation runs BEFORE the replay check, like `system.py::write_settings`: a body the whitelist
      // refuses records nothing, so its key stays reusable for the corrected attempt.
      if (values === null || typeof values !== 'object' || Array.isArray(values)) {
        throw referenceInvalid('body must be a JSON object of settings', instance);
      }
      const unknown = unknownSettingsDetail(values);
      if (unknown !== null) throw referenceInvalid(unknown, instance);
      for (const [name, value] of Object.entries(values)) {
        const rejection = settingsRejection(name, value);
        if (rejection !== null) throw referenceInvalid(rejection, instance);
      }
      const sketch = { endpoint: 'settings:update', body: { ...values } };
      const answer = applyIdempotency<OpsSettingsRead>(idempotency, key, sketch, () => {
        // Merge, not replace: an omitted key keeps its stored value, and a key set back to its default is
        // still an explicit write. An EMPTY body is a no-op that answers 200 and stamps nothing — the
        // door refuses to let a PUT that changed nothing claim a `updated_at`.
        if (Object.keys(values).length > 0) {
          for (const [name, value] of Object.entries(values)) settingsState.stored[name] = value;
          settingsState.updated_at = (options.clock?.() ?? new Date()).toISOString();
        }
        return settingsReadFor(settingsState) as OpsSettingsRead;
      });
      return wait(answer);
    },

    /* ---- permission matrix — the two `/ops/v1/permissions` doors (§۶, U-16-g2) ----
       The read answers the COMPLETE grid with the vocabularies riding along, exactly as
       `identity.effective_matrix` publishes it (§۳-۴: an unnamed cell is `none`, never an absence). The
       write judges BEFORE it applies — the ported `validateMatrixWrite` refuses with the door's own
       sentences (422 `reference-invalid` for the vocabulary and §۳-۳, 409 `policy-conflict` for §۳-۱), a
       refusal leaves the grid byte-identical and writes NO audit line, and an accepted batch appends one
       `permission.change` row with the canonical actor shape beside its cells (U-16-b). The capability
       check is NOT here on purpose — the editor mirrors `permissions/effective` for that, following the
       `listKeys` precedent: a fixture answering 403 itself would make the denied-state tests prove
       nothing about the mirror. */

    getPermissionMatrix: async (): Promise<PermissionMatrixRead> => {
      forceScenario('/ops/v1/permissions');
      return wait<PermissionMatrixRead>({
        capabilities: cloneMatrix(matrixGrid),
        roles: [...FIXTURE_RBAC_ROLES],
        levels: [...FIXTURE_LEVELS],
      });
    },

    putPermissionMatrix: async (
      body: MatrixWriteBody,
      opts: WriteOptions,
    ): Promise<MatrixMutationAccepted> => {
      const instance = 'PUT /ops/v1/permissions';
      forceScenario(instance);
      const key = assertWriteKey(opts.idempotencyKey);
      // `models.py::MatrixWriteRequest` is `min_length=1`: a write carrying no cells has no meaning to
      // replay, so it is a body error before any judgment. The editor cannot construct this body (its
      // submit needs a non-empty draft); the guard is here so a smuggled one meets the door's refusal.
      if (!Array.isArray(body?.cells) || body.cells.length === 0) {
        throw referenceInvalid(
          'cells must carry at least one cell — an empty write has no meaning to replay',
          instance,
        );
      }
      const sketch = { endpoint: 'permissions:put', cells: body.cells };
      // The key is checked BEFORE the judgment, like the door: a replay of the same body answers the
      // stored accepted document — same changed cells, same detail sentence — and writes nothing new.
      const answer = applyIdempotency<MatrixMutationAccepted>(idempotency, key, sketch, () => {
        const judged = validateMatrixWrite(matrixGrid, body.cells);
        if (!judged.ok) {
          throw judged.code === 'policy-conflict'
            ? problemError(buildProblem('policy-conflict', { detail: judged.detail, instance }))
            : referenceInvalid(judged.detail, instance);
        }
        const changes: MatrixCellChange[] = judged.resolved.map((cell) => ({
          capability: cell.capability,
          role: cell.role,
          before: cell.before,
          after: cell.level,
        }));
        // Only what was judged is written (`routers/identity.py`: «write what was judged»), and the
        // validation above guarantees every cell names a registry key and a published role.
        for (const cell of judged.resolved) matrixGrid[cell.capability]![cell.role] = cell.level;
        const moved = changes.reduce(
          (count, change) => (change.before !== change.after ? count + 1 : count),
          0,
        );
        const subject = identitySubject();
        const actorRow = userRows.find((row) => row.username === fixtureSession?.username);
        matrixAuditRows.unshift({
          kind: MATRIX_AUDIT_KIND,
          action: 'put',
          cells: changes.map((change) => ({ ...change })),
          // The canonical (أ) caller shape, spread as the door spreads `**principal.actor()`: a human
          // write names `actor` with the username and `user_id` with the acting account (null when
          // nobody signed in — the demo refuses to fabricate a person, §۶).
          actor_kind: 'human',
          actor: subject,
          user_id: actorRow?.user_id ?? null,
          client_id: null,
          created_at: (options.clock?.() ?? new Date()).toISOString(),
        });
        return {
          changed: changes,
          capabilities: cloneMatrix(matrixGrid),
          detail: matrixAcceptedDetail(moved, changes.length, subject),
        } satisfies MatrixMutationAccepted;
      });
      return wait(answer);
    },

    /* ---- the subscriber surface (S1, G-SUB-01/03) ----
       The six doors of `routers/subscribers.py`, and the SERVER's answers to them —
       including the ones a fixture is tempted to soften:

       - **the identifying fields are masked by the CALLER'S LEVEL**, never by a request
         flag. `subscriber.read` at `edit` sees them whole, at `read` it sees them masked,
         and a signed-out caller sees them masked. The create door asks the level on
         `subscriber.manage` instead — the key IT is gated by — exactly like the server;
       - **a missing subscriber is a 404, not a zero.** «there is no such subscriber» is a
         fact about the world, and a fixture that answered `total: 0` for a lookup miss
         would teach the console to draw an empty fleet where a missing row is;
       - **the device link is a real link**: an unknown device is `404 device-not-found`,
         a device somebody else holds is `409 binding-conflict` (the repository's EXISTING
         code, the same word the bind door uses), and an unknown profile is
         `404 service-profile-not-found`;
       - **the service profile carries no personal data**, so it has no mask and no level:
         every reader of `service.profile` sees the same fields;
       - **the writes carry the server's key rules**: the two creates REQUIRE an
         `Idempotency-Key` (a replay there would mint a second row for one person), the
         key is checked BEFORE the conflict checks — replay-EQUIVALENCE, so a replay
         answers the stored 200 and never its own 409 — and `PUT /service-profiles/{id}`
         takes NONE: the body IS the row, so a retry writes the same row with the same
         values. */

    listSubscribers: async (query: ListSubscribersQuery = {}): Promise<SubscriberListResponse> => {
      const instance = 'GET /ops/v1/subscribers';
      const limit = query.limit ?? SUBSCRIBER_PAGE_LIMIT;
      const offset = query.offset ?? 0;
      if (!Number.isInteger(limit) || limit < 1 || limit > SUBSCRIBER_PAGE_LIMIT) {
        throw referenceInvalid(`limit must be between 1 and ${SUBSCRIBER_PAGE_LIMIT}`, instance);
      }
      if (!Number.isInteger(offset) || offset < 0) {
        throw referenceInvalid('offset must be an integer ≥ 0', instance);
      }
      if (
        query.status !== undefined &&
        !(SUBSCRIBER_STATUSES as readonly string[]).includes(query.status)
      ) {
        throw referenceInvalid(`status must be one of ${SUBSCRIBER_STATUSES.join(', ')}`, instance);
      }
      // The order is by the subscriber's OWN identity — a total order nothing a write can
      // move, so a page boundary always describes the same list.
      const rows = [...subscriberRows.values()]
        .filter((row) => (query.status ? row.status === query.status : true))
        .sort((a, b) =>
          a.subscriber_id < b.subscriber_id ? -1 : a.subscriber_id > b.subscriber_id ? 1 : 0,
        );
      const page = rows.slice(offset, offset + limit);
      const level = callerLevel('subscriber.read');
      return wait<SubscriberListResponse>({
        items: page.map((row) => subscriberView(row, level)),
        total: rows.length,
        limit,
        offset,
      });
    },

    getSubscriber: async (subscriberId: string): Promise<Subscriber> => {
      const row = subscriberRows.get(subscriberId);
      if (!row) {
        throw problemError(
          buildProblem('subscriber-not-found', {
            instance: `GET /ops/v1/subscribers/${subscriberId}`,
          }),
        );
      }
      return wait(subscriberView(row, callerLevel('subscriber.read')));
    },

    createSubscriber: async (
      body: SubscriberCreateRequest,
      opts: WriteOptions,
    ): Promise<Subscriber> => {
      const key = assertWriteKey(opts.idempotencyKey);
      const instance = 'POST /ops/v1/subscribers';
      // The body is judged BEFORE the idempotency reservation, exactly like the door: a
      // refused body records nothing and the same key stays free for a corrected retry.
      const bad = [...subscriberBodyProblems(body), ...subscriberSecondGate(body)];
      if (bad.length > 0) {
        const fields = [...new Set(bad)].sort();
        const err = referenceInvalid(
          'the subscriber body is not a shape this vocabulary accepts: ' + fields.join(', '),
          instance,
        );
        (err.problem as { errors?: unknown }).errors = fields.map((name) => ({
          field: name,
          message: 'not a value this vocabulary accepts',
        }));
        throw err;
      }

      // The key is checked BEFORE the link checks, exactly like the door — the house order
      // every key-taking door keeps (the bind door, the outbox re-drive, the job cancel,
      // the profile activation): reserve the key first, judge the conflict second.
      //
      // The reason is the contract's own rule, replay-EQUIVALENCE: a replay of a create
      // that NAMED a device must answer the first call's answer — a 200 — while the
      // device is now held by the very subscriber that call created. Judging the link
      // first would make the replay meet its own `409 binding-conflict` and answer «this
      // device is not yours» about a device the caller had just been given.
      const sketch = { endpoint: 'subscriber.create', body };
      const replayed = replayOrThrow<Subscriber>(key, sketch);
      if (replayed !== undefined) return wait(replayed);

      // The identity is NOT in the body: the door mints it, server-side.
      const subscriberId = mintSubscriberId();

      // The BSS's OWN reference is an IDENTITY too, where the body carries one: it
      // names exactly ONE row, so a second row under it is refused — with the
      // repository's EXISTING code (`409 binding-conflict`), the same word the device
      // conflict wears, because a taken identity is the same fact as a taken device.
      const bssRef = body.bss_subscriber_ref ?? null;
      if (bssRef !== null && findSubscriberByBssRef(bssRef) !== undefined) {
        throw problemError(
          buildProblem('binding-conflict', {
            detail: 'another subscriber already records this BSS subscriber reference',
            instance,
          }),
        );
      }

      if (body.acs_device_id) {
        if (findDevice(body.acs_device_id) === undefined) {
          throw problemError(buildProblem('device-not-found', { instance }));
        }
        // A device already bound to ANOTHER subscriber is refused with the repository's
        // EXISTING code — the same word the bind door uses, not a new one.
        //
        // **A name that RESOLVES to this row is this row.** The row carries TWO
        // identities — the minted one and the BSS's own — and the binding's
        // `subscriber_ref` may be either of them, so the comparison accepts BOTH of
        // this row's names and every other name is somebody else's.
        const holder = bindingOverlay.get(body.acs_device_id)?.subscriber_ref ?? '';
        const ownNames = new Set<string>([subscriberId]);
        if (bssRef !== null) ownNames.add(bssRef);
        if (holder && !ownNames.has(holder)) {
          throw problemError(
            buildProblem('binding-conflict', {
              detail: 'this device is already bound to another subscriber',
              instance,
            }),
          );
        }
        const existing = findSubscriberByDevice(body.acs_device_id);
        if (existing !== undefined && existing.subscriber_id !== subscriberId) {
          throw problemError(
            buildProblem('binding-conflict', {
              detail: 'another subscriber already holds this device',
              instance,
            }),
          );
        }
      }

      if (body.service_profile_id && !profileRows.has(body.service_profile_id)) {
        throw problemError(buildProblem('service-profile-not-found', { instance }));
      }

      const now = new Date().toISOString();
      const row: FixtureSubscriber = {
        subscriber_id: subscriberId,
        mobile: body.mobile,
        national_id: body.national_id ?? null,
        contract_number: body.contract_number ?? null,
        // The status vocabulary was already judged above (`subscriberSecondGate`), so this
        // narrowing is the door's own: the row stores one of the two words.
        status: (body.status ?? 'active') as FixtureSubscriber['status'],
        acs_device_id: body.acs_device_id ?? null,
        service_profile_id: body.service_profile_id ?? null,
        // The BSS's own reference is RECORDED: it is the row's second identity, and a
        // console that dropped it would teach the operator a shape the server does not
        // keep.
        bss_subscriber_ref: bssRef,
        created_at: now,
        updated_at: now,
      };
      subscriberRows.set(row.subscriber_id, row);
      // The answer carries the fields the CALLER may see — and the level that decides them
      // is the one on the key this door is gated by (`subscriber.manage`), as on the server.
      const payload = subscriberView(row, callerLevel('subscriber.manage'));
      record(key, sketch, payload);
      return wait(payload);
    },

    listServiceProfiles: async (
      query: ListServiceProfilesQuery = {},
    ): Promise<ServiceProfileListResponse> => {
      const instance = 'GET /ops/v1/service-profiles';
      const limit = query.limit ?? SUBSCRIBER_PAGE_LIMIT;
      const offset = query.offset ?? 0;
      if (!Number.isInteger(limit) || limit < 1 || limit > SUBSCRIBER_PAGE_LIMIT) {
        throw referenceInvalid(`limit must be between 1 and ${SUBSCRIBER_PAGE_LIMIT}`, instance);
      }
      if (!Number.isInteger(offset) || offset < 0) {
        throw referenceInvalid('offset must be an integer ≥ 0', instance);
      }
      // Ordered by name — a total order, because the name is unique.
      const rows = [...profileRows.values()].sort((a, b) =>
        a.name < b.name ? -1 : a.name > b.name ? 1 : 0,
      );
      return wait<ServiceProfileListResponse>({
        items: rows.slice(offset, offset + limit).map((row) => profileView(row)),
        total: rows.length,
        limit,
        offset,
      });
    },

    createServiceProfile: async (
      body: ServiceProfileWriteRequest,
      opts: WriteOptions,
    ): Promise<ServiceProfile> => {
      const key = assertWriteKey(opts.idempotencyKey);
      const instance = 'POST /ops/v1/service-profiles';
      const bad = profileBodyProblems(body);
      if (bad.length > 0) {
        const fields = [...new Set(bad)].sort();
        const err = referenceInvalid(
          'the service profile body is not a shape this vocabulary accepts: ' + fields.join(', '),
          instance,
        );
        (err.problem as { errors?: unknown }).errors = fields.map((name) => ({
          field: name,
          message: 'not a value this vocabulary accepts',
        }));
        throw err;
      }
      // The key is checked BEFORE the name, exactly like the door: the rule of the key is
      // replay-EQUIVALENCE, so a replay of a create that succeeded must answer the stored
      // 200 rather than meet the name IT took and answer `409 service-profile-conflict`
      // about a name the caller had just been given.
      const sketch = { endpoint: 'service-profile.create', body };
      const replayed = replayOrThrow<ServiceProfile>(key, sketch);
      if (replayed !== undefined) return wait(replayed);
      // The name is the profile's operator-facing identity: a taken name is refused with
      // `409 service-profile-conflict` — «the request is fine, the name is taken» is a
      // different fact from «the body is malformed». A refusal here records nothing, so
      // the key stays free for the corrected request.
      if (findProfileByName(body.name) !== undefined) {
        throw problemError(buildProblem('service-profile-conflict', { instance }));
      }
      const now = new Date().toISOString();
      const row: ServiceProfile = {
        profile_id: mintProfileId(),
        name: body.name,
        down_mbps: body.down_mbps,
        up_mbps: body.up_mbps,
        vlan_id: body.vlan_id,
        dba: { ...body.dba },
        created_at: now,
        updated_at: now,
      };
      profileRows.set(row.profile_id, row);
      const payload = profileView(row);
      record(key, sketch, payload);
      return wait(payload);
    },

    putServiceProfile: async (
      profileId: string,
      body: ServiceProfileWriteRequest,
    ): Promise<ServiceProfile> => {
      const instance = `PUT /ops/v1/service-profiles/${profileId}`;
      const bad = profileBodyProblems(body);
      if (bad.length > 0) {
        const fields = [...new Set(bad)].sort();
        const err = referenceInvalid(
          'the service profile body is not a shape this vocabulary accepts: ' + fields.join(', '),
          instance,
        );
        (err.problem as { errors?: unknown }).errors = fields.map((name) => ({
          field: name,
          message: 'not a value this vocabulary accepts',
        }));
        throw err;
      }
      const existing = profileRows.get(profileId);
      if (existing === undefined) {
        // The same code a create answers with: «there is no such profile» is one fact
        // however you ask for it.
        throw problemError(buildProblem('service-profile-not-found', { instance }));
      }
      // A name already held by ANOTHER profile is refused: two rows under one name would
      // make an operator's pick out of the list a coin toss.
      const holder = findProfileByName(body.name);
      if (holder !== undefined && holder.profile_id !== profileId) {
        throw problemError(buildProblem('service-profile-conflict', { instance }));
      }
      // A FULL REPLACEMENT: the body IS the profile, not a patch of it. The row's
      // `created_at` stays; `updated_at` moves. NO `Idempotency-Key` is taken here — the
      // door declares none, and the retry writes the same row with the same values.
      const row: ServiceProfile = {
        profile_id: profileId,
        name: body.name,
        down_mbps: body.down_mbps,
        up_mbps: body.up_mbps,
        vlan_id: body.vlan_id,
        dba: { ...body.dba },
        created_at: existing.created_at,
        updated_at: new Date().toISOString(),
      };
      profileRows.set(profileId, row);
      return wait(profileView(row));
    },

    /* ---- the subscriber search (S2, G-SUB-08) + the 360-degree view (S3, G-PRC-14) ----
       The port of `routers/subscriber_search.py` and the S3 door of
       `routers/subscribers.py`, with the server's own answers and the server's own
       ORDER:

       - **the search takes NO `Idempotency-Key`**: it is a read, its replay is
         equivalent, and the published door declares no key parameter. The criteria
         travel in the BODY, because a phone number in a query string is a phone
         number in an access log;
       - **the criteria are a CONJUNCTION and at least one must be given**: an empty
         criteria is 422 `reference-invalid`, never an unfiltered read — «this
         deployment holds no subscribers» is a lie a caller would act on;
       - **`subscriber_ref` matches EITHER identity** — the minted one and the BSS's
         own — because the two share ONE namespace (S1's rule);
       - **`serial` is a field of the DEVICE row**: the answer is the subscriber who
         HOLDS that device, not a device row;
       - **the order is by `subscriber_id` ascending** and `total` is the number of
         rows BEHIND the page;
       - **the mask is the door's**: the level on the search door's own key
         (`fleet.devices.read`) decides the identifying fields, and
         `bss_subscriber_ref` is never masked (it is an IDENTIFIER);
       - **the audit row carries the SHAPE of the search and the count** — the
         criterion NAMES, never the value that was searched for. */

    searchSubscribers: async (body: SubscriberSearchRequest): Promise<SubscriberSearchResponse> => {
      const instance = 'POST /integration/v1/devices:search';

      // The body's OWN validation comes FIRST — the house order the doors keep:
      // «this body is wrong» is more useful than «this deployment does not run it»
      // when both are true. The bound is the one the body publishes (`1..200`,
      // default `50` — the outbox's paging cap).
      const limit = body?.limit ?? SUBSCRIBER_SEARCH_DEFAULT_LIMIT;
      if (
        !Number.isInteger(limit) ||
        limit < SUBSCRIBER_SEARCH_MIN_LIMIT ||
        limit > SUBSCRIBER_SEARCH_MAX_LIMIT
      ) {
        throw referenceInvalid(
          `limit must be between ${SUBSCRIBER_SEARCH_MIN_LIMIT} and ${SUBSCRIBER_SEARCH_MAX_LIMIT}`,
          instance,
        );
      }

      // The criteria the body actually carries — the absent ones are NOT «the empty
      // string», they are «no constraint on this field» (`SubscriberSearchRequest.criteria`).
      const criteria: { name: string; value: string }[] = [];
      for (const name of SUBSCRIBER_SEARCH_CRITERIA) {
        const value = body?.[name];
        if (typeof value === 'string' && value.length > 0) criteria.push({ name, value });
      }
      if (criteria.length === 0) {
        throw referenceInvalid(
          'the search body names no criterion: a search with no criterion is a request for every ' +
            'subscriber, and an empty answer to that request would say this deployment holds none',
          instance,
        );
      }

      // …and only then the switch: a well-formed request from a caller who is allowed
      // to make it gets the honest 503.
      if (!subscriberSurfaceEnabled) {
        throw problemError(buildProblem('subscriber-disabled', { instance }));
      }

      const rows = [...subscriberRows.values()]
        .filter((row) => subscriberMatchesCriteria(row, criteria))
        // The order is by the subscriber's OWN identity — a total order nothing a
        // write can move, so a page boundary always describes the same list.
        .sort((a, b) =>
          a.subscriber_id < b.subscriber_id ? -1 : a.subscriber_id > b.subscriber_id ? 1 : 0,
        );
      const total = rows.length;
      const page = rows.slice(0, limit);
      const level = callerLevel('fleet.devices.read');
      const items: SubscriberSearchHit[] = page.map((row) => subscriberSearchHit(row, level));

      // The audit row: the actor, the SHAPE of the search and the COUNT — never the
      // value that was searched for. `by` names the criteria the body carried.
      const actorRow = userRows.find((row) => row.username === fixtureSession?.username);
      subscriberSearchAuditRows.unshift({
        kind: SUBSCRIBER_SEARCH_AUDIT_KIND,
        by: criteria.map((criterion) => criterion.name),
        count: total,
        actor_kind: 'human',
        actor: identitySubject(),
        user_id: actorRow?.user_id ?? null,
        client_id: null,
        created_at: (options.clock?.() ?? new Date()).toISOString(),
      });

      return wait<SubscriberSearchResponse>({ items, total, limit });
    },

    /**
     * The 360-degree view of one subscriber — the port of the S3 door of
     * `routers/subscribers.py`.
     *
     * **Six sources and no seventh**: the subscriber row, the device row, the binding
     * row, the service-profile row, a BOUNDED page of the device's alarm rows and a
     * BOUNDED page of the device's job rows. **A section whose row does not exist is
     * `null`** — absent, never invented, and never a zero.
     */
    getSubscriberOverview: async (subscriberId: string): Promise<SubscriberOverview> => {
      const instance = `GET /ops/v1/subscribers/${subscriberId}/overview`;
      // The switch answers first here (this door validates nothing but the path), and
      // a miss is the surface's own 404 — «no such subscriber» is one fact however it
      // is asked for.
      if (!subscriberSurfaceEnabled) {
        throw problemError(buildProblem('subscriber-disabled', { instance }));
      }
      const row = subscriberRows.get(subscriberId);
      if (!row) {
        throw problemError(buildProblem('subscriber-not-found', { instance }));
      }

      const deviceId = row.acs_device_id ?? '';
      const device = deviceId ? findDevice(deviceId) : undefined;
      // The session's own binding facts (a bind this session wrote) win over the
      // device row's seeded ones — the same overlay the device summary reads.
      const overlay = deviceId ? bindingOverlay.get(deviceId) : undefined;
      const nasRef = overlay?.nas_ref ?? device?.nas_ref ?? null;

      const deviceSection: SubscriberDeviceView | null = device
        ? {
            acs_device_id: device.acs_device_id,
            serial: device.serial,
            oui: device.oui,
            product_class: device.product_class,
            manufacturer: device.manufacturer,
            software_version: device.software_version,
            online: device.online,
            last_inform_at: device.last_inform_at,
          }
        : null;

      // The BINDING row is the MIRROR of the claim: the device's own binding facts
      // (its `nas_ref` and its `binding_version`) plus whatever this session wrote.
      // A device with no binding at all has NO binding section — `null`, not a zero.
      const bindingSection: SubscriberBindingView | null =
        nasRef === null
          ? null
          : {
              acs_device_id: deviceId,
              nas_ref: nasRef,
              // The claim the binding row carries — either of the subscriber's two
              // identities, and never masked (it is an identifier).
              subscriber_ref: overlay?.subscriber_ref ?? null,
              binding_version: overlay?.binding_version ?? device?.binding_version ?? 0,
              // The fixture stores no bind instant, and a field nobody read is a field
              // nobody may publish.
              bound_at: null,
            };

      // The two BOUNDED pages: both grow with TIME, so an unbounded read of either
      // would cost more every day and answer no more.
      const alarms = deviceId
        ? deriveAlarmRows(lifecyclesOf())
            .filter((row) => row.acs_device_id === deviceId)
            .slice(0, OVERVIEW_ALARM_PAGE)
        : [];
      const jobs = deviceId
        ? fleetJobRows()
            .filter((row) => row.device_id === deviceId)
            .slice(0, OVERVIEW_JOB_PAGE)
        : [];

      const profile = row.service_profile_id ? profileRows.get(row.service_profile_id) : undefined;

      return wait<SubscriberOverview>({
        // The subscriber section is masked by the caller's level on THIS door's key
        // (`subscriber.read`) — the same rule, and the same mask, as every other
        // subscriber door.
        subscriber: subscriberView(row, callerLevel('subscriber.read')),
        device: deviceSection,
        binding: bindingSection,
        service_profile: profile === undefined ? null : profileView(profile),
        alarms,
        jobs,
      });
    },

    /**
     * The subscriber's lifecycle (S4, G-SUB-05) — the port of the two doors of
     * `routers/subscribers.py`.
     *
     * **A SUSPENSION IS ONE FIELD ON ONE ROW.** The row's `status` moves and nothing
     * else does: no second row is created, and the device link, the binding and the
     * service profile are untouched — so the resume undoes exactly what the
     * suspension did.
     *
     * **THE ORDER is the door's**: the switch answers first (a well-formed request from
     * a caller who may make it gets the honest 503), then the row is read, then the
     * key is reserved, and only then is the row judged (404 for a row that is not
     * there, 409 for a row whose state forbids the step). A refusal records nothing, so
     * the key stays free for a corrected request.
     *
     * **THE KEY IS RESERVED BEFORE THE ROW IS JUDGED**, exactly like the door: a replay
     * of a suspension that SUCCEEDED must answer the stored answer, not meet the row it
     * just suspended and answer its own conflict.
     */
    suspendSubscriber: async (subscriberId: string, opts: WriteOptions): Promise<Subscriber> =>
      subscriberLifecycleStep('suspend', subscriberId, opts),
    resumeSubscriber: async (subscriberId: string, opts: WriteOptions): Promise<Subscriber> =>
      subscriberLifecycleStep('resume', subscriberId, opts),
  };
}

/**
 * The campaign rows in the order `GET /ops/v1/campaigns` answers them: newest first,
 * which is what the router's `store.list_campaigns` does — not the seed order.
 *
 * Exported so a test can pin the ordering itself rather than only a page rendering it.
 */
export function campaignRowsNewestFirst(rows: readonly FixtureCampaign[]): FixtureCampaign[] {
  return [...rows].sort((a, b) => (b.doc.created_at ?? '').localeCompare(a.doc.created_at ?? ''));
}

/**
 * The mirror of `_admin_state()` in `acs_service/routers/olt_ops.py:142-155`,
 * applied to the row the adapter returned from `authorize_onu`.
 *
 * The router reads `admin_state` **first, then `auth_state`**, and only falls
 * back to `enabled` when the driver said nothing. Both backends spell the field
 * `auth_state` and answer `authorized` (`acs_service/adapter.py:291,297`;
 * `olt_adapter/drivers/base.py:175,183`), and neither ever sets `admin_state` —
 * so the value of a real 200 body is `authorized`, exactly as the committed
 * backend tests assert (`tests/test_acs_v11_olt_views.py:180`,
 * `tests/test_acs_schemas_contract.py:278`). `'enabled'` is only the Pydantic
 * default of `OnuAuthorizeResponse` (`acs_service/models.py:456`) and the OpenAPI
 * example (`openapi/openapi.yaml:2010`); it is not what this endpoint returns.
 *
 * Exported so a test can assert the mirror itself, not just a page rendering it.
 */
export function adminStateOfResult(result: unknown): string {
  if (result === null || typeof result !== 'object') return 'enabled';
  const row = result as { admin_state?: unknown; auth_state?: unknown };
  for (const value of [row.admin_state, row.auth_state]) {
    if (value) return String(value);
  }
  return 'enabled';
}

/** `DeviceJobRow` (console aggregate) → the contract `Job` document. */
export function jobFromRow(row: DeviceJobRow): Job {
  return {
    job_id: row.job_id,
    action: row.action,
    status: row.status,
    result: row.result_summary === null ? null : { summary: row.result_summary },
    started_at: row.started_at,
    completed_at: row.completed_at,
    requested_at: row.requested_at,
  };
}

/** Contract `Job` → the console's per-device history row (inverse of `jobFromRow`). */
export function rowFromJob(job: Job): DeviceJobRow {
  const result = job.result;
  const summary =
    result !== null &&
    typeof result === 'object' &&
    typeof (result as { summary?: unknown }).summary === 'string'
      ? (result as { summary: string }).summary
      : null;
  const row: DeviceJobRow = {
    job_id: job.job_id,
    action: job.action,
    status: job.status as DeviceJobRow['status'],
    requested_at: job.requested_at ?? '',
    started_at: job.started_at,
    completed_at: job.completed_at,
    result_summary: summary,
  };
  return row;
}

/**
 * Idempotency semantics shared with the server: replaying a key with the same
 * body returns the stored response (no side effect); reusing it with a
 * different body is a 409 `idempotency-conflict`.
 */
export function applyIdempotency<T>(
  store: Map<string, StoredResponse>,
  key: string,
  body: unknown,
  produce: () => T,
): T {
  const replayed = lookupIdempotency<T>(store, key, body);
  if (replayed !== undefined) return replayed;
  const payload = produce();
  storeIdempotency(store, key, body, payload);
  return payload;
}

/** The stored payload for a matching replay, or `undefined` when the key is new. */
export function lookupIdempotency<T>(
  store: Map<string, StoredResponse>,
  key: string,
  body: unknown,
): T | undefined {
  const fingerprint = JSON.stringify(body);
  const existing = store.get(key);
  if (!existing) return undefined;
  if (existing.fingerprint !== fingerprint) {
    throw new ApiError('problem', 'problem+json 409', {
      status: 409,
      problem: buildProblem('idempotency-conflict', {
        detail: `key ${key} was already used with a different request body`,
      }),
    });
  }
  return existing.payload as T;
}

export function storeIdempotency(
  store: Map<string, StoredResponse>,
  key: string,
  body: unknown,
  payload: unknown,
): void {
  store.set(key, { fingerprint: JSON.stringify(body), payload });
}

/** `GET /integration/v1/health`, shaped like production. */
export function buildFixtureHealth(): Health {
  return {
    service: 'acs-api',
    version: '0.1.0',
    status: 'ok',
    store_backend: 'mongo',
    nbi_mode: 'http',
    contract_version: 'v1',
    time: new Date().toISOString(),
  };
}

/**
 * `GET /ops/v1/system/components` — the four components and their evidence (O4).
 *
 * The SAME four components the health rollup names (api / store / nbi / adapter), the same
 * per-component state, and the store's detail also carries the queue depths. `depthsUnavailable`
 * reproduces the server's «the depth read failed» answer: `depths: null` — never zeros, because a
 * depth the server did not send is not a depth of zero.
 */
export function buildFixtureSystemComponents(depthsUnavailable = false): SystemComponentsResponse {
  const storeDetail: SystemComponentDetail = {
    backend: 'mongo',
    counts: { ...FIXTURE_COUNTS },
    depths: depthsUnavailable
      ? null
      : {
          outbox: { ...FIXTURE_OUTBOX_DEPTHS },
          jobs: { ...FIXTURE_JOB_DEPTHS },
        },
  };
  if (depthsUnavailable) {
    storeDetail.depths_error = 'RuntimeError: the depth read failed';
  }
  return {
    service: 'acs-api',
    status: 'ok',
    components: {
      api: {
        status: 'up',
        detail: { version: '0.1.0', service: 'acs-api' },
      },
      store: { status: 'up', detail: storeDetail },
      nbi: { status: 'up', detail: { mode: 'http', status_code: 200 } },
      adapter: {
        status: 'up',
        detail: { mode: 'http', driver: 'acs-olt-adapter', olts: 2 },
      },
    },
    contract_version: 'v1',
    version: '0.1.0',
    time: new Date().toISOString(),
  };
}

/**
 * `GET /ops/v1/system/queues` — the depths and the live session count (O4).
 *
 * The vocabularies are the server's, whole: every word of both closed alphabets, a word nobody
 * holds included (a MEASURED zero). The note is the server's own `QUEUES_NOTE`, verbatim — it is
 * the honesty clause, and a paraphrase would teach the console a sentence the server never says.
 */
export function buildFixtureSystemQueues(): SystemQueuesResponse {
  return {
    outbox: { ...FIXTURE_OUTBOX_DEPTHS },
    jobs: { ...FIXTURE_JOB_DEPTHS },
    sessions_active: FIXTURE_SESSIONS_ACTIVE,
    contract_version: 'v1',
    time: new Date().toISOString(),
    note: QUEUES_NOTE,
  };
}

/**
 * `GET /ops/v1/system/workers` — the heartbeat table, judged (DA-09, TLM-4b).
 *
 * Four rows and two DELIBERATE ABSENCES: `acs-campaigns` and `acs-optical` have no row here
 * because this fixture deployment has never received a beat from them, and the door's answer
 * to that is silence, not `down` (the API cannot know whether the deployment runs the loop).
 * The `status` words are the rows' own, written down rather than computed: `judge_workers()`
 * is the server's rule and a second copy of it in the console is a rule that can disagree.
 *
 * `unreadable` reproduces the door's «I could not look» answer: `status: degraded` WITH the
 * error and NO rows — never `ok` with an empty object, because an empty object is also the
 * honest answer for a deployment that runs no workers at all, and a console must be able to
 * tell the two apart.
 */
export function buildFixtureSystemWorkers(unreadable = false): SystemWorkersResponse {
  const nowMs = Date.now();
  const rows = unreadable ? {} : fixtureWorkerBeats(nowMs);
  // The rollup is over the rows that EXIST: no rows and no error is `ok` (a deployment that runs
  // no workers is not a broken one), while no rows BECAUSE of an error is `degraded` — the two
  // have the same payload shape and opposite meanings, which is why the door sends the error.
  const healthy = !unreadable && Object.values(rows).every((beat) => beat.status === 'ok');
  return {
    service: 'acs-api',
    status: healthy ? 'ok' : 'degraded',
    workers: rows,
    note: WORKERS_NOTE,
    error: unreadable ? 'RuntimeError: the heartbeat table could not be read' : '',
    contract_version: 'v1',
    version: '0.1.0',
    time: new Date(nowMs).toISOString(),
  };
}
